import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import * as d3 from "d3";
import { useDispatch, useSelector } from "react-redux";
import { Excel } from "antd-table-saveas-excel";
import { setSaveWidgetChanges } from "../../../../redux/actions/dashboard/Tab1";
import { setY_attribute } from "../../../../redux/actions/WidgetMaker/GraphConfig";
import {
  callNotification,
  getBrands,
  getCustomizedDate,
  getPaneIndex,
  getWindowLocationPath,
  kFormatter,
  getAllFilters,
  getAllShareFilters,
  getAllDeepDiveDashboardFilters,
  useQuery,
  deepCopy,
} from "../../../../redux/constants";
import { benchmarkAggregation } from "../../../../redux/constants/enums";
import { getAllWidgetScreenFilters } from "../../../../redux/constants/commandCenterConstants";
import moment from "moment";
// Deep dive imports from HighCharts index.js
import {
  setHighChartDeepDive,
  setOptionHighChart,
} from "../../../../redux/actions/HighChart";
import {
  setDeepDiveFlag,
  setDeepDiveInfoLoader,
  setIsDetailedInsightVisible,
  setMentionPageData,
  setTabClick,
} from "../../../../redux/actions/DeepDive";
import {
  setisDeepDive,
  setSelectedDeepdiveWidget,
} from "../../../../redux/actions/dashboard/Tab1";
import {
  setDeepDiveInfluencersObject,
  setDeepDiveMentionsObject,
  setDeepDiveObject,
  set_WM_DeepDive,
  setClusterNextLoadedNodes,
} from "../../../../redux/actions/WidgetMaker/WidgetMaker";
import { getDeepDive } from "../../../../redux/actions/WidgetMaker/WidgetAPI";
import { setShareSelectedDeepdiveWidget } from "../../../../redux/actions/share/Share";
import {
  setScreenIsDeepDive,
  setSelectedDeepdiveWidgetInScreen,
} from "../../../../redux/actions/commandCenter/screens/screen";
import { setIsDeepDiveInDashboardTemplateData } from "../../../../redux/actions/DeepDiveDashboard/DeepDiveDashboard";
import { Tooltip } from "antd";

const ClusterChart = ({
  width = "100%",
  height = "100%",
  minHeight = 600,
  maxHeight = 800,
  data: externalData = null,
  useHierarchicalData = true,
  onNodeClick = null,
  onNodeHover = null,
  showTooltip = true,
  enableDrag = true,
  enableZoom = true,
  parentNodeRange = [50, 80],
  childNodeRange = [20, 30],
  dynamicNodeSize = true,
  linkColor = "#999",
  linkOpacity = 0.6,
  backgroundColor = "#fff",
  cluster_chart_data,
  metadata,
  PdfDownloadStatus,
  PptGeneratingStatus,
  widgetIndex,
  sectionIndex,
  isPreviewState,
}) => {
  const dispatch = useDispatch();
  const panes = useSelector((state) => state?.Tab1?.panes);
  const activeKey = useSelector((state) => state?.Tab1?.activeKey);
  const axisPanelAtt = useSelector(
    (state) => state.GraphConfig.widgetGraphConfig
  );
  const isDashboardScheduleModal = useSelector(
    (state) => state?.ApiCall?.is_dashboard_schedule_modal
  );
  const allAttributes1 = useSelector(
    (state) => state?.attributes?.allAttributes
  );
  const All_WM_attributes = useSelector(
    (state) => state.WidgetMaker.All_WM_attributes
  );
  const graphConditionConfig = useSelector(
    (state) => state?.apiCall?.graphConditions
  );
  const shareTemplateData = useSelector(
    (state) => state.ApiCall.shareTemplateData
  );
  const personalizationSettingsObj = useSelector(
    (state) => state?.PersonalizationSetting
  );
  const WM_attributes = useSelector((state) => state.WidgetMaker.WM_attributes);
  const screen = useSelector((state) => state?.Screens?.screen);
  const shareBrands = useSelector((state) => state.ApiCall.shareBrands);
  const sharedDuration = useSelector((state) => state.Share.sharedDuration);
  const authParams = useSelector((state) => state.Authentication.authParams);
  const brands = useSelector(
    (state) => state.ApiCall.brands?.data?.result?.brands
  );
  const deepdiveDashboardData = useSelector(
    (state) => state?.DeepDiveDashboard?.dashboardData
  );
  const deepdiveDashboardTemplateData = useSelector(
    (state) => state?.DeepDiveDashboard?.dashboardTemplateData
  );
  const opData = useSelector((state) => state.Highchart.Options);

  const brandlogicalgroup = useSelector(
    (state) => state.ApiCall.brandlogicalgroup?.data?.result
  );
  const clusterNextLoadedNodes = useSelector(
    (state) => state.WidgetMaker.cluster_next_loaded_nodes
  );

  let allAttributes = allAttributes1 ? allAttributes1 : All_WM_attributes;

  const index = getPaneIndex(panes, activeKey);
  let query = useQuery();

  // Props that would come from parent component in chart context
  const section_index =
    sectionIndex !== null && sectionIndex !== undefined && sectionIndex >= 0
      ? sectionIndex
      : metadata?.section_index || 0;
  const widget_index =
    widgetIndex !== null && widgetIndex !== undefined && widgetIndex >= 0
      ? widgetIndex
      : metadata?.widget_index || 0;
  const id = metadata?.id || "cluster-chart";
  const type = metadata?.chart?.chart_type || metadata?.chart_type || "cluster";
  const single = cluster_chart_data;
  const chartGlobalDataStacked =
    metadata?.chart?.chart_settings?.is_stacked || false;

  // Loading state
  const [load, setLoad] = useState(true);

  // Computed values for deep dive
  const isDeepDiveDashboardView = useMemo(() => {
    let pathName = getWindowLocationPath()?.toLowerCase();
    return (
      (authParams?.screen_id
        ? screen?.isDeepDiveDashboard
          ? true
          : false
        : shareTemplateData?.isDeepDiveDashboard
        ? true
        : false) && !deepdiveDashboardTemplateData?.isDeepDive
    );
  }, [screen, shareTemplateData, authParams, deepdiveDashboardTemplateData]);

  const isSurveyCardChart = metadata?.chart?.chart_type === "survey-card";
  const shareCategory = shareTemplateData?.category;
  const shareableLinkDeepDiveStatus = useSelector(
    (state) => state.ApiCall.shareableLinkDeepDiveStatus
  );
  const workspace_privileges = useSelector(
    (state) => state.ApiCall.workspace_privileges
  );
  const share_workspace_privileges = useSelector(
    (state) => state.ApiCall.share_workspace_privileges
  );

  // Function to generate random color
  const generateRandomColor = () => {
    let color;
    let attempts = 0;
    const maxAttempts = 50;

    do {
      const hue = Math.floor(Math.random() * 360);
      const saturation = 60 + Math.floor(Math.random() * 20); // 60-80%
      const lightness = 50 + Math.floor(Math.random() * 10); // 50-60%
      color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
      attempts++;
    } while (excludedColors.has(color) && attempts < maxAttempts);

    return color;
  };

  // Function to get node color
  const getNodeColor = (node) => {
    // If node has a color property
    if (node.color) {
      // If color is black (#000000), generate random color
      if (node.color === "#000000") {
        return generateRandomColor();
      }
      // Otherwise use the provided color
      return node.color;
    }
    // Default color if no color property
    return "#94a3b8";
  };

  // Function to calculate dynamic font size based on radius
  const getDynamicFontSize = (radius, type = "bubble") => {
    let minFontSize, maxFontSize, minRadius, maxRadius;

    if (type === "bubble") {
      // For sentiment bubbles
      minFontSize = 8;
      maxFontSize = 16;
      minRadius = 10;
      maxRadius = 30;
    } else if (type === "pie") {
      // For pie/donut slices
      minFontSize = 8;
      maxFontSize = 14;
      minRadius = 30;
      maxRadius = 80;
    }

    // Linear interpolation
    const fontSize =
      minFontSize +
      ((radius - minRadius) / (maxRadius - minRadius)) *
        (maxFontSize - minFontSize);

    // Clamp between min and max
    return Math.max(minFontSize, Math.min(maxFontSize, Math.round(fontSize)));
  };

  // All excluded colors from COLOR_MAP_DICT and system colors
  const excludedColors = new Set([
    // Sentiment colors
    "#5ed142",
    "#ff5d48",
    "#ffdb4d",
    "#dc1e1e",
    "#1c9f00",
    "#deaa0c",
    // Rating colors
    "#f24040",
    "#ff9b9b",
    "#f2c508",
    "#acff77",
    "#54d600",
    // Promotion colors
    "#ff763f",
    "#1da2ff",
    "#9c27b0",
    // Gender colors
    "#185edf",
    "#f83a99",
    "#696969",
    // Verification colors
    "#c3cbd7",
    "#751bdc",
    // Tweet colors
    "#19bfe5",
    "#14abce",
    // Emotion colors
    "#daa520",
    "#ff0033",
    "#60ca3b",
    "#ff8d94",
    "#129c30",
    "#bbe347",
    "#ff6230",
    "#ff69b4",
    "#c50000",
    "#6b8e23",
    "#ff4487",
    "#d2b48c",
    "#ffd700",
    // Ticket emotion colors
    "#52ec1d",
    "#ff6a16",
    "#1db63e",
    "#ea1818",
    "#820000",
    "#c5a4df",
    "#5ca103",
    "#a4e984",
    "#c55402",
    "#ca9100",
    "#f31d6a",
    "#12e233",
    "#2a8a28",
    // Ticket tag colors
    "#e12828",
    "#864eff",
    "#b815f1",
    "#e4b50f",
    "#11c6c6",
    "#5374a5",
    // Social media colors
    "#678bff",
    "#ff5622",
    "#0c3b7c",
    "#21b496",
    "#6bd82d",
    "#ffe684",
    "#cdb200",
    "#ff2ef2",
    "#072f54",
    "#889014",
    "#1877f2",
    "#ce30f4",
    "#0869aa",
    "#a1fea7",
    "#b9bfd0",
    "#f00075",
    "#0e76a8",
    "#8e5106",
    "#144be1",
    "#ac52ff",
    "#da26fc",
    "#b92131",
    "#6765cf",
    "#9df34b",
    "#10c34b",
    "#ff0000",
    "#b19587",
    "#ffd000",
    "#e10e50",
    "#ffa02f",
    "#a82400",
    "#0caa41",
    "#ff3d00",
    "#2f84ed",
    "#6a2a2a",
    "#2968b9",
    "#0088cc",
    // Survey colors
    "#4361ee",
    "#f72585",
    "#b5179e",
    "#7209b7",
    "#3f37c9",
    "#4895ef",
    "#4cc9f0",
    "#390099",
    "#8f699c",
    "#b14a6f",
    // Also exclude existing sentiment colors from sentimentColorMap
    "#10b981",
    "#ef4444",
    "#f59e0b",
    "#6366f1",
    "#fbbf24",
    "#84cc16",
  ]);

  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const zoomRef = useRef(null);
  const simulationRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    data: null,
  });
  const tooltipTimeoutRef = useRef(null);
  const [visualizationType, setVisualizationType] = useState(
    metadata?.chart?.chart_settings?.cluster_chart_type
      ? metadata?.chart?.chart_settings?.cluster_chart_type
      : metadata?.chart_settings?.cluster_chart_type
      ? metadata?.chart_settings?.cluster_chart_type
      : "pie"
  ); // bubble, donut, pie
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedNodes, setHighlightedNodes] = useState(new Set());
  const [showSearch, setShowSearch] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [collapsedNodes, setCollapsedNodes] = useState(new Set());
  const [nodeVisibility, setNodeVisibility] = useState(new Map());
  const [isDynamicSize, setIsDynamicSize] = useState(dynamicNodeSize);
  const [nodeShape, setNodeShape] = useState(
    metadata?.chart?.chart_settings?.cluster_style
      ? metadata?.chart?.chart_settings?.cluster_style
      : metadata?.chart_settings?.cluster_style
      ? metadata?.chart_settings?.cluster_style
      : "circle"
  ); // circle, square, diamond, hexagon, triangle, star
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Track which nodes have already loaded their children
  const [loadedNodes, setLoadedNodes] = useState(new Set());
  const [newlyAddedNodes, setNewlyAddedNodes] = useState(new Set()); // Track newly added nodes for highlighting
  const [loadingNodes, setLoadingNodes] = useState(new Set()); // Track nodes currently being loaded
  const loadingTimeoutRef = useRef(new Map()); // Track timeouts for loading nodes
  const [isHelpTextCollapsed, setIsHelpTextCollapsed] = useState(true);
  const [isShapePanelCollapsed, setIsShapePanelCollapsed] = useState(true);
  const clickedParentNodeRef = useRef(null);
  const isLocalUpdateRef = useRef(false); // Track when we're updating locally
  const isProcessingRef = useRef(false); // Prevent multiple processing of clusterNextLoadedNodes

  let pathName = window.location.pathname?.split("/")?.[1];
  let total_count_flag =
    panes?.[index]?.is_widget_maker_open_obj?.open_flag ||
    pathName === "widgetMaker" ||
    pathName === "edit-widget"
      ? axisPanelAtt?.chart_settings?.total_count_flag
      : shareTemplateData
      ? shareTemplateData?.sections
        ? shareTemplateData?.sections?.[section_index]?.widgets?.[widget_index]
            ?.chart?.chart_settings?.total_count_flag
        : shareTemplateData?.section?.[0]?.widgets?.[widget_index]?.chart
            ?.chart_settings?.total_count_flag
      : panes?.[index]?.sections
      ? panes?.[index]?.sections?.[section_index]?.widgets?.[widget_index]
          ?.chart?.chart_settings?.total_count_flag
      : panes?.[index]?.section?.[0]?.widgets?.[widget_index]?.chart
          ?.chart_settings?.total_count_flag;

  // Common sentiment color map
  const sentimentColorMap = {
    positive: "#1c9f00",
    negative: "#dc1e1e",
    neutral: "#deaa0c",
    mixed: "#6366f1",
    happy: "#10b981",
    sad: "#3b82f6",
    angry: "#ef4444",
    surprised: "#fbbf24",
    satisfied: "#10b981",
    unsatisfied: "#ef4444",
    supportive: "#22c55e",
    critical: "#dc2626",
    questioning: "#8b5cf6",
    constructive: "#06b6d4",
    complaint: "#f97316",
    praise: "#22d3ee",
    "5-star": "#10b981",
    "4-star": "#22c55e",
    "3-star": "#fbbf24",
    "2-star": "#fb923c",
    "1-star": "#ef4444",
  };

  // Function to handle visualization type change with transitions
  const handleVisualizationChange = (newType) => {
    if (newType === visualizationType || isTransitioning) return;

    setIsTransitioning(true);

    // Remove existing visualizations
    const svg = d3.select(svgRef.current);
    svg
      .selectAll(
        ".sentiment-bubble, .sentiment-chart, .sentiment-bubble + text"
      )
      .remove();

    // Update visualization type immediately
    setVisualizationType(newType);
    setIsTransitioning(false);

    if (
      panes?.[index]?.is_widget_maker_open_obj?.open_flag ||
      window.location.pathname === "/widgetMaker"
    ) {
      dispatch(
        setY_attribute({
          ...axisPanelAtt,
          chart_settings: {
            ...axisPanelAtt.chart_settings,
            cluster_chart_type: newType,
            cluster_style: nodeShape,
          },
        })
      );
    } else {
      let updateSaveChanges = {
        flag: true,
        cluster_chart_type: newType,
        cluster_style: nodeShape,
      };
      dispatch(setSaveWidgetChanges(updateSaveChanges, panes, index));
    }
  };

  const handleNodeShapeChange = (shape) => {
    setNodeShape(shape);

    if (
      panes?.[index]?.is_widget_maker_open_obj?.open_flag ||
      window.location.pathname === "/widgetMaker"
    ) {
      dispatch(
        setY_attribute({
          ...axisPanelAtt,
          chart_settings: {
            ...axisPanelAtt.chart_settings,
            cluster_chart_type: visualizationType,
            cluster_style: shape,
          },
        })
      );
    } else {
      let updateSaveChanges = {
        flag: true,
        cluster_chart_type: visualizationType,
        cluster_style: shape,
      };
      dispatch(setSaveWidgetChanges(updateSaveChanges, panes, index));
    }
  };

  // Function to calculate node depth level
  const getNodeDepth = (node, nodes) => {
    let depth = 0;
    let currentNode = node;

    while (currentNode.parentId) {
      depth++;
      currentNode = nodes.find((n) => n.id === currentNode.parentId);
      if (!currentNode) break;
    }

    return depth;
  };

  // Function to check if load icon should be shown based on configuration
  const shouldShowLoadIcon = (node) => {
    // Don't show load icon for parent nodes (aspectGroup)
    if (node.type === "aspectGroup") {
      return false;
    }

    // Convert node ID to string for consistent comparison
    const nodeIdStr = String(node.id);

    // Don't show if already loaded
    if (loadedNodes.has(nodeIdStr) || loadedNodes.has(node.id)) {
      return false;
    }

    // Don't show if currently loading
    if (loadingNodes.has(nodeIdStr) || loadingNodes.has(node.id)) {
      return false;
    }

    // Check if this node already has children in the current data
    // If it has children, don't show the load icon
    const hasChildren =
      data.nodes && data.nodes.some((n) => n.parentId === node.id);
    if (hasChildren) {
      return false;
    }

    // Calculate current node depth (0 for parent, 1 for first level child, etc.)
    const nodeDepth = getNodeDepth(node, data.nodes);

    // Check for configuration to determine if next level exists
    let hasNextLevel = false;

    // Dashboard mode: Check y_axes in metadata FIRST (prioritize metadata for dashboard)
    if (metadata?.y_axes && Array.isArray(metadata.y_axes)) {
      hasNextLevel = metadata.y_axes.length > nodeDepth;
    }
    // Widget Maker mode: Check breakdown_by in axisPanelAtt
    else if (
      axisPanelAtt?.breakdown_by &&
      Array.isArray(axisPanelAtt.breakdown_by)
    ) {
      hasNextLevel = axisPanelAtt.breakdown_by.length > nodeDepth;
    }
    // Default: Check both breakdown_by and y_axes in both places
    else {
      // Check all possible locations
      const breakdownLength =
        metadata?.breakdown_by?.length ||
        axisPanelAtt?.breakdown_by?.length ||
        0;
      const yAxesLength =
        metadata?.y_axes?.length || axisPanelAtt?.y_axes?.length || 0;
      const maxLength = Math.max(breakdownLength, yAxesLength);
      hasNextLevel = maxLength > nodeDepth;
    }

    return hasNextLevel;
  };

  // Helper function to get parent node details
  const getParentNodeDetails = (node) => {
    if (!node.parentId || !data?.nodes) return null;
    return data.nodes.find((n) => n.id === node.parentId);
  };

  // Helper function to get the root parent attribute for any node level
  const getRootParentAttribute = (node) => {
    // For parent nodes (aspectGroup), use x_axis attribute
    if (node.type === "aspectGroup") {
      return metadata?.x_axis?.attribute || axisPanelAtt?.x_axis?.attribute;
    }

    // For child nodes, traverse up to find the root parent
    let currentNode = node;
    let parentNode = null;

    while (currentNode.parentId && data?.nodes) {
      parentNode = data.nodes.find((n) => n.id === currentNode.parentId);
      if (!parentNode) break;

      // If we found an aspectGroup (root parent), return x_axis attribute
      if (parentNode.type === "aspectGroup") {
        return metadata?.x_axis?.attribute || axisPanelAtt?.x_axis?.attribute;
      }

      currentNode = parentNode;
    }

    // Fallback to x_axis attribute
    return metadata?.x_axis?.attribute || axisPanelAtt?.x_axis?.attribute;
  };

  // Helper function to get the core/root parent node (e.g., Twitter)
  const getCoreParentNode = (node) => {
    // If the node itself is aspectGroup, it's the core parent
    if (node.type === "aspectGroup") {
      return node;
    }

    // For child nodes, traverse up to find the root parent (aspectGroup)
    let currentNode = node;
    let parentNode = null;
    let depth = 0;
    const maxDepth = 10; // Increase max depth to handle deeper hierarchies
    const visitedIds = new Set(); // Prevent infinite loops

    while (
      currentNode &&
      currentNode.parentId &&
      data?.nodes &&
      depth < maxDepth
    ) {
      // Check for circular references
      if (visitedIds.has(currentNode.id)) {
        break;
      }
      visitedIds.add(currentNode.id);

      // Find parent node by ID (handle both string and number IDs)
      parentNode = data.nodes.find((n) => {
        return (
          n.id === currentNode.parentId ||
          n.id === String(currentNode.parentId) ||
          String(n.id) === String(currentNode.parentId)
        );
      });

      if (!parentNode) {
        // Try finding a node with parent "0" as fallback (root nodes)
        const rootNodes = data.nodes.filter(
          (n) =>
            n.parentId === "0" || n.parentId === 0 || n.type === "aspectGroup"
        );
        if (rootNodes.length > 0) {
          return rootNodes[0];
        }
        break;
      }

      // If we found an aspectGroup (root parent), return it
      if (parentNode.type === "aspectGroup") {
        return parentNode;
      }

      // Also check if parent has parentId "0" which indicates it's a root
      if (parentNode.parentId === "0" || parentNode.parentId === 0) {
        return parentNode;
      }

      // Move up one level
      currentNode = parentNode;
      depth++;
    }

    // If we couldn't find aspectGroup, try to find any node with parentId "0"
    const rootNodes = data.nodes.filter(
      (n) => n.parentId === "0" || n.parentId === 0 || n.type === "aspectGroup"
    );
    if (rootNodes.length > 0) {
      return rootNodes[0];
    }

    // Last resort
    return parentNode || node;
  };

  // Helper function to get all nodes in hierarchy from root to clicked node
  const getNodeHierarchy = (clickedNode) => {
    const hierarchy = [];

    // If the clicked node is the root, just return it
    if (
      clickedNode.type === "aspectGroup" ||
      !clickedNode.parentId ||
      clickedNode.parentId === "0"
    ) {
      return [
        {
          level: 0,
          node: clickedNode,
          name: clickedNode.name,
          value: clickedNode.value,
          attribute: clickedNode.attribute,
          type: clickedNode.type,
          id: clickedNode.id,
        },
      ];
    }

    // Traverse up from clicked node to root, collecting all nodes
    let currentNode = clickedNode;
    const nodesPath = [];
    const visitedIds = new Set();

    // First, collect the clicked node
    nodesPath.push(currentNode);

    // Then traverse up to root
    while (
      currentNode &&
      currentNode.parentId &&
      currentNode.parentId !== "0" &&
      data?.nodes
    ) {
      if (visitedIds.has(currentNode.id)) {
        break; // Prevent infinite loops
      }
      visitedIds.add(currentNode.id);

      const parentNode = data.nodes.find((n) => {
        return (
          n.id === currentNode.parentId ||
          n.id === String(currentNode.parentId) ||
          String(n.id) === String(currentNode.parentId)
        );
      });

      if (!parentNode) break;

      nodesPath.push(parentNode);
      currentNode = parentNode;

      // Stop if we reached the root
      if (
        parentNode.type === "aspectGroup" ||
        parentNode.parentId === "0" ||
        parentNode.parentId === 0
      ) {
        break;
      }
    }

    // Reverse the array so it goes from root to clicked node
    nodesPath.reverse();

    // Create hierarchy with levels
    nodesPath.forEach((node, index) => {
      hierarchy.push({
        level: index,
        node: node,
        name: node.name,
        value: node.value,
        attribute: node.attribute,
        type: node.type,
        id: node.id,
        isRoot: index === 0,
        isClicked: index === nodesPath.length - 1,
      });
    });

    // console.log("Node Hierarchy from root to clicked:", hierarchy);
    return hierarchy;
  };

  // Function to handle deep dive on node click
  const handleDeepDiveNode = (node) => {
    // Find and set the core parent (root node like Twitter) for ANY clicked node
    if (!node.coreGroup || node.type !== "aspectGroup") {
      const coreParent = getCoreParentNode(node);
      node.coreGroup = coreParent.name;
    }

    // Enhance node with parent details if it's a child node
    if (node.parentId) {
      const parentDetails = getParentNodeDetails(node);
      if (parentDetails) {
        node.parentNode = node.parentNode || parentDetails.name;
        node.group = node.group || parentDetails.name;

        // Get the parent attribute - for aspectGroup parents, use x_axis attribute
        if (parentDetails.type === "aspectGroup") {
          node.parentAttribute =
            metadata?.x_axis?.attribute || axisPanelAtt?.x_axis?.attribute;
        } else {
          node.parentAttribute =
            node.parentAttribute ||
            parentDetails.attribute ||
            getRootParentAttribute(node);
        }
      }
    }

    // For aspectGroup nodes themselves, ensure they have the x_axis attribute
    if (node.type === "aspectGroup" && !node.attribute) {
      node.attribute =
        metadata?.x_axis?.attribute || axisPanelAtt?.x_axis?.attribute;
    }

    // Collect complete node hierarchy from root to clicked node
    const nodeHierarchy = getNodeHierarchy(node);
    // Add hierarchy to node object for deep dive
    node.nodeHierarchy = nodeHierarchy;
    handleOnClickDeepDive("test", section_index, widget_index, single, node);
  };

  const handleChartAPI = (node) => {
    // Get current node positions before making API call
    const nodePositions = saveNodePositions(data.nodes);

    let pathName = window.location.pathname?.split("/")?.[1];
    if (
      panes?.[index]?.is_widget_maker_open_obj?.open_flag ||
      pathName === "widgetMaker" ||
      pathName === "edit-widget"
    ) {
      // Always find and set the correct coreGroup for ANY non-root node
      if (node.type !== "aspectGroup") {
        const coreParent = getCoreParentNode(node);
        const oldCoreGroup = node.coreGroup;
        node.coreGroup = coreParent.name;
      } else {
        // For aspectGroup nodes, coreGroup should be their own name
        node.coreGroup = node.name;
      }

      const nodeLevel = getNodeDepth(node, data.nodes);
      let updatedYaxis = [];
      axisPanelAtt?.breakdown_by?.map((att, index) => {
        updatedYaxis.push({
          attribute: att?.attribute,
          trace_name: att?.trace_name || allAttributes?.[att?.attribute],
          value:
            index === 0 && nodeLevel === 2
              ? node?.group
              : nodeLevel - 1 === index
              ? node?.name
              : null,
          deep_dive_node: nodeLevel === index ? true : false,
        });
      });
      // console.log("updatedYaxis", updatedYaxis);

      let deep_dive_on_data = {
        y_axis_secondary: node?.parentId ? updatedYaxis : null,
        x_axis: {
          attribute: axisPanelAtt?.x_axis?.attribute,
          value: node?.coreGroup,
          trace_name:
            axisPanelAtt?.x_axis?.trace_name ||
            allAttributes?.[axisPanelAtt?.x_axis?.attribute],
        },
        y_series: {
          attribute: axisPanelAtt?.y_axis?.y_series_1?.attribute,
          value: node?.value,
          trace_name:
            axisPanelAtt?.y_axis?.y_series_1?.trace_name ||
            allAttributes?.[axisPanelAtt?.y_axis?.y_series_1?.attribute],
        },
        y_axis: null,
      };
      // console.log("deep_dive_on_data", deep_dive_on_data);

      if (!isPreviewState) {
        dispatch(
          setY_attribute({
            ...axisPanelAtt,
            deep_dive_on: deep_dive_on_data,
          })
        );
      }
    } else if (shareTemplateData) {
      // share dashboard
      const nodeLevel = getNodeDepth(node, data.nodes);
      let updatedYaxis = [];
      metadata?.y_axes?.map((att, index) => {
        updatedYaxis.push({
          attribute: att?.attribute,
          trace_name: att?.trace_name || allAttributes?.[att?.attribute],
          value:
            index === 0 && nodeLevel === 2
              ? node?.group
              : nodeLevel - 1 === index
              ? node?.name
              : null,
          deep_dive_node: nodeLevel === index ? true : false,
        });
      });

      let deep_dive_on_data = {
        y_axis_secondary: node?.parentId ? updatedYaxis : null,
        x_axis: {
          attribute: metadata?.x_axis?.attribute,
          value: node?.coreGroup,
          trace_name:
            metadata?.x_axis?.trace_name ||
            allAttributes?.[metadata?.x_axis?.attribute],
        },
        y_series: {
          attribute: metadata?.y_series?.[0]?.attribute,
          value: node?.value,
          trace_name:
            metadata?.y_series?.[0]?.trace_name ||
            allAttributes?.[metadata?.y_series?.[0]?.attribute],
        },
        y_axis: null,
      };

      if (metadata) {
        let updateShareTemplateData = { ...shareTemplateData };
        if (updateShareTemplateData?.sections) {
          let dropdown_value =
            updateShareTemplateData.sections[section_index].widgets[
              widget_index
            ]?.graphData?.data?.dropdown;

          let persist_graphdata = {
            data: {
              kpi: [],
              "cluster-chart": cluster_chart_data,
              nodePositions: nodePositions, // Use current node positions as parallel key
              dropdown: dropdown_value,
              roi_grid: null,
            },
            refresh_ri: false,
          };

          updateShareTemplateData.sections[section_index].widgets[
            widget_index
          ].deep_dive_on = deep_dive_on_data;
          updateShareTemplateData.sections[section_index].widgets[
            widget_index
          ].persist_graphdata = persist_graphdata;
          updateShareTemplateData.sections[section_index].widgets[
            widget_index
          ].graphData = null;
        } else {
          let dropdown_value =
            updateShareTemplateData.section[0].widgets[widget_index]?.graphData
              ?.data?.dropdown;

          let persist_graphdata = {
            data: {
              kpi: [],
              "cluster-chart": cluster_chart_data,
              nodePositions: nodePositions, // Use current node positions as parallel key
              dropdown: dropdown_value,
              roi_grid: null,
            },
            refresh_ri: false,
          };
          updateShareTemplateData.section[0].widgets[
            widget_index
          ].deep_dive_on = deep_dive_on_data;
          updateShareTemplateData.section[0].widgets[
            widget_index
          ].persist_graphdata = persist_graphdata;
          updateShareTemplateData.section[0].widgets[widget_index].graphData =
            null;
        }
        dispatch({
          type: "SET_SHARE_TEMPLATE_DATA",
          payload: updateShareTemplateData,
        });
      }
    } else {
      // view dashboard
      const nodeLevel = getNodeDepth(node, data.nodes);
      let updatedYaxis = [];
      metadata?.y_axes?.map((att, index) => {
        updatedYaxis.push({
          attribute: att?.attribute,
          trace_name: att?.trace_name || allAttributes?.[att?.attribute],
          value:
            index === 0 && nodeLevel === 2
              ? node?.group
              : nodeLevel - 1 === index
              ? node?.name
              : null,
          deep_dive_node: nodeLevel === index ? true : false,
        });
      });
      // console.log("updatedYaxis 123", updatedYaxis);

      let deep_dive_on_data = {
        y_axis_secondary: node?.parentId ? updatedYaxis : null,
        x_axis: {
          attribute: metadata?.x_axis?.attribute,
          value: node?.coreGroup,
          trace_name:
            metadata?.x_axis?.trace_name ||
            allAttributes?.[metadata?.x_axis?.attribute],
        },
        y_series: {
          attribute: metadata?.y_series?.[0]?.attribute,
          value: node?.value,
          trace_name:
            metadata?.y_series?.[0]?.trace_name ||
            allAttributes?.[metadata?.y_series?.[0]?.attribute],
        },
        y_axis: null,
      };
      // console.log("deep_dive_on_data", deep_dive_on_data);

      if (metadata) {
        let updatePanes = [...panes];
        if (updatePanes?.[index]?.sections) {
          let dropdown_value =
            updatePanes[index].sections[section_index].widgets[widget_index]
              ?.graphData?.data?.dropdown;

          let persist_graphdata = {
            data: {
              kpi: [],
              "cluster-chart": cluster_chart_data,
              nodePositions: nodePositions, // Use current node positions as parallel key
              dropdown: dropdown_value,
              roi_grid: null,
            },
            refresh_ri: false,
          };
          updatePanes[index].sections[section_index].widgets[
            widget_index
          ].deep_dive_on = deep_dive_on_data;
          updatePanes[index].sections[section_index].widgets[
            widget_index
          ].persist_graphdata = persist_graphdata;
          updatePanes[index].sections[section_index].widgets[
            widget_index
          ].graphData = null;
        } else {
          let dropdown_value =
            updatePanes[index].section[0].widgets[widget_index]?.graphData?.data
              ?.dropdown;
          let persist_graphdata = {
            data: {
              kpi: [],
              "cluster-chart": cluster_chart_data,
              nodePositions: nodePositions, // Use current node positions as parallel key
              dropdown: dropdown_value,
              roi_grid: null,
            },
            refresh_ri: false,
          };
          updatePanes[index].section[0].widgets[widget_index].deep_dive_on =
            deep_dive_on_data;
          updatePanes[index].section[0].widgets[
            widget_index
          ].persist_graphdata = persist_graphdata;
          updatePanes[index].section[0].widgets[widget_index].graphData = null;
        }
        dispatch({ type: "SET_WIDGET_GRAPH_DATA", panes: updatePanes });
      }
    }
  };

  // Function to handle load more data API call
  const handleLoadMoreData = useCallback(
    async (node) => {
      // Prevent loading more nodes in fullscreen mode
      if (isFullscreen) {
        // console.log("Cannot load more nodes in fullscreen mode");
        return;
      }

      // Convert to string for consistent comparison
      const nodeIdStr = String(node.id);

      // Check if this node has already been loaded or is currently loading
      if (loadedNodes.has(nodeIdStr) || loadedNodes.has(node.id)) {
        // console.log("Node already loaded, skipping:", node.id);
        // console.log("LoadedNodes contains:", Array.from(loadedNodes));
        return;
      }

      // Check if this node is currently being loaded
      if (loadingNodes.has(nodeIdStr) || loadingNodes.has(node.id)) {
        // console.log("Node is currently loading, skipping:", node.id);
        return;
      }

      if (!shouldShowLoadIcon(node)) {
        // console.log("Node should not show load icon, skipping:", node.id);
        return;
      }

      // Mark node as loading IMMEDIATELY to prevent multiple calls
      setLoadingNodes((prev) => {
        const newSet = new Set(prev);
        newSet.add(nodeIdStr);
        newSet.add(node.id); // Add both formats for safety
        return newSet;
      });

      // Set a timeout to clear loading state in case API never responds
      const timeoutId = setTimeout(() => {
        console.warn("Load more timeout for node:", node.id);
        setLoadingNodes((prev) => {
          const newSet = new Set(prev);
          newSet.delete(nodeIdStr);
          newSet.delete(node.id);
          return newSet;
        });
        loadingTimeoutRef.current.delete(nodeIdStr);
      }, 30000); // 30 second timeout

      loadingTimeoutRef.current.set(nodeIdStr, timeoutId);

      try {
        const nodeIdToStore = String(node.id);

        // Clear and set fresh to avoid stale references
        clickedParentNodeRef.current = nodeIdToStore;
        sessionStorage.setItem("clusterClickedParentNodeId", nodeIdToStore);

        // Store widget and section indices when clicking load more
        sessionStorage.setItem("clusterWidgetIndex", widget_index);
        sessionStorage.setItem("clusterSectionIndex", section_index);

        // Pass node level to API handler
        handleChartAPI(node);
      } catch (error) {
        console.error("Error in handleLoadMoreData:", error);
        // Remove from loading state if there's an error
        setLoadingNodes((prev) => {
          const newSet = new Set(prev);
          newSet.delete(nodeIdStr);
          newSet.delete(node.id);
          return newSet;
        });

        // Clear timeout
        const timeoutId = loadingTimeoutRef.current.get(nodeIdStr);
        if (timeoutId) {
          clearTimeout(timeoutId);
          loadingTimeoutRef.current.delete(nodeIdStr);
        }
      }
    },
    [
      loadedNodes,
      loadingNodes,
      data,
      isFullscreen,
      metadata,
      axisPanelAtt,
      widget_index,
      section_index,
    ]
  );

  // Deep dive click handler
  const handleOnClickDeepDive = (
    cordinates,
    section_index,
    widget_index,
    single,
    node = null
  ) => {
    let shareSections = shareTemplateData?.section
      ? shareTemplateData?.section?.[0]
      : shareTemplateData?.sections?.[section_index];

    let checkAttType = [];
    WM_attributes?.forEach((e) => {
      return e[1]?.forEach((el) => {
        if (el[1]?.datatype === "int") {
          checkAttType?.push(el[1]?.title);
        }
      });
    });

    let pathName = getWindowLocationPath()?.toLowerCase();

    let seriesNamesForAggregation =
      benchmarkAggregation && Array.isArray(benchmarkAggregation)
        ? benchmarkAggregation?.map(
            (aggrigation, i) => `Group's ${aggrigation?.label}`
          )
        : [];

    if (seriesNamesForAggregation) {
      seriesNamesForAggregation = [
        ...seriesNamesForAggregation,
        "Benchmark",
        "Custom Benchmark",
      ];
    }

    if (
      !panes?.[index]?.is_widget_maker_open_obj?.open_flag &&
      !isDashboardScheduleModal &&
      (pathName === "dashboard" ||
        pathName === "pin-dashboard" ||
        (pathName === "share" && shareableLinkDeepDiveStatus))
    ) {
      let dashboardDataOfDeepDive = deepdiveDashboardData;
      let deepdiveTemplatData = deepdiveDashboardTemplateData;
      let sectionData = isDeepDiveDashboardView
        ? deepdiveTemplatData?.sections?.[section_index]
        : null;
      let widget_metadata = isDeepDiveDashboardView
        ? sectionData?.widgets?.[widget_index]
        : pathName === "CommandCenter"?.toLowerCase()
        ? section_index !== null &&
          section_index !== undefined &&
          screen?.sections &&
          screen?.sections?.[section_index]?.widgets?.[widget_index]
          ? screen?.sections?.[section_index]?.widgets?.[widget_index]
          : section_index >= 0 &&
            screen?.sections &&
            screen?.sections?.[section_index]
          ? screen?.sections?.[section_index]
          : screen?.section?.[0]?.widgets?.[widget_index]
          ? screen?.section?.[0]?.widgets?.[widget_index]
          : screen?.widgets && screen?.widgets?.[widget_index]
          ? screen?.widgets && screen?.widgets?.[widget_index]
          : null
        : shareBrands
        ? shareSections?.widgets?.[widget_index]
        : section_index >= 0
        ? panes?.[index]?.sections?.[section_index]?.widgets?.[widget_index]
        : panes?.[index]?.section?.[0]?.widgets?.[widget_index];

      widget_metadata = widget_metadata
        ? widget_metadata
        : metadata
        ? metadata
        : null;
      let widget_ids =
        widget_metadata?.data_source === "mentions" &&
        (graphConditionConfig?.["workflow-attrs"]?.includes(
          widget_metadata?.x_axis?.attribute
        ) ||
          graphConditionConfig?.["workflow-attrs"]?.includes(
            widget_metadata?.y_series?.[0]?.attribute
          ))
          ? ["8.1"]
          : widget_metadata?.ticket_deep_dive &&
            widget_metadata?.data_source === "ticket"
          ? ["9.1"]
          : widget_metadata?.data_source === "tickets"
          ? ["6.1"]
          : widget_metadata?.deep_dive_id
          ? widget_metadata?.deep_dive_id
          : workspace_privileges?.environment === 2 ||
            share_workspace_privileges?.environment === 2
          ? ["info-bar", "2.1", "2.2", "2.3", "2.4"]
          : ["info-bar", "1.1", "1.2"];

      if (true) {
        let index =
          !shareBrands &&
          pathName !== "CommandCenter"?.toLowerCase() &&
          getPaneIndex(panes, activeKey);

        let section_metadata = isDeepDiveDashboardView
          ? sectionData
          : pathName === "CommandCenter"?.toLowerCase()
          ? section_index !== null &&
            section_index !== undefined &&
            screen?.sections &&
            screen?.sections?.[section_index]
            ? screen?.sections?.[section_index]
            : section_index >= 0 &&
              screen?.sections &&
              screen?.sections?.[section_index]
            ? screen?.sections?.[section_index]
            : screen?.section && screen?.section?.[0]
            ? screen?.section?.[0]
            : null
          : shareBrands
          ? shareSections
          : section_index >= 0
          ? panes[index]?.sections?.[section_index]
          : panes[index]?.section?.[0];
        let Section_is_locked = section_metadata?.is_locked;
        let widgets_is_locked = widget_metadata?.is_locked;
        let twoMonthsDuration = getCustomizedDate(6, "Days");
        let defaultDuration = {
          from:
            moment(twoMonthsDuration?.date[0]).format("YYYY/MM/DD") +
            " " +
            twoMonthsDuration?.time?.startTime,
          to:
            moment(twoMonthsDuration?.date[1]).format("YYYY/MM/DD") +
            " " +
            twoMonthsDuration?.time?.endTime,
        };

        let global_duration = isDeepDiveDashboardView
          ? dashboardDataOfDeepDive?.start_date
            ? {
                from: dashboardDataOfDeepDive?.start_date,
                to: dashboardDataOfDeepDive?.end_date,
              }
            : null
          : pathName === "CommandCenter"?.toLowerCase()
          ? screen?.duration
          : shareBrands
          ? sharedDuration?.from
            ? {
                from: sharedDuration?.from,
                to: sharedDuration?.to,
              }
            : null
          : panes[index]?.duration;

        let global_brands = isDeepDiveDashboardView
          ? dashboardDataOfDeepDive?.brands
          : pathName === "CommandCenter"?.toLowerCase()
          ? screen?.brands
          : shareBrands
          ? shareBrands
          : panes?.[index]?.brands;
        let brandIds =
          shareBrands ||
          pathName === "CommandCenter"?.toLowerCase() ||
          isDeepDiveDashboardView
            ? widget_metadata?.brand_ids
            : section_index >= 0
            ? panes[index]?.sections?.[section_index]?.widgets &&
              panes[index]?.sections?.[section_index]?.widgets[widget_index]
                ?.brand_ids
            : panes[index]?.section?.[0]?.widgets[widget_index]?.brand_ids
            ? panes[index]?.section?.[0]?.widgets[widget_index]?.brand_ids
            : null;

        //share link filter code
        let sectionDateFilter =
          shareTemplateData?.sections && !isDeepDiveDashboardView
            ? {
                from: shareTemplateData?.sections[section_index]?.start_date
                  ? shareTemplateData?.sections[section_index]?.start_date
                  : null,
                to: shareTemplateData?.sections[section_index]?.end_date
                  ? shareTemplateData?.sections[section_index]?.end_date
                  : null,
              }
            : null;

        let widgetFilterDate = {
          from: widget_metadata?.start_date
            ? widget_metadata?.start_date
            : null,
          to: widget_metadata?.end_date ? widget_metadata?.end_date : null,
        };
        let combinedFilters = [];
        if (!widget_metadata?.is_locked) {
          if (widgetFilterDate?.from)
            combinedFilters.push({
              attribute: "CreatedDate",
              type: "datetime",
              columns: widgetFilterDate,
            });

          if (sectionDateFilter?.from && !section_metadata?.is_locked) {
            combinedFilters.push({
              attribute: "CreatedDate",
              type: "datetime",
              columns: sectionDateFilter,
            });
          }
          if (section_metadata?.brand_ids && !section_metadata?.is_locked) {
            let brandsForFilter = shareBrands
              ? getBrands(
                  shareBrands?.brand_groups,
                  section_metadata?.brand_ids,
                  shareBrands?.brand_list,
                  section_metadata?.brand_groups
                )
              : getBrands(
                  brandlogicalgroup,
                  section_metadata?.brand_ids,
                  brands,
                  section_metadata?.brand_groups
                );

            combinedFilters.push(brandsForFilter);
          }
          if (widget_metadata?.brand_ids) {
            let brandsForFilter = shareBrands
              ? getBrands(
                  shareBrands?.brand_groups,
                  widget_metadata?.brand_ids,
                  shareBrands?.brand_list,
                  widget_metadata?.brand_groups
                )
              : getBrands(
                  brandlogicalgroup,
                  widget_metadata?.brand_ids,
                  brands,
                  widget_metadata?.brand_groups
                );

            combinedFilters.push(brandsForFilter);
          }
        }

        if (widget_metadata?.filters)
          combinedFilters = [...widget_metadata?.filters, ...combinedFilters];
        if (section_metadata?.filters)
          combinedFilters = [...section_metadata?.filters, ...combinedFilters];

        let widgetObj = {
          brand_groups: widget_metadata?.brand_groups,
          singleBrands: brands,
        };
        let brandsContainter = {
          brand_ids: widget_metadata?.brand_ids,
          brand_groups: widget_metadata?.brand_groups,
          singleBrands: shareBrands ? shareBrands?.brand_list : brands,
          brandGroupList: shareBrands
            ? shareBrands?.brand_groups
            : brandlogicalgroup,
        };

        let filter = isDeepDiveDashboardView
          ? getAllDeepDiveDashboardFilters(
              graphConditionConfig,
              deepdiveDashboardData,
              deepdiveDashboardTemplateData,
              section_index,
              widget_index,
              brandsContainter
            )
          : pathName === "CommandCenter"?.toLowerCase()
          ? getAllWidgetScreenFilters(
              screen,
              section_index,
              widget_index,
              brands,
              brandlogicalgroup,
              widget_metadata
            )
          : shareBrands
          ? getAllShareFilters(
              graphConditionConfig,
              shareTemplateData,
              section_index,
              widget_index,
              shareBrands
            )
          : getAllFilters(
              graphConditionConfig,
              panes,
              index,
              section_index,
              widget_index,
              brandlogicalgroup,
              brandIds,
              widgetObj
            );

        if (
          screen?.category_screen_filters?.length &&
          !isDeepDiveDashboardView
        ) {
          let category_screen_filters = screen?.category_screen_filters?.length
            ? screen?.category_screen_filters
            : [];
          filter = [...filter, ...category_screen_filters];
        }

        let widget_brands = shareBrands
          ? getBrands(
              shareBrands?.brand_groups,
              widget_metadata?.brand_ids,
              shareBrands?.brand_list,
              widget_metadata?.brand_groups
            )
          : getBrands(
              brandlogicalgroup,
              widget_metadata?.brand_ids,
              brands,
              widget_metadata?.brand_groups
            );

        let section_brands = shareBrands
          ? getBrands(
              shareBrands?.brand_groups,
              section_metadata?.brand_ids,
              shareBrands?.brand_list,
              section_metadata?.brand_groups
            )
          : getBrands(
              brandlogicalgroup,
              section_metadata?.brand_ids,
              brands,
              section_metadata?.brand_groups
            );
        let defaultBrands = isDeepDiveDashboardView
          ? dashboardDataOfDeepDive?.brands
          : pathName === "CommandCenter"?.toLowerCase()
          ? brands
          : shareBrands
          ? shareBrands
          : null;

        let series_index = cordinates?.series?.index
          ? cordinates?.series?.index
          : 0;
        let isChartTypeChange = false;
        let validChartTypes = [
          "area",
          "bar",
          "horizontal-bar",
          "lollipop",
          "combination",
        ];
        if (
          validChartTypes?.includes(
            type ||
              widget_metadata?.chart?.chart_type ||
              widget_metadata?.graph_type
          ) &&
          ["line", "spline"].includes(opData?.[id]?.chart?.type) &&
          opData?.[id]?.isChartTypeChange
        ) {
          isChartTypeChange = true;
        }
        let isValidChartTypes = validChartTypes?.includes(
          type ||
            widget_metadata?.chart?.chart_type ||
            widget_metadata?.graph_type
        );
        let isStackedChartType =
          (isValidChartTypes && chartGlobalDataStacked) ||
          (isChartTypeChange &&
            isValidChartTypes &&
            (chartGlobalDataStacked || chartGlobalDataStacked == undefined));

        let single_data =
          Array.isArray(single) && single[0]?.data
            ? isStackedChartType &&
              Array.isArray(single[0]?.data) &&
              single[0]?.data?.length > 0
              ? single[0]?.data?.slice()?.reverse()
              : single[0]?.data
            : single?.y_axis
            ? opData[id]?.series
            : null;
        let ySeriesInWidgetMetadata = widget_metadata?.y_series
          ? widget_metadata?.y_series
          : [];
        if (Array.isArray(single) && single[0]?.data && isStackedChartType) {
          if (
            Array.isArray(ySeriesInWidgetMetadata) &&
            ySeriesInWidgetMetadata?.length > 0
          ) {
            ySeriesInWidgetMetadata = [...ySeriesInWidgetMetadata]
              ?.slice()
              ?.reverse();
          }
        }

        // Calculating data source for handling page attribute deepdive
        let y_series_datasource =
          // y_series_datasource for widget with journey attribute
          widget_metadata?.y_axes?.[0]?.config_filters?.length ||
          widget_metadata?.x_axis?.config_filters?.length
            ? type === "pie" &&
              Array.isArray(single) &&
              widget_metadata?.y_axes?.length > 0 &&
              widget_metadata?.y_axes?.[0]?.attribute
              ? widget_metadata?.y_series?.[0]?.data_source
              : type === "sunburst" || type === "heatmap"
              ? widget_metadata?.y_series?.[0]?.data_source
              : widget_metadata?.y_axes?.length > 0
              ? widget_metadata?.y_axes?.[0]?.attribute && single_data
                ? single_data[series_index]?.attribute ===
                  widget_metadata?.y_axes?.[0]?.config_filters?.[0]?.attribute
                  ? widget_metadata?.y_series?.[0]?.data_source
                  : single_data[series_index]?.data_source
                : null
              : single_data && single_data[series_index]?.data_source
            : // y_series_datasource for widgets without journey attribute
            type === "pie" &&
              Array.isArray(single) &&
              widget_metadata?.y_axes?.length > 0 &&
              widget_metadata?.y_axes?.[0]?.attribute
            ? widget_metadata?.y_series?.[0]?.data_source
            : type === "sunburst" || type === "heatmap"
            ? widget_metadata?.y_series?.[0]?.data_source
            : widget_metadata?.y_axes?.length > 0
            ? widget_metadata?.y_axes?.[0]?.attribute && single_data
              ? single_data[series_index]?.attribute ===
                widget_metadata?.y_axes?.[0]?.attribute
                ? widget_metadata?.y_series?.[0]?.data_source
                : single_data[series_index]?.data_source
              : null
            : single_data && single_data[series_index]?.data_source;
        // If y_series_datasource is not present in the payload, then assigning it from widget_metadata
        // or defaulting to "mentions"
        if (!y_series_datasource) {
          y_series_datasource = widget_metadata?.data_source
            ? widget_metadata?.data_source
            : "mentions";
        }
        let y_series_filter =
          type === "sunburst" || type === "heatmap"
            ? widget_metadata?.y_series?.[0]?.filters
            : widget_metadata?.y_axes
            ? widget_metadata?.y_axes?.[0]?.attribute && single_data
              ? single_data[series_index]?.attribute ===
                widget_metadata?.y_axes[0]?.attribute
                ? single_data[series_index]?.filters
                : single_data[series_index]?.filters
              : null
            : single_data && single_data[series_index]?.filters;

        //Now Calculating y_series object for deepdive_on payload
        let ySeriesObjForDeepDiveOn = {
          filters: ySeriesInWidgetMetadata
            ? widget_metadata?.y_axes?.length > 0
              ? y_series_filter
                ? y_series_filter
                : []
              : ySeriesInWidgetMetadata[series_index]?.filters
            : [],
          date_part: ySeriesInWidgetMetadata
            ? ySeriesInWidgetMetadata[series_index]?.date_part &&
              ySeriesInWidgetMetadata[series_index]?.date_part !== "D"
              ? ySeriesInWidgetMetadata[series_index]?.date_part
              : ySeriesInWidgetMetadata[series_index]?.date_aggregation
              ? ySeriesInWidgetMetadata[series_index]?.date_aggregation
              : null
            : null,
          special_cases: ySeriesInWidgetMetadata
            ? ySeriesInWidgetMetadata[series_index]?.special_cases
            : widget_metadata?.special_cases,
          attribute: widget_metadata?.y_series?.[0]?.attribute,
          data_source: widget_metadata?.data_source,
          value: node?.value,
          trace_name:
            widget_metadata?.y_series?.[0]?.trace_name ||
            allAttributes?.[widget_metadata?.y_series?.[0]?.attribute],
        };

        // Don't allow deepdive if y_series_datasource is page
        // if (y_series_datasource === "page") then show notification and return
        if (!ySeriesObjForDeepDiveOn?.data_source) {
          ySeriesObjForDeepDiveOn.data_source =
            widget_metadata?.data_source || "mentions";
        }
        if (ySeriesObjForDeepDiveOn.data_source === "page") {
          callNotification(
            "Deep dive on this attribute is not allowed",
            "info"
          );
          return;
        }

        dispatch(setDeepDiveFlag(true));

        !shareBrands &&
          pathName !== "CommandCenter"?.toLowerCase() &&
          dispatch(
            setSelectedDeepdiveWidget(
              section_index === 0 ? undefined : section_index,
              widget_index,
              panes,
              index
            )
          );
        !shareBrands &&
          pathName !== "CommandCenter"?.toLowerCase() &&
          dispatch(setisDeepDive(true, panes, index));

        // To add selected widget of deep dive of share dashboard
        shareBrands &&
          dispatch(
            setShareSelectedDeepdiveWidget({
              section_index: section_index === 0 ? undefined : section_index,
              widget_index: widget_index,
            })
          );

        // to add selected widget of deep dive in screen
        if (pathName === "CommandCenter"?.toLowerCase()) {
          dispatch(
            setSelectedDeepdiveWidgetInScreen(
              section_index === 0 ? undefined : section_index,
              widget_index
            )
          );
        }

        // This case is applied when we select competition brand and applied competition filter(Owned/Competition)
        // calculating brand in case of section comprtiotion brand type exists
        let sectionComprtitorBrandType =
          section_metadata?.comp_brand_type === false ||
          section_metadata?.comp_brand_type === true
            ? section_metadata?.comp_brand_type
            : null;
        let sectionComprtitorBrandTypeExists =
          sectionComprtitorBrandType === false ||
          sectionComprtitorBrandType === true;
        let globalBrandChangeInShare = false;
        let updatedComBrandList = [];
        if (
          !(Section_is_locked && section_brands?.columns?.length) &&
          sectionComprtitorBrandTypeExists
        ) {
          if (shareBrands) {
            let comBrand = defaultBrands?.brands?.columns?.[0]?.competitor
              ? defaultBrands?.brands?.columns?.[0]
              : defaultBrands?.brands?.[0]?.competitor
              ? defaultBrands?.brands?.[0]
              : defaultBrands?.brands?.competitor
              ? defaultBrands?.brands
              : null;
            let comBrandExists = comBrand?.competitor
              ? true
              : defaultBrands
              ? defaultBrands?.selected_brand_ids?.length &&
                defaultBrands?.selected_comp_brand_ids?.length > 0
              : null;
            if (comBrandExists) {
              if (comBrand) {
                if (sectionComprtitorBrandType === false) {
                  updatedComBrandList.push({
                    brand_id: comBrand?.name,
                    brand_name: comBrand?.brand_name,
                    brand_friendly_name: comBrand?.brand_friendly_name,
                  });
                } else {
                  comBrand?.competitorList?.forEach((competitorBrand) => {
                    updatedComBrandList.push(competitorBrand);
                  });
                }
                globalBrandChangeInShare = true;
              } else {
                if (sectionComprtitorBrandType === false) {
                  let parentB = defaultBrands?.selected_brand_ids?.[0];
                  updatedComBrandList.push({
                    brand_id: parentB?.brand_id,
                    brand_name: parentB?.brand_name,
                    brand_friendly_name: parentB?.brand_friendly_name,
                  });
                } else {
                  defaultBrands?.selected_comp_brand_ids?.forEach(
                    (competitorBrand) => {
                      updatedComBrandList.push(competitorBrand);
                    }
                  );
                }
              }
            } else {
              sectionComprtitorBrandTypeExists = false;
            }
          } else {
            let comBrand = global_brands?.columns?.[0]?.competitor
              ? global_brands?.columns?.[0]
              : global_brands?.[0]?.competitor
              ? global_brands?.[0]
              : global_brands?.competitor
              ? global_brands
              : null;
            if (comBrand?.competitor) {
              if (sectionComprtitorBrandType === false) {
                updatedComBrandList.push({
                  brand_id: comBrand?.name,
                  brand_name: comBrand?.brand_name,
                  brand_friendly_name: comBrand?.brand_friendly_name,
                });
              } else {
                comBrand?.competitorList?.forEach((competitorBrand) => {
                  updatedComBrandList.push(competitorBrand);
                });
              }
            } else {
              sectionComprtitorBrandTypeExists = false;
            }
          }
        }
        // here calculating the public_engagement_only for the deepdive
        let public_engagement_only =
          pathName === "CommandCenter"?.toLowerCase() || isDeepDiveDashboardView
            ? false // for now static false sending for command center
            : shareBrands
            ? shareTemplateData?.template_type === "competition"
              ? shareTemplateData?.public_engagement_only || false
              : false
            : panes?.[index]?.template_type === "competition"
            ? panes?.[index]?.public_engagement_only || false
            : false;

        // New Logic for final states
        let final_state_date = widgets_is_locked
          ? widget_metadata?.start_date
            ? widget_metadata?.start_date
            : global_duration?.from
            ? global_duration?.from
            : defaultDuration?.from
          : Section_is_locked
          ? section_metadata.start_date
            ? section_metadata?.start_date
            : global_duration?.from
            ? global_duration?.from
            : defaultDuration?.from
          : global_duration?.from
          ? global_duration?.from
          : defaultDuration?.from;

        let final_end_date = widgets_is_locked
          ? widget_metadata?.end_date
            ? widget_metadata?.end_date
            : global_duration?.to
            ? global_duration?.to
            : defaultDuration?.to
          : Section_is_locked
          ? section_metadata?.end_date
            ? section_metadata?.end_date
            : global_duration?.to
            ? global_duration?.to
            : defaultDuration?.to
          : global_duration?.to
          ? global_duration?.to
          : defaultDuration?.to;

        let final_brands = shareBrands
          ? widget_metadata?.is_locked
            ? widget_brands?.columns
              ? widget_brands?.columns
              : section_metadata?.is_locked
              ? section_brands?.columns
                ? section_brands?.columns
                : widget_metadata?.duplicate_for_brands
                ? widget_metadata?.com_brand_ids
                : sectionComprtitorBrandTypeExists
                ? updatedComBrandList
                : defaultBrands
              : widget_metadata?.duplicate_for_brands
              ? widget_metadata?.com_brand_ids
              : sectionComprtitorBrandTypeExists
              ? updatedComBrandList
              : defaultBrands
            : section_metadata?.is_locked
            ? section_brands?.columns
              ? section_brands?.columns
              : widget_metadata?.duplicate_for_brands
              ? widget_metadata?.com_brand_ids
              : sectionComprtitorBrandTypeExists
              ? updatedComBrandList
              : defaultBrands
            : widget_metadata?.duplicate_for_brands
            ? widget_metadata?.com_brand_ids
            : sectionComprtitorBrandTypeExists
            ? updatedComBrandList
            : defaultBrands
          : widgets_is_locked
          ? widget_metadata?.brand_ids || widget_metadata?.brand_groups
            ? widget_brands?.columns
            : Section_is_locked
            ? section_metadata?.brand_ids || section_metadata?.brand_groups
              ? section_brands?.columns
              : widget_metadata?.duplicate_for_brands
              ? widget_metadata?.com_brand_ids
              : sectionComprtitorBrandTypeExists
              ? updatedComBrandList
              : global_brands
              ? global_brands
              : brandlogicalgroup?.length > 0
              ? brandlogicalgroup
              : brands
            : widget_metadata?.duplicate_for_brands
            ? widget_metadata?.com_brand_ids
            : sectionComprtitorBrandTypeExists
            ? updatedComBrandList
            : global_brands
            ? global_brands
            : brandlogicalgroup?.length > 0
            ? brandlogicalgroup
            : brands
          : Section_is_locked
          ? section_metadata?.brand_ids || section_metadata?.brand_groups
            ? section_brands?.columns
            : widget_metadata?.duplicate_for_brands
            ? widget_metadata?.com_brand_ids
            : sectionComprtitorBrandTypeExists
            ? updatedComBrandList
            : global_brands
            ? global_brands
            : brandlogicalgroup?.length > 0
            ? brandlogicalgroup
            : brands
          : widget_metadata?.duplicate_for_brands
          ? widget_metadata?.com_brand_ids
          : sectionComprtitorBrandTypeExists
          ? updatedComBrandList
          : global_brands
          ? global_brands
          : brandlogicalgroup?.length > 0
          ? brandlogicalgroup
          : brands;

        // Count child nodes
        const childNodes =
          data?.nodes?.filter((n) => n.parentId === node.id) || [];
        const childCount = childNodes.length;

        // Count all descendants (children, grandchildren, etc.)
        const getDescendants = (nodeId) => {
          const children =
            data?.nodes?.filter((n) => n.parentId === nodeId) || [];
          let descendants = [...children];
          children.forEach((child) => {
            descendants = [...descendants, ...getDescendants(child.id)];
          });
          return descendants;
        };
        const allDescendants = getDescendants(node.id);
        const totalDescendantCount = allDescendants.length;

        // console.log("node", node);
        const nodeLevel = getNodeDepth(node, data.nodes);
        let updatedYaxis = [];
        metadata?.y_axes?.map((att, index) => {
          updatedYaxis.push({
            attribute: att?.attribute,
            trace_name: allAttributes?.[att?.attribute],
            value: node?.nodeHierarchy?.length
              ? node?.nodeHierarchy?.[index + 1]?.name
              : null,
            deep_dive_node: nodeLevel === index ? true : false,
          });
        });

        let updated_x_axis = {
          attribute: node?.nodeHierarchy?.[0]?.attribute,
          value: node?.nodeHierarchy?.[0]?.name,
          trace_name:
            node?.nodeHierarchy?.[0]?.trace_name ||
            allAttributes?.[node?.nodeHierarchy?.[0]?.attribute],
        };
        updatedYaxis?.unshift(updated_x_axis);
        updatedYaxis?.splice(nodeLevel + 1);

        function getKeysAsString(obj) {
          return Object.keys(obj);
        }
        const breakdownByValues =
          node?.sentimenttype && getKeysAsString(node?.sentimenttype);

        let graphObject = {
          start_date: final_state_date,
          end_date: final_end_date,
          brands: final_brands,
          chart_type: type,
          deepdive_on: {
            y_axis_secondary: node?.parentId ? updatedYaxis : null,
            x_axis: {
              // x_limit: totalDescendantCount,
              special_cases:
                widget_metadata?.x_axis?.special_cases ||
                widget_metadata?.special_cases,
              attribute: node?.attribute,
              value: node?.name,
              trace_name: node?.trace_name || allAttributes?.[node?.attribute],
            },
            y_series: ySeriesObjForDeepDiveOn,
            y_axis:
              (node?.parentNode || (nodeLevel === 0 && node?.sentimenttype)) &&
              widget_metadata?.multilevel_breakdown?.attribute
                ? {
                    attribute: widget_metadata?.multilevel_breakdown?.attribute,
                    trace_name:
                      widget_metadata?.multilevel_breakdown?.trace_name ||
                      allAttributes?.[
                        widget_metadata?.multilevel_breakdown?.attribute
                      ],
                    value: node?.sentimentType
                      ? node?.sentimentType
                      : breakdownByValues,
                  }
                : null,
            deep_dive_id: widget_metadata?.deep_dive_id
              ? widget_metadata?.deep_dive_id
              : null,
            widget_inner_id: cordinates?.options?.widget_inner_id
              ? cordinates?.options?.widget_inner_id
              : null,
            name:
              widget_metadata?.chart?.chart_type === "survey-card"
                ? null
                : cordinates?.options?.name
                ? cordinates?.options?.name
                : null,
          },
          filters: filter ? filter : null,
          paneIndex: index,
          panes: panes,
          shareBrands: shareBrands,
          shareTemplateData: {
            ...shareTemplateData,
            isDeepDive: true,
            deep_dive: {
              infoBar: null,
              mentions: null,
              wordcloud: [],
              category: [],
              top_users: [],
              influencers: [],
              source_of_post: [],
              locations: [],
            },
          },
          tabName:
            workspace_privileges?.environment === 2 ||
            share_workspace_privileges?.environment === 2
              ? "Word Cloud"
              : "Mentions",
          shareCategory: shareCategory,
          filter_by: widget_metadata?.payload?.filter_by,
          filter_dropdown: widget_metadata?.payload?.filter_dropdown || [],
          filter_by_dropdown:
            widget_metadata?.payload?.filter_by_dropdown || [],
          screen: pathName === "CommandCenter"?.toLowerCase() ? screen : null,
          isTicketBased:
            widget_ids?.includes("6.1") || widget_ids?.includes("9.1"),
          isWorkFlowBased: widget_ids?.includes("8.1"),
          isSurveyCardBased: isSurveyCardChart ? true : false,
          public_engagement_only: public_engagement_only ? true : false,
        };
        graphObject.isSectionCompetitorBrandTypeExists = (() => {
          // Return false if any of these conditions are true
          if (
            widget_metadata?.duplicate_for_brands ||
            (shareBrands &&
              widget_metadata?.is_locked &&
              widget_brands?.columns) ||
            (shareBrands &&
              section_metadata?.is_locked &&
              section_brands?.columns) ||
            (!shareBrands &&
              widgets_is_locked &&
              (widget_metadata?.brand_ids || widget_metadata?.brand_groups)) ||
            (!shareBrands &&
              Section_is_locked &&
              (section_metadata?.brand_ids || section_metadata?.brand_groups))
          ) {
            return false;
          }

          // Return true only if sectionComprtitorBrandTypeExists is true and globalBrandChangeInShare is true
          return (
            (sectionComprtitorBrandTypeExists && globalBrandChangeInShare) ||
            false
          );
        })();
        graphObject.isBrandModified =
          !shareBrands &&
          (global_brands ||
            (widgets_is_locked &&
              (widget_brands?.columns
                ? widget_brands?.columns?.length
                : widget_brands?.length)) ||
            (Section_is_locked &&
              (section_brands?.columns
                ? section_brands?.columns?.length
                : section_brands?.length)) ||
            widget_metadata?.duplicate_for_brands)
            ? true
            : false;
        if (isDeepDiveDashboardView) {
          dispatch(setIsDeepDiveInDashboardTemplateData(true)); // to indicate second level deepdive is opened
          // if this is second level deepdive so need to pass sub_deep_dive_on key
          // where old deepdive_on object will pass and new one is same as

          if (deepdiveDashboardData?.deepdive_on) {
            let newDeepDiveOnObject = graphObject?.deepdive_on;
            let deediveDashboardOnObject = deepdiveDashboardData?.deepdive_on
              ? { ...deepdiveDashboardData?.deepdive_on }
              : null;
            deediveDashboardOnObject = deediveDashboardOnObject
              ? deepCopy(deediveDashboardOnObject)
              : null;

            let sub_deep_dive_on = [];
            if (
              deepdiveDashboardData?.sub_deep_dive_on &&
              Array.isArray(deepdiveDashboardData.sub_deep_dive_on)
            ) {
              sub_deep_dive_on = [
                ...sub_deep_dive_on,
                ...deepdiveDashboardData.sub_deep_dive_on,
              ];
            }
            if (deediveDashboardOnObject) {
              sub_deep_dive_on.push(deediveDashboardOnObject);
            }
            graphObject.sub_deep_dive_on =
              sub_deep_dive_on &&
              Array.isArray(sub_deep_dive_on) &&
              sub_deep_dive_on?.length > 0
                ? [...sub_deep_dive_on]
                : null;
          }
        }
        dispatch(
          setIsDetailedInsightVisible({
            value: false,
            tab_id: null,
            name:
              workspace_privileges?.environment === 2 ||
              share_workspace_privileges?.environment === 2
                ? "Word Cloud"
                : "Mentions",
            key:
              workspace_privileges?.environment === 2 ||
              share_workspace_privileges?.environment === 2
                ? "2"
                : "1",
          })
        );

        dispatch(
          setTabClick({
            tabName:
              workspace_privileges?.environment === 2 ||
              share_workspace_privileges?.environment === 2
                ? "wordcloud"
                : "mentions",
            state: true,
          })
        );
        dispatch(setDeepDiveObject(graphObject));
        dispatch(setDeepDiveMentionsObject(graphObject));
        dispatch(setDeepDiveInfluencersObject(graphObject));
        dispatch(setMentionPageData({ data: null, page: 1, limit: 10 }));
        shareTemplateData &&
          dispatch({
            type: "SET_SHARE_TEMPLATE_DATA",
            payload: {
              ...shareTemplateData,
              isDeepDive: true,
              deep_dive: {
                infoBar: null,
                mentions: null,
                wordcloud: [],
                category: [],
                top_users: [],
                influencers: [],
                source_of_post: [],
                locations: [],
              },
            },
          });
        if (pathName === "CommandCenter"?.toLowerCase()) {
          let deep_dive_object = {
            infoBar: null,
            mentions: null,
            wordcloud: [],
            category: [],
            top_users: [],
            influencers: [],
            source_of_post: [],
            locations: [],
          };

          // Enable Deep Dive Modal
          dispatch(setScreenIsDeepDive(true, deep_dive_object));
        }
        dispatch(set_WM_DeepDive(true));
        dispatch(setDeepDiveInfoLoader(true));
        // graph/deep-dive Api Call
        dispatch(
          getDeepDive(
            authParams,
            graphObject,
            widget_ids,
            query?.get("share_id")
          )
        );
        setLoad(false);
        dispatch(setOptionHighChart(null));
        window.scrollTo(0, 0);
      }
    }
  };

  // Handle search functionality
  const handleSearch = (term) => {
    setSearchTerm(term);

    if (!term || !data || !data.nodes) {
      setHighlightedNodes(new Set());
      return;
    }

    const searchLower = term.toLowerCase();
    const matches = new Set();

    data.nodes.forEach((node) => {
      if (node.name && node.name.toLowerCase().includes(searchLower)) {
        matches.add(node.id);
        // Also highlight parent/children
        if (node.parentId) {
          matches.add(node.parentId);
        }
        // Find children
        data.nodes.forEach((child) => {
          if (child.parentId === node.id) {
            matches.add(child.id);
          }
        });
      }
    });

    setHighlightedNodes(matches);

    // If there are matches, zoom to the first one
    if (matches.size > 0 && svgRef.current) {
      const firstMatchId = Array.from(matches)[0];
      const matchedNode = data.nodes.find((n) => n.id === firstMatchId);
      if (matchedNode && zoomRef.current) {
        const svg = d3.select(svgRef.current);
        const scale = 2;
        const translateX = dimensions.width / 2 - matchedNode.x * scale;
        const translateY = dimensions.height / 2 - matchedNode.y * scale;

        const transform = d3.zoomIdentity
          .translate(translateX, translateY)
          .scale(scale);

        // Apply zoom transform directly to container
        const container = svg.select(".container");
        container.attr("transform", transform);
        if (svg.node()) {
          svg.node().__zoom = transform;
        }
      }
    }
  };

  // Export functions
  const exportAsPNG = () => {
    if (!svgRef.current) return;

    const svg = svgRef.current;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    // Set canvas size
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;

    // Set background
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    img.onload = () => {
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = `cluster-chart-${new Date()
          .toISOString()
          .slice(0, 10)}.png`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
      });
    };

    img.src =
      "data:image/svg+xml;base64," +
      btoa(unescape(encodeURIComponent(svgData)));
  };

  const exportAsSVG = () => {
    if (!svgRef.current) return;

    const svg = svgRef.current;
    const svgData = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = `cluster-chart-${new Date()
      .toISOString()
      .slice(0, 10)}.svg`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportAsExcel = () => {
    if (!data) return;

    // Helper function to get dynamic attribute name
    const getAttributeName = (attribute) => {
      // Use allAttributes mapping if available, otherwise use the attribute as is
      if (allAttributes && allAttributes[attribute]) {
        return allAttributes[attribute];
      }
      // Fallback: format the attribute name nicely
      if (!attribute) return null;
      return (
        attribute.charAt(0).toUpperCase() +
        attribute.slice(1).replace(/_/g, " ")
      );
    };

    // Check if we're in widget maker mode
    const isWidgetMakerMode =
      panes?.[index]?.is_widget_maker_open_obj?.open_flag ||
      window.location.pathname === "/widgetMaker";

    // Dynamic column structure based on metadata and mode
    let firstColumn, hasFirstBreakdown, secondColumn, valueColumn;

    if (isWidgetMakerMode) {
      // Widget maker mode: use x_axis, y_axis, and breakdown_by
      firstColumn = getAttributeName(metadata?.x_axis?.attribute) || "Channel";

      // Column 2: breakdown_by[0] attribute (only if it exists)
      hasFirstBreakdown = metadata?.breakdown_by?.[0];
      secondColumn = hasFirstBreakdown
        ? getAttributeName(metadata.breakdown_by?.[0]?.attribute) ||
          "Inner channel"
        : null;

      // Value column: y_axis attribute
      valueColumn = getAttributeName(metadata?.y_axis?.attribute) || "Total";
    } else {
      // Dashboard mode: use x_axis, y_series, and y_axes
      firstColumn = getAttributeName(metadata?.x_axis?.attribute) || "Channel";

      // Column 2: y_axes[0] attribute (only if it exists)
      hasFirstBreakdown = metadata?.y_axes?.[0];
      secondColumn = hasFirstBreakdown
        ? getAttributeName(metadata.y_axes[0].attribute) || "Inner channel"
        : null;

      // Value column: y_series attribute
      valueColumn =
        getAttributeName(metadata?.y_series?.[0]?.attribute) ||
        getAttributeName(metadata?.y_series?.attribute) ||
        "Total";
    }

    // Prepare data in table format using Map to merge duplicate rows
    const mergedRows = new Map();

    // Get parent nodes (platforms)
    const parentNodes = data.nodes.filter(
      (node) => node.type === "aspectGroup"
    );

    // Collect breakdown values for dynamic columns
    const breakdownKeys = new Set();

    // Check which breakdown to use based on mode
    let hasSecondBreakdown;
    if (isWidgetMakerMode) {
      // Widget maker mode: use breakdown_by[1]
      hasSecondBreakdown = metadata?.breakdown_by?.[1];
    } else {
      // Dashboard mode: use y_axes[1] or breakdown_by[0] if no y_axes
      hasSecondBreakdown =
        metadata?.y_axes?.[1] ||
        (!metadata?.y_axes && metadata?.breakdown_by?.[0]);
    }

    if (hasSecondBreakdown) {
      // Collect all unique values from the breakdown data
      data.nodes.forEach((node) => {
        if (node.sentimenttype) {
          Object.keys(node.sentimenttype).forEach((key) =>
            breakdownKeys.add(key)
          );
        } else if (node.breakdown_by_data && node.breakdown_by_data.values) {
          node.breakdown_by_data.values.forEach((valueObj) => {
            Object.keys(valueObj).forEach((key) => breakdownKeys.add(key));
          });
        }
      });
    }

    parentNodes.forEach((parent) => {
      // Get child nodes for this parent
      const childNodes = data.nodes.filter(
        (node) => node.parentId === parent.id && node.type !== "aspectGroup"
      );

      if (childNodes.length > 0) {
        // First, add the parent summary row
        const parentRow = {
          [firstColumn]: parent.name,
          [valueColumn]: parent.value || 0,
        };

        // Add second column only if it exists
        if (hasFirstBreakdown && secondColumn) {
          parentRow[secondColumn] = ""; // Empty for parent rows
        }

        // Initialize breakdown columns with 0 only if we have breakdown_by[1]
        if (hasSecondBreakdown && breakdownKeys.size > 0) {
          breakdownKeys.forEach((key) => {
            parentRow[key.charAt(0).toUpperCase() + key.slice(1)] = 0;
          });
        }

        // Calculate parent sentiment totals from all children
        childNodes.forEach((child) => {
          if (child.sentimenttype) {
            Object.keys(child.sentimenttype).forEach((sentiment) => {
              const columnName =
                sentiment.charAt(0).toUpperCase() + sentiment.slice(1);
              parentRow[columnName] =
                (parentRow[columnName] || 0) +
                (child.sentimenttype[sentiment] || 0);
            });
          } else if (
            child.breakdown_by_data &&
            child.breakdown_by_data.values
          ) {
            child.breakdown_by_data.values.forEach((valueObj) => {
              Object.keys(valueObj).forEach((key) => {
                const columnName = key.charAt(0).toUpperCase() + key.slice(1);
                parentRow[columnName] =
                  (parentRow[columnName] || 0) + (valueObj[key] || 0);
              });
            });
          }
        });

        mergedRows.set(parent.name, parentRow);

        // Then process each child node
        childNodes.forEach((child) => {
          // Create unique key for merging rows
          const rowKey = `${parent.name}_${child.name}`;

          if (mergedRows.has(rowKey)) {
            // Merge with existing row
            const existingRow = mergedRows.get(rowKey);
            existingRow[valueColumn] =
              (existingRow[valueColumn] || 0) + (child.value || 0);

            // Merge breakdown data if we have breakdown_by[1]
            if (hasSecondBreakdown) {
              if (child.sentimenttype) {
                Object.keys(child.sentimenttype).forEach((sentiment) => {
                  const columnName =
                    sentiment.charAt(0).toUpperCase() + sentiment.slice(1);
                  existingRow[columnName] =
                    (existingRow[columnName] || 0) +
                    (child.sentimenttype[sentiment] || 0);
                });
              } else if (
                child.breakdown_by_data &&
                child.breakdown_by_data.values
              ) {
                child.breakdown_by_data.values.forEach((valueObj) => {
                  Object.keys(valueObj).forEach((key) => {
                    const columnName =
                      key.charAt(0).toUpperCase() + key.slice(1);
                    existingRow[columnName] =
                      (existingRow[columnName] || 0) + (valueObj[key] || 0);
                  });
                });
              }
            }
          } else {
            // Create new row
            const row = {
              [firstColumn]: "", // Empty for child rows to show hierarchy
              [valueColumn]: child.value || 0,
            };

            // Add second column only if it exists
            if (hasFirstBreakdown && secondColumn) {
              row[secondColumn] = child.name;
            }

            // Initialize breakdown columns with 0 only if we have breakdown_by[1]
            if (hasSecondBreakdown && breakdownKeys.size > 0) {
              breakdownKeys.forEach((key) => {
                row[key.charAt(0).toUpperCase() + key.slice(1)] = 0;
              });
            }

            // Add breakdown data as separate columns only if we have breakdown_by[1]
            if (hasSecondBreakdown) {
              if (child.sentimenttype) {
                Object.keys(child.sentimenttype).forEach((sentiment) => {
                  const columnName =
                    sentiment.charAt(0).toUpperCase() + sentiment.slice(1);
                  row[columnName] = child.sentimenttype[sentiment] || 0;
                });
              } else if (
                child.breakdown_by_data &&
                child.breakdown_by_data.values
              ) {
                // Handle new format - merge all sentiment values
                child.breakdown_by_data.values.forEach((valueObj) => {
                  Object.keys(valueObj).forEach((key) => {
                    const columnName =
                      key.charAt(0).toUpperCase() + key.slice(1);
                    row[columnName] =
                      (row[columnName] || 0) + (valueObj[key] || 0);
                  });
                });
              }
            }

            mergedRows.set(rowKey, row);
          }
        });
      } else {
        // If no child nodes, add parent as a single row
        const rowKey = parent.name;

        if (!mergedRows.has(rowKey)) {
          const row = {
            [firstColumn]: parent.name,
            [valueColumn]: parent.value || 0,
          };

          // Add second column only if it exists
          if (hasFirstBreakdown && secondColumn) {
            row[secondColumn] = ""; // Empty for parent-only rows
          }

          // Initialize breakdown columns with 0 only if we have breakdown_by[1]
          if (hasSecondBreakdown && breakdownKeys.size > 0) {
            breakdownKeys.forEach((key) => {
              row[key.charAt(0).toUpperCase() + key.slice(1)] = 0;
            });
          }

          // Add breakdown data as separate columns only if we have breakdown_by[1]
          if (hasSecondBreakdown) {
            if (parent.sentimenttype) {
              Object.keys(parent.sentimenttype).forEach((sentiment) => {
                const columnName =
                  sentiment.charAt(0).toUpperCase() + sentiment.slice(1);
                row[columnName] = parent.sentimenttype[sentiment] || 0;
              });
            } else if (
              parent.breakdown_by_data &&
              parent.breakdown_by_data.values
            ) {
              // Handle new format - merge all sentiment values
              parent.breakdown_by_data.values.forEach((valueObj) => {
                Object.keys(valueObj).forEach((sentimentKey) => {
                  const columnName =
                    sentimentKey.charAt(0).toUpperCase() +
                    sentimentKey.slice(1);
                  row[columnName] =
                    (row[columnName] || 0) + (valueObj[sentimentKey] || 0);
                });
              });
            }
          }

          mergedRows.set(rowKey, row);
        }
      }
    });

    // Convert Map to array
    const tableData = Array.from(mergedRows.values());

    // If no data, add at least headers
    if (tableData.length === 0) {
      const defaultRow = {
        [firstColumn]: "",
        [valueColumn]: 0,
      };

      // Add second column only if it exists
      if (hasFirstBreakdown && secondColumn) {
        defaultRow[secondColumn] = "";
      }

      // Only add breakdown columns if we have breakdown_by[1]
      if (hasSecondBreakdown && breakdownKeys.size > 0) {
        breakdownKeys.forEach((key) => {
          defaultRow[key.charAt(0).toUpperCase() + key.slice(1)] = 0;
        });
      }

      tableData.push(defaultRow);
    }

    // Get all unique column keys in the order we want
    const columnOrder = [];
    const seenColumns = new Set();

    // Define column order based on metadata structure
    columnOrder.push(firstColumn);

    // Add second column only if it exists
    if (hasFirstBreakdown && secondColumn) {
      columnOrder.push(secondColumn);
    }

    columnOrder.push(valueColumn);

    // Only add breakdown columns if we have breakdown_by[1]
    if (hasSecondBreakdown && breakdownKeys.size > 0) {
      // Add breakdown columns in a specific order if they match common patterns
      const orderedBreakdownKeys = [];
      const commonOrder = ["positive", "negative", "neutral", "mixed"];

      // First add columns that match common order
      commonOrder.forEach((key) => {
        if (breakdownKeys.has(key)) {
          orderedBreakdownKeys.push(key);
        }
      });

      // Then add any remaining breakdown keys
      breakdownKeys.forEach((key) => {
        if (!orderedBreakdownKeys.includes(key)) {
          orderedBreakdownKeys.push(key);
        }
      });

      // Add all breakdown columns to column order
      orderedBreakdownKeys.forEach((key) => {
        columnOrder.push(key.charAt(0).toUpperCase() + key.slice(1));
      });
    }

    // Create columns configuration
    const columns = columnOrder.map((key) => ({
      title: key,
      dataIndex: key,
      key: key,
    }));

    // Custom sort to maintain parent-child hierarchy
    const sortedData = [];
    const processedParents = new Set();

    // Process data to maintain hierarchy
    tableData.forEach((row) => {
      if (row[firstColumn] && !processedParents.has(row[firstColumn])) {
        // This is a parent row
        processedParents.add(row[firstColumn]);
        sortedData.push(row);

        // Find and add all child rows for this parent
        const parentName = row[firstColumn];
        tableData.forEach((childRow) => {
          if (
            !childRow[firstColumn] &&
            (hasFirstBreakdown ? childRow[secondColumn] : true)
          ) {
            // Check if this child belongs to current parent by checking the key in mergedRows
            const isChild = Array.from(mergedRows.entries()).some(
              ([key, value]) => {
                return value === childRow && key.startsWith(parentName + "_");
              }
            );
            if (isChild) {
              sortedData.push(childRow);
            }
          }
        });
      }
    });

    // Replace tableData with sorted data
    tableData.length = 0;
    tableData.push(...sortedData);

    // Create Excel instance
    const excel = new Excel();
    excel
      .addSheet("Cluster Chart Data")
      .addColumns(columns)
      .addDataSource(tableData, {
        str2Percent: true,
      })
      .saveAs(`cluster-chart-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // Toggle node collapse/expand
  const toggleNodeCollapse = (nodeId) => {
    if (!data || !data.nodes || !svgRef.current) {
      console.error("Missing required data for toggle");
      return;
    }

    const newCollapsed = new Set(collapsedNodes);
    const isExpanding = newCollapsed.has(nodeId);

    if (isExpanding) {
      newCollapsed.delete(nodeId);
    } else {
      newCollapsed.add(nodeId);
    }

    setCollapsedNodes(newCollapsed);

    // Use requestAnimationFrame for better performance
    requestAnimationFrame(() => {
      const svg = d3.select(svgRef.current);

      // Update button text for the clicked node only
      svg.selectAll("g.node").each(function (d) {
        if (d.id === nodeId) {
          d3.select(this)
            .select(".expand-button text")
            .text(isExpanding ? "−" : "+");
        }
      });

      // Create maps for faster lookups
      const nodeMap = new Map(data.nodes.map((n) => [n.id, n]));
      const childrenByParent = new Map();

      // Build parent-children relationships
      data.nodes.forEach((node) => {
        if (node.parentId) {
          if (!childrenByParent.has(node.parentId)) {
            childrenByParent.set(node.parentId, []);
          }
          childrenByParent.get(node.parentId).push(node.id);
        }
      });

      // Recursive function to get all descendants of a node
      const getDescendants = (nodeId, descendants = new Set()) => {
        const children = childrenByParent.get(nodeId) || [];
        children.forEach((childId) => {
          descendants.add(childId);
          getDescendants(childId, descendants); // Recursively get grandchildren
        });
        return descendants;
      };

      // Function to check if a node should be visible
      const isNodeVisible = (nodeId) => {
        const node = nodeMap.get(nodeId);
        if (!node) return false;

        // Root nodes (aspectGroup) are always visible
        if (node.type === "aspectGroup") return true;

        // Check if any ancestor is collapsed
        let currentNode = node;
        while (currentNode.parentId) {
          if (newCollapsed.has(currentNode.parentId)) {
            return false; // Parent is collapsed, so this node is hidden
          }
          currentNode = nodeMap.get(currentNode.parentId);
          if (!currentNode) break;
        }

        return true; // All ancestors are expanded
      };

      // Determine visible nodes
      const visibleNodes = new Set();
      data.nodes.forEach((node) => {
        if (isNodeVisible(node.id)) {
          visibleNodes.add(node.id);
        }
      });

      // Batch update visibility
      svg.selectAll("g.node").style("display", function (d) {
        return visibleNodes.has(d.id) ? null : "none";
      });

      svg.selectAll("g.links line").style("display", function (l) {
        const sourceId = typeof l.source === "object" ? l.source.id : l.source;
        const targetId = typeof l.target === "object" ? l.target.id : l.target;
        return visibleNodes.has(sourceId) && visibleNodes.has(targetId)
          ? null
          : "none";
      });
    });
  };

  // Function to create pie/donut chart
  const createPieChart = (data, nodeRadius, isDonut = false) => {
    const innerRadius = isDonut ? nodeRadius * 0.35 : 0;
    const outerRadius = nodeRadius * 0.85;

    const pie = d3
      .pie()
      .value((d) => d.value)
      .sort(null);

    const arc = d3.arc().innerRadius(innerRadius).outerRadius(outerRadius);

    return { pie, arc, data: pie(data) };
  };

  // Create shape paths
  const createShapePath = (shape, radius) => {
    switch (shape) {
      case "square":
        const size = radius * 1.5;
        return `M${-size},${-size} L${size},${-size} L${size},${size} L${-size},${size} Z`;

      case "diamond":
        const dSize = radius * 1.7;
        return `M0,${-dSize} L${dSize},0 L0,${dSize} L${-dSize},0 Z`;

      case "hexagon":
        const hexRadius = radius * 1.2;
        const angles = Array.from(
          { length: 6 },
          (_, i) => ((i * 60 - 30) * Math.PI) / 180
        );
        const points = angles
          .map(
            (angle) =>
              `${hexRadius * Math.cos(angle)},${hexRadius * Math.sin(angle)}`
          )
          .join(" ");
        return `M${points} Z`;

      case "triangle":
        const tSize = radius * 1.5;
        return `M0,${-tSize} L${tSize * 0.866},${tSize * 0.5} L${
          -tSize * 0.866
        },${tSize * 0.5} Z`;

      case "star":
        const outerRadius = radius * 1.4;
        const innerRadius = radius * 0.6;
        let path = "";
        for (let i = 0; i < 10; i++) {
          const angle = ((i * 36 - 90) * Math.PI) / 180;
          const r = i % 2 === 0 ? outerRadius : innerRadius;
          const x = r * Math.cos(angle);
          const y = r * Math.sin(angle);
          path += i === 0 ? `M${x},${y}` : ` L${x},${y}`;
        }
        return path + " Z";

      default: // circle
        return null; // Use SVG circle element
    }
  };

  // Process backend data format into nodes and links
  const processBackendData = (backendData) => {
    const nodes = [];
    const links = [];

    if (!backendData || !Array.isArray(backendData)) {
      return { nodes: [], links: [] };
    }

    // Get existing persist_graphdata to retrieve saved node positions
    let existingPositions = {};
    let pathName = getWindowLocationPath()?.toLowerCase();
    try {
      let currentWidget = null;

      if (
        !panes?.[index]?.is_widget_maker_open_obj?.open_flag &&
        !isDashboardScheduleModal &&
        (pathName === "dashboard" ||
          pathName === "pin-dashboard" ||
          (pathName === "share" && shareableLinkDeepDiveStatus))
      ) {
        let dashboardDataOfDeepDive = deepdiveDashboardData;
        let deepdiveTemplatData = deepdiveDashboardTemplateData;
        let sectionData = isDeepDiveDashboardView
          ? deepdiveTemplatData?.sections?.[section_index]
          : null;
        let shareSections = shareBrands
          ? shareTemplateData?.section
            ? shareTemplateData?.section?.[0]
            : shareTemplateData?.sections?.[section_index]
          : null;
        let widget_metadata = isDeepDiveDashboardView
          ? sectionData?.widgets?.[widget_index]
          : pathName === "CommandCenter"?.toLowerCase()
          ? section_index !== null &&
            section_index !== undefined &&
            screen?.sections &&
            screen?.sections?.[section_index]?.widgets?.[widget_index]
            ? screen?.sections?.[section_index]?.widgets?.[widget_index]
            : section_index >= 0 &&
              screen?.sections &&
              screen?.sections?.[section_index]
            ? screen?.sections?.[section_index]
            : screen?.section?.[0]?.widgets?.[widget_index]
            ? screen?.section?.[0]?.widgets?.[widget_index]
            : screen?.widgets && screen?.widgets?.[widget_index]
            ? screen?.widgets && screen?.widgets?.[widget_index]
            : null
          : shareBrands
          ? shareSections?.widgets?.[widget_index]
          : section_index >= 0
          ? panes?.[index]?.sections?.[section_index]?.widgets?.[widget_index]
          : panes?.[index]?.section?.[0]?.widgets?.[widget_index];

        widget_metadata = widget_metadata
          ? widget_metadata
          : metadata
          ? metadata
          : null;
        currentWidget = widget_metadata;
      }

      if (currentWidget?.persist_graphdata?.data?.nodePositions) {
        existingPositions = currentWidget.persist_graphdata.data.nodePositions;
        // console.log("Retrieved existing positions:", existingPositions);
      }
    } catch (error) {
      // console.log("Could not retrieve existing positions:", error);
    }

    // Find parent nodes (those with parent "0")
    const parentNodes = backendData.filter((item) => item.parent === "0");
    const totalGroups = parentNodes.length;

    // Calculate positions for parent nodes with grid layout
    const margin = 40;
    const chartWidth = dimensions.width || 800;
    const chartHeight = dimensions.height || 600;

    // Increase spacing between parent nodes to avoid overlap
    const nodeSpacing = 250; // Fixed spacing between nodes
    // Use a more spread out grid layout
    let cols, rows;
    if (totalGroups <= 2) {
      cols = totalGroups;
      rows = 1;
    } else if (totalGroups <= 4) {
      cols = 2;
      rows = 2;
    } else if (totalGroups <= 6) {
      cols = 3;
      rows = 2;
    } else {
      cols = Math.ceil(Math.sqrt(totalGroups));
      rows = Math.ceil(totalGroups / cols);
    }

    // Calculate total grid size
    const gridWidth = cols === 1 ? 0 : (cols - 1) * nodeSpacing;
    const gridHeight = rows === 1 ? 0 : (rows - 1) * nodeSpacing;

    // Center the grid
    const offsetX = (chartWidth - gridWidth) / 2;
    const offsetY = (chartHeight - gridHeight) / 2;

    // Sort parent nodes by value (count) in descending order
    const sortedParentNodes = [...parentNodes].sort(
      (a, b) => (b.value || 1) - (a.value || 1)
    );

    const totalNodes = sortedParentNodes.length;

    // Calculate radius for each node
    const nodeRadii = [];
    const maxValue = Math.max(...parentNodes.map((g) => g.value || 1));
    sortedParentNodes.forEach((group) => {
      const radius =
        Math.sqrt((group.value || 1) / maxValue) * parentNodeRange[1];
      nodeRadii.push(radius);
    });

    // Use margins to ensure nodes stay within bounds
    const nodeMargin = 80;
    const minX = nodeMargin;
    const maxX = chartWidth - nodeMargin;
    const minY = nodeMargin;
    const maxY = chartHeight - nodeMargin;

    // Function to check if two nodes overlap
    const nodesOverlap = (x1, y1, r1, x2, y2, r2, padding = 30) => {
      const distance = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
      return distance < r1 + r2 + padding;
    };

    // Place nodes randomly with collision detection
    const placedNodes = [];

    sortedParentNodes.forEach((group, index) => {
      const nodeRadius = nodeRadii[index];
      let x, y;

      // Check if we have existing position for this node
      const existingPosition = existingPositions?.[group.id];
      if (
        existingPosition &&
        existingPosition.x !== undefined &&
        existingPosition.y !== undefined
      ) {
        // Use existing position
        x = existingPosition.x;
        y = existingPosition.y;
        // console.log(`Using existing position for node ${group.id}:`, x, y);
      } else {
        // Calculate new position as before
        let attempts = 0;
        const maxAttempts = 100;

        // Try to find a non-overlapping position
        do {
          // Random position within bounds
          x =
            minX + nodeRadius + Math.random() * (maxX - minX - 2 * nodeRadius);
          y =
            minY + nodeRadius + Math.random() * (maxY - minY - 2 * nodeRadius);

          // Check for overlaps with already placed nodes
          let hasOverlap = false;
          for (const placed of placedNodes) {
            if (
              nodesOverlap(x, y, nodeRadius, placed.x, placed.y, placed.radius)
            ) {
              hasOverlap = true;
              break;
            }
          }

          if (!hasOverlap) {
            break;
          }

          attempts++;
        } while (attempts < maxAttempts);

        // If we couldn't find a non-overlapping position, use a grid fallback
        if (attempts === maxAttempts) {
          const cols = Math.ceil(Math.sqrt(totalNodes));
          const col = index % cols;
          const row = Math.floor(index / cols);
          x = minX + nodeRadius + (col * (maxX - minX)) / cols;
          y =
            minY +
            nodeRadius +
            (row * (maxY - minY)) / Math.ceil(totalNodes / cols);
        }
      }

      placedNodes.push({ x, y, radius: nodeRadius });

      // Add some velocity for initial force simulation
      group.vx = (Math.random() - 0.5) * 2;
      group.vy = (Math.random() - 0.5) * 2;

      // Find original group in parentNodes array to update position
      const originalIndex = parentNodes.findIndex((p) => p.id === group.id);
      const originalGroup = parentNodes[originalIndex];

      // Handle new breakdown_by_data format
      let sentimenttype = {};
      if (
        originalGroup.breakdown_by_data &&
        originalGroup.breakdown_by_data.values &&
        originalGroup.breakdown_by_data.values.length > 0
      ) {
        // Merge all objects in the values array
        originalGroup.breakdown_by_data.values.forEach((valueObj) => {
          Object.assign(sentimenttype, valueObj);
        });
      } else if (originalGroup.sentimenttype) {
        // Fallback to old format
        sentimenttype = originalGroup.sentimenttype;
      } else {
        // Default values
        sentimenttype = {
          positive: originalGroup.name === "Positive" ? 100 : 0,
          negative: originalGroup.name === "Negative" ? 100 : 0,
          neutral: originalGroup.name === "Neutral" ? 100 : 0,
        };
      }

      // Normalize sentiment keys to lowercase
      const normalizedSentiments = {};
      Object.keys(sentimenttype).forEach((key) => {
        normalizedSentiments[key.toLowerCase()] = sentimenttype[key];
      });

      // Use the original color from the data
      const displayColor = originalGroup.color || "#94a3b8";

      nodes.push({
        id: originalGroup.id,
        name: originalGroup.name,
        type: "aspectGroup",
        attribute:
          originalGroup.attribute ||
          metadata?.x_axis?.attribute ||
          axisPanelAtt?.x_axis?.attribute ||
          "aspectGroup",
        x: x,
        y: y,
        value: originalGroup.value || 1,
        sentimenttype: normalizedSentiments,
        breakdown_by_data: originalGroup.breakdown_by_data || null,
        color: displayColor,
        share: originalGroup.share,
      });
    });

    // Add child nodes
    backendData
      .filter((item) => item.parent !== "0")
      .forEach((child) => {
        const parent = nodes.find((n) => n.id === child.parent);
        if (parent) {
          // Position children around their parent
          const childrenOfParent = backendData.filter(
            (item) => item.parent === child.parent
          );
          const childIndex = childrenOfParent.findIndex(
            (c) => c.id === child.id
          );
          const childCount = childrenOfParent.length;

          // Calculate parent radius to position children appropriately
          const parentRadius = parent.value
            ? Math.sqrt(
                parent.value / Math.max(...parentNodes.map((g) => g.value || 1))
              ) * parentNodeRange[1]
            : parentNodeRange[1];

          // Calculate minimum distance needed between children
          const childNodeRadius = 25; // Approximate child node radius
          const minDistanceBetweenChildren = childNodeRadius * 2.5; // Minimum distance between child centers

          // Calculate required radius to fit all children without overlap
          const minRequiredRadius =
            childCount > 1
              ? (minDistanceBetweenChildren * childCount) / (2 * Math.PI)
              : 50;

          // Use the larger of minimum required radius or base distance
          const baseDistance = 80; // Increased base distance from parent
          const childRadius = Math.max(
            parentRadius + baseDistance,
            minRequiredRadius + parentRadius + 20
          );

          // Position children in a full circle around parent
          const angleStep = (2 * Math.PI) / childCount;
          const startAngle = -Math.PI / 2; // Start from top
          const childAngle = startAngle + childIndex * angleStep;

          // Check if we have existing position for this child node
          const existingChildPosition = existingPositions?.[child.id];
          let x, y;
          if (
            existingChildPosition &&
            existingChildPosition.x !== undefined &&
            existingChildPosition.y !== undefined
          ) {
            // Use existing position
            x = existingChildPosition.x;
            y = existingChildPosition.y;
            // console.log(
            //   `Using existing position for child node ${child.id}:`,
            //   x,
            //   y
            // );
          } else {
            // Calculate new position as before
            // Random offset for more organic look
            const radiusMultiplier = 1 + Math.random() * 0.3;

            x =
              parent.x + childRadius * radiusMultiplier * Math.cos(childAngle);
            y =
              parent.y + childRadius * radiusMultiplier * Math.sin(childAngle);
          }

          // Handle new breakdown_by_data format for children
          let childSentiment = {};
          if (
            child.breakdown_by_data &&
            child.breakdown_by_data.values &&
            child.breakdown_by_data.values.length > 0
          ) {
            // Merge all objects in the values array
            child.breakdown_by_data.values.forEach((valueObj) => {
              Object.assign(childSentiment, valueObj);
            });
          } else if (child.sentimenttype) {
            childSentiment = child.sentimenttype;
          } else {
            // Fall back to parent's sentiment
            childSentiment = parent.sentimenttype;
          }

          // Normalize sentiment keys to lowercase
          const normalizedChildSentiments = {};
          Object.keys(childSentiment).forEach((key) => {
            normalizedChildSentiments[key.toLowerCase()] = childSentiment[key];
          });

          // Child inherits parent's display color
          nodes.push({
            id: child.id,
            name: child.name,
            type: "aspect",
            attribute: child.attribute || "aspect",
            group: parent.name,
            coreGroup:
              parent.type === "aspectGroup"
                ? parent.name
                : parent.coreGroup || parent.name, // Ensure coreGroup is always the root
            parentId: child.parent,
            x: x,
            y: y,
            value: child.value || 1,
            sentimenttype: normalizedChildSentiments,
            breakdown_by_data: child.breakdown_by_data || null,
            share: child.share,
            color: parent.color, // Use parent's already processed color
          });

          links.push({
            source: child.parent,
            target: child.id,
            value: child.value || 1,
          });
        }
      });

    return { nodes, links };
  };

  // Function to get node positions from current nodes
  const saveNodePositions = (nodes) => {
    try {
      if (!nodes || nodes.length === 0) return {};

      // Create positions object from current nodes
      const nodePositions = {};
      nodes.forEach((node) => {
        if (node.x !== undefined && node.y !== undefined) {
          nodePositions[node.id] = { x: node.x, y: node.y };
        }
      });

      // console.log("Generated node positions:", nodePositions);
      return nodePositions;
    } catch (error) {
      // console.error("Error generating node positions:", error);
      return {};
    }
  };

  // Calculate bubble positions within a node
  const calculateBubblePositions = (nodeData, nodeRadius) => {
    const bubbles = [];

    // Get values from breakdown_by_data if available, otherwise use sentimenttype
    let sentimentData = [];

    if (
      nodeData.breakdown_by_data &&
      nodeData.breakdown_by_data.values &&
      nodeData.breakdown_by_data.values.length > 0
    ) {
      // Merge all values from the array
      const mergedValues = {};
      nodeData.breakdown_by_data.values.forEach((valueObj) => {
        Object.assign(mergedValues, valueObj);
      });

      // Convert to array format with actual counts
      sentimentData = Object.entries(mergedValues)
        .map(([key, count]) => ({
          type: key,
          value: count || 0,
          count: count || 0, // Store actual count
        }))
        .filter((item) => item.value > 0);
    } else if (nodeData.sentimenttype) {
      // Fallback to old format - treat as percentages
      const total = nodeData.value || 100;
      sentimentData = Object.entries(nodeData.sentimenttype)
        .map(([key, percentage]) => ({
          type: key,
          value: percentage || 0,
          count: Math.round((percentage / 100) * total), // Convert percentage to count
        }))
        .filter((item) => item.value > 0);
    }

    if (sentimentData.length === 0) return bubbles;

    // Add color to sentiment data
    sentimentData = sentimentData.map((item) => ({
      ...item,
      color: sentimentColorMap[item.type.toLowerCase()] || "#94a3b8",
    }));

    // Position bubbles in a more compact triangular arrangement
    const positions = [
      { x: 0, y: -nodeRadius * 0.3 }, // Top (positive)
      { x: -nodeRadius * 0.35, y: nodeRadius * 0.25 }, // Bottom left (negative)
      { x: nodeRadius * 0.35, y: nodeRadius * 0.25 }, // Bottom right (neutral)
    ];

    // Calculate total for sizing
    const total = sentimentData.reduce((sum, item) => sum + item.value, 0);

    sentimentData.forEach((sentiment, index) => {
      if (sentiment.value > 0) {
        // Scale bubbles smaller to reduce clutter
        const radius = Math.sqrt(sentiment.value / total) * nodeRadius * 0.8;

        // Position logic - use predefined positions or calculate
        const position = positions[index] || {
          x:
            nodeRadius *
            0.4 *
            Math.cos((index * 2 * Math.PI) / sentimentData.length),
          y:
            nodeRadius *
            0.4 *
            Math.sin((index * 2 * Math.PI) / sentimentData.length),
        };

        const bubbleData = {
          ...sentiment,
          radius,
          x: position.x,
          y: position.y,
          percentage: sentiment.value,
          count: sentiment.count, // Use the actual count from breakdown_by_data
        };

        bubbles.push(bubbleData);
      }
    });

    return bubbles;
  };

  // Handle container resize
  useEffect(() => {
    if (!containerRef.current) return;

    let resizeTimeout;
    let lastWidth = 0;
    let lastHeight = 0;

    // Set initial dimensions
    const rect = containerRef.current.getBoundingClientRect();
    const initialHeight = Math.min(
      Math.max(rect.height || minHeight, minHeight),
      maxHeight
    );
    setDimensions({
      width: rect.width || 800,
      height: initialHeight,
    });
    lastWidth = rect.width || 800;
    lastHeight = initialHeight;

    const resizeObserver = new ResizeObserver((entries) => {
      // Clear any pending resize timeout
      clearTimeout(resizeTimeout);

      resizeTimeout = setTimeout(() => {
        try {
          for (let entry of entries) {
            const { width: containerWidth, height: containerHeight } =
              entry.contentRect;
            const finalHeight = Math.min(
              Math.max(containerHeight || minHeight, minHeight),
              maxHeight
            );

            // Only update if dimensions actually changed (avoid loops)
            if (
              Math.abs(containerWidth - lastWidth) > 1 ||
              Math.abs(finalHeight - lastHeight) > 1
            ) {
              lastWidth = containerWidth || 800;
              lastHeight = finalHeight;

              // Use requestAnimationFrame to avoid ResizeObserver loop
              requestAnimationFrame(() => {
                setDimensions({
                  width: containerWidth || 800,
                  height: finalHeight,
                });
              });
            }
          }
        } catch (error) {
          console.warn("ResizeObserver error:", error);
        }
      }, 100); // Debounce resize events
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      clearTimeout(resizeTimeout);
      resizeObserver.disconnect();
    };
  }, [minHeight, maxHeight]);

  // Load data
  useEffect(() => {
    if (dimensions.width > 0) {
      if (cluster_chart_data) {
        // Use the backend data format processor
        // console.log("Processing cluster_chart_data:", cluster_chart_data);
        const processedData = processBackendData(cluster_chart_data);
        // console.log("Processed data:", processedData);
        setData(processedData);
        setLoading(false);
      } else {
        // No data provided - use empty data or test data
        setData({ nodes: [], links: [] });
        setLoading(false);
      }
    }
  }, [cluster_chart_data, dimensions]);

  // Main D3 visualization
  useEffect(() => {
    if (!data || loading || dimensions.width === 0) return;

    // Check if we have any nodes to render
    if (!data.nodes || data.nodes.length === 0) {
      // console.log("No nodes to render");
      return;
    }

    // Check if this is a local update (adding new nodes) vs complete re-render
    const isLocalUpdate = isLocalUpdateRef.current;

    // Clean up previous zoom if not a local update
    if (!isLocalUpdate) {
      zoomRef.current = null;
    }

    const svg = d3.select(svgRef.current);

    // For local updates, don't clear everything - just update existing elements
    if (!isLocalUpdate) {
      svg.selectAll("*").remove();
    }

    const margin = { top: 10, right: 10, bottom: 10, left: 10 };
    const chartWidth = dimensions.width;
    const chartHeight = dimensions.height;

    // Set viewBox for responsive behavior
    svg
      .attr("width", chartWidth)
      .attr("height", chartHeight)
      .attr("viewBox", `0 0 ${chartWidth} ${chartHeight}`)
      .style("max-width", "100%")
      .style("height", "auto")
      .on("click", (event) => {
        // Prevent click events on the SVG background
        event.stopPropagation();
      });

    // Add a background rect to capture events and enable panning
    svg
      .append("rect")
      .attr("width", chartWidth)
      .attr("height", chartHeight)
      .attr("fill", "transparent")
      .style("pointer-events", "all")
      .style("cursor", "move")
      .on("click", (event) => {
        event.stopPropagation();
      });

    // Add SVG filters for shadows and effects
    const defs = svg.append("defs");

    // Drop shadow filter
    const filter = defs
      .append("filter")
      .attr("id", "drop-shadow")
      .attr("height", "130%");

    filter
      .append("feGaussianBlur")
      .attr("in", "SourceAlpha")
      .attr("stdDeviation", 3);

    filter
      .append("feOffset")
      .attr("dx", 2)
      .attr("dy", 2)
      .attr("result", "offsetblur");

    filter
      .append("feFlood")
      .attr("flood-color", "#000000")
      .attr("flood-opacity", "0.15");

    filter
      .append("feComposite")
      .attr("in2", "offsetblur")
      .attr("operator", "in");

    const feMerge = filter.append("feMerge");
    feMerge.append("feMergeNode");
    feMerge.append("feMergeNode").attr("in", "SourceGraphic");

    // Create a container that centers the content
    let container, mainGroup, g;

    if (isLocalUpdate) {
      // For local updates, reuse existing containers
      container = svg.select(".container");
      mainGroup = container.select(".main-group");
      g = mainGroup.select("g");
    } else {
      // For complete re-renders, create new containers
      container = svg.append("g").attr("class", "container");
      // Main container
      mainGroup = container.append("g").attr("class", "main-group");
      // Create container for links and nodes first
      g = mainGroup
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);
    }

    // Create scales for node sizes
    const parentNodes = data.nodes.filter((d) => d.type === "aspectGroup");
    const childNodes = data.nodes.filter((d) => d.type === "aspect");

    const parentRadiusScale = d3
      .scaleSqrt()
      .domain([
        d3.min(parentNodes, (d) => d.value) || 0,
        d3.max(parentNodes, (d) => d.value) || 1,
      ])
      .range(parentNodeRange);

    const childRadiusScale = d3
      .scaleSqrt()
      .domain([
        d3.min(childNodes, (d) => d.value) || 0,
        d3.max(childNodes, (d) => d.value) || 1,
      ])
      .range(childNodeRange);

    const getNodeRadius = (d) => {
      if (isDynamicSize) {
        // Dynamic sizing based on value
        return d.type === "aspectGroup"
          ? parentRadiusScale(d.value)
          : childRadiusScale(d.value);
      } else {
        // Fixed sizing
        const fixedParentSize = (parentNodeRange[0] + parentNodeRange[1]) / 2;
        const fixedChildSize = (childNodeRange[0] + childNodeRange[1]) / 2;
        return d.type === "aspectGroup" ? fixedParentSize : fixedChildSize;
      }
    };

    // Store current zoom level
    let currentZoom = 1;

    // Add zoom behavior if enabled
    if (enableZoom) {
      const zoom = d3
        .zoom()
        .scaleExtent([0.3, 4])
        .extent([
          [0, 0],
          [chartWidth, chartHeight],
        ])
        .filter((event) => {
          // Prevent all zoom/pan interactions when not in fullscreen mode
          if (!isFullscreen) return false;

          // Prevent zoom on double click
          if (event.type === "dblclick") return false;

          // Allow wheel for zoom
          if (event.type === "wheel") return true;

          // Allow drag for pan when not on a node
          if (event.type === "mousedown" || event.type === "mousemove") {
            // Check if we're clicking on a node
            const target = event.target;
            const isNode =
              target.classList.contains("node") ||
              target.parentElement?.classList.contains("node") ||
              target.parentElement?.parentElement?.classList.contains("node");
            return !isNode; // Allow pan only when not on a node
          }

          return false;
        })
        .on("zoom", (event) => {
          // Only allow zoom/pan in fullscreen mode
          if (!isFullscreen) {
            // Reset to previous transform if not in fullscreen
            event.transform = d3.zoomIdentity;
            container.attr("transform", d3.zoomIdentity);
            return;
          }

          // Apply the transform to container for both zoom and pan
          container.attr("transform", event.transform);
          currentZoom = event.transform.k;

          // Dynamic node sizing based on zoom
          const zoomFactor = Math.min(
            1.5,
            Math.max(0.5, 1 / Math.sqrt(currentZoom))
          );

          g.selectAll(".node circle").attr("r", function () {
            const originalRadius = d3.select(this).attr("data-original-radius");
            if (!originalRadius) {
              const baseRadius = parseFloat(d3.select(this).attr("r"));
              d3.select(this).attr("data-original-radius", baseRadius);
              return baseRadius * zoomFactor;
            }
            return parseFloat(originalRadius) * zoomFactor;
          });

          // Handle sentiment visualizations during zoom
          if (visualizationType === "bubble") {
            g.selectAll(".sentiment-bubble").attr("r", function () {
              const originalRadius = d3
                .select(this)
                .attr("data-original-radius");
              if (!originalRadius) {
                const baseRadius = parseFloat(d3.select(this).attr("r"));
                d3.select(this).attr("data-original-radius", baseRadius);
                return baseRadius * zoomFactor;
              }
              return parseFloat(originalRadius) * zoomFactor;
            });
          } else {
            // Scale pie/donut charts
            g.selectAll(".sentiment-chart").attr(
              "transform",
              `scale(${zoomFactor})`
            );
          }
        });

      svg.call(zoom);

      // Store zoom behavior in ref
      zoomRef.current = zoom;

      // Auto-fit function - using direct transform
      const autoFitAllNodes = () => {
        if (!data.nodes || data.nodes.length === 0) return;

        const nodePadding = 10;
        const labelHeight = 20;

        let minX = Infinity,
          maxX = -Infinity;
        let minY = Infinity,
          maxY = -Infinity;

        data.nodes.forEach((d) => {
          const radius = getNodeRadius(d);
          minX = Math.min(minX, d.x - radius - nodePadding);
          maxX = Math.max(maxX, d.x + radius + nodePadding);
          minY = Math.min(minY, d.y - radius - nodePadding);
          maxY = Math.max(maxY, d.y + radius + nodePadding + labelHeight);
        });

        const boundsWidth = maxX - minX;
        const boundsHeight = maxY - minY;
        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        const availableWidth = chartWidth - 10;
        const availableHeight = chartHeight - 10;
        const scaleX = availableWidth / boundsWidth;
        const scaleY = availableHeight / boundsHeight;
        const scale = Math.min(scaleX, scaleY, 0.9); // Slight zoom out to show full layout

        const translateX = chartWidth / 2 - centerX * scale;
        const translateY = chartHeight / 2 - centerY * scale;

        // Apply transform directly to container without using zoom.transform
        const transform = d3.zoomIdentity
          .translate(translateX, translateY)
          .scale(scale);

        container.attr("transform", transform);

        // Update zoom behavior's internal state
        if (svg.node()) {
          svg.node().__zoom = transform;
        }
        currentZoom = scale;
      };

      // Auto-fit immediately after render
      requestAnimationFrame(() => {
        setTimeout(autoFitAllNodes, 50);
      });
    }

    // Initialize link variable
    let linkSelection = null;

    // Update link positions
    const updateLinks = () => {
      if (!linkSelection) return;

      linkSelection
        .attr("x1", (d) => {
          const sourceId =
            typeof d.source === "object" ? d.source.id : d.source;
          const sourceNode = data.nodes.find((n) => n.id === sourceId);
          return sourceNode ? sourceNode.x : 0;
        })
        .attr("y1", (d) => {
          const sourceId =
            typeof d.source === "object" ? d.source.id : d.source;
          const sourceNode = data.nodes.find((n) => n.id === sourceId);
          return sourceNode ? sourceNode.y : 0;
        })
        .attr("x2", (d) => {
          const targetId =
            typeof d.target === "object" ? d.target.id : d.target;
          const targetNode = data.nodes.find((n) => n.id === targetId);
          return targetNode ? targetNode.x : 0;
        })
        .attr("y2", (d) => {
          const targetId =
            typeof d.target === "object" ? d.target.id : d.target;
          const targetNode = data.nodes.find((n) => n.id === targetId);
          return targetNode ? targetNode.y : 0;
        })
        .style("stroke-width", (d) => {
          // Make links thinner when stretched
          const sourceId =
            typeof d.source === "object" ? d.source.id : d.source;
          const targetId =
            typeof d.target === "object" ? d.target.id : d.target;
          const sourceNode = data.nodes.find((n) => n.id === sourceId);
          const targetNode = data.nodes.find((n) => n.id === targetId);

          if (sourceNode && targetNode) {
            const distance = Math.sqrt(
              Math.pow(sourceNode.x - targetNode.x, 2) +
                Math.pow(sourceNode.y - targetNode.y, 2)
            );
            const normalDistance = 100;
            const stretchFactor = Math.min(1, normalDistance / distance);
            return 3 * stretchFactor;
          }
          return 3;
        });
    };

    // Create groups for links and nodes
    let linkGroup;

    if (isLocalUpdate) {
      // For local updates, reuse existing link group
      linkGroup = g.select(".links");
      if (linkGroup.empty()) {
        linkGroup = g
          .append("g")
          .attr("class", "links")
          .style("pointer-events", "none");
      }
    } else {
      // For complete re-renders, create new link group
      linkGroup = g
        .append("g")
        .attr("class", "links")
        .style("pointer-events", "none");
    }

    let nodeGroup, node;

    if (isLocalUpdate) {
      // For local updates, update existing nodes and add new ones
      nodeGroup = g.select(".nodes");
      if (nodeGroup.empty()) {
        nodeGroup = g.append("g").attr("class", "nodes");
      }

      // Use D3's general update pattern for local updates
      node = nodeGroup.selectAll("g.node").data(data.nodes, (d) => d.id);

      // Remove any nodes that are no longer in the data
      node.exit().remove();

      // Add new nodes
      const nodeEnter = node
        .enter()
        .append("g")
        .attr("class", "node")
        .attr("data-id", (d) => d.id)
        .attr("transform", (d) => `translate(${d.x || 0},${d.y || 0})`)
        .style("cursor", enableDrag ? "grab" : "pointer");

      // Merge enter and update selections
      node = node.merge(nodeEnter);
    } else {
      // For complete re-renders, create everything from scratch
      nodeGroup = g.append("g").attr("class", "nodes");

      node = nodeGroup
        .selectAll("g.node")
        .data(data.nodes, (d) => d.id)
        .enter()
        .append("g")
        .attr("class", "node")
        .attr("data-id", (d) => d.id)
        .attr("transform", (d) => `translate(${d.x},${d.y})`)
        .style("cursor", enableDrag ? "grab" : "pointer");
    }

    // Add drag behavior if enabled
    if (enableDrag) {
      const dragstarted = function (event, d) {
        if (event.sourceEvent) {
          event.sourceEvent.stopPropagation();
        }

        // Mark as dragging
        d3.select(this).raise().classed("dragging", true);
        d3.select(this).style("cursor", "grabbing");

        // Store initial positions for all children if dragging parent
        if (d.type === "aspectGroup") {
          // Store original position
          d.dragStartX = d.x;
          d.dragStartY = d.y;

          d.childrenData = data.nodes
            .filter((n) => n.parentId === d.id)
            .map((child) => ({
              node: child,
              originalX: child.x,
              originalY: child.y,
              offsetX: child.x - d.x,
              offsetY: child.y - d.y,
            }));
        }
      };

      const dragged = function (event, d) {
        // Prevent default behavior
        if (event.sourceEvent) {
          event.sourceEvent.preventDefault();
          event.sourceEvent.stopPropagation();
        }

        // Update position with bounds checking
        const newX = Math.max(50, Math.min(chartWidth - 50, event.x));
        const newY = Math.max(50, Math.min(chartHeight - 50, event.y));

        d.x = newX;
        d.y = newY;

        // Move the dragged node
        d3.select(this).attr("transform", `translate(${d.x},${d.y})`);

        // Push away other nodes to prevent overlap
        const minDistance = 20; // Minimum distance between node edges
        const pushForce = 0.3; // How strongly nodes push each other

        // Check all nodes for collision
        data.nodes.forEach((otherNode) => {
          // Skip self and children of the dragged node
          if (
            otherNode.id === d.id ||
            (d.type === "aspectGroup" && otherNode.parentId === d.id) ||
            (d.parentId && d.parentId === otherNode.id)
          ) {
            return;
          }

          // Calculate distance between nodes
          const dx = otherNode.x - d.x;
          const dy = otherNode.y - d.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          // Get combined radius with buffer
          const r1 = getNodeRadius(d);
          const r2 = getNodeRadius(otherNode);
          const combinedRadius = r1 + r2 + minDistance;

          // If nodes are overlapping, push the other node away
          if (distance < combinedRadius && distance > 0) {
            const overlap = combinedRadius - distance;
            const pushAngle = Math.atan2(dy, dx);
            const pushX = Math.cos(pushAngle) * overlap * pushForce;
            const pushY = Math.sin(pushAngle) * overlap * pushForce;

            // Apply smooth movement
            otherNode.x += pushX;
            otherNode.y += pushY;

            // Keep within bounds
            const nodeRadius = getNodeRadius(otherNode);
            otherNode.x = Math.max(
              nodeRadius + 20,
              Math.min(chartWidth - nodeRadius - 20, otherNode.x)
            );
            otherNode.y = Math.max(
              nodeRadius + 20,
              Math.min(chartHeight - nodeRadius - 20, otherNode.y)
            );

            // If the other node is a parent, move its children with it
            if (otherNode.type === "aspectGroup") {
              data.nodes
                .filter((n) => n.parentId === otherNode.id)
                .forEach((child) => {
                  child.x += pushX;
                  child.y += pushY;
                  // Keep children within bounds too
                  const childRadius = getNodeRadius(child);
                  child.x = Math.max(
                    childRadius + 20,
                    Math.min(chartWidth - childRadius - 20, child.x)
                  );
                  child.y = Math.max(
                    childRadius + 20,
                    Math.min(chartHeight - childRadius - 20, child.y)
                  );
                });
            }
          }
        });

        // If parent node, update children with elastic following
        if (d.type === "aspectGroup" && d.childrenData) {
          const maxDistance = 150; // Maximum distance before children start following
          const elasticity = 0.3; // How quickly children follow (0-1)

          d.childrenData.forEach((childData) => {
            // Calculate distance from original position
            const targetX = d.x + childData.offsetX;
            const targetY = d.y + childData.offsetY;
            const distanceFromTarget = Math.sqrt(
              Math.pow(childData.node.x - targetX, 2) +
                Math.pow(childData.node.y - targetY, 2)
            );

            // Only move children if parent moved beyond threshold
            if (distanceFromTarget > maxDistance) {
              // Calculate elastic movement
              const moveX = (targetX - childData.node.x) * elasticity;
              const moveY = (targetY - childData.node.y) * elasticity;

              childData.node.x += moveX;
              childData.node.y += moveY;
            }
          });
        }

        // Update all node positions at once
        const svg = d3.select(svgRef.current);
        svg.selectAll("g.node").attr("transform", function (n) {
          return `translate(${n.x},${n.y})`;
        });

        // Update all links
        updateLinks();
      };

      const dragended = function (event, d) {
        if (event.sourceEvent) {
          event.sourceEvent.stopPropagation();
        }

        // Reset cursor and dragging state
        d3.select(this).classed("dragging", false);
        d3.select(this).style("cursor", enableDrag ? "grab" : "pointer");

        // If parent node, animate children to final positions
        if (d.type === "aspectGroup" && d.childrenData) {
          // Animate children back to position
          const animationDuration = 300;
          const startTime = Date.now();

          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / animationDuration, 1);

            // Ease out cubic function
            const easeProgress = 1 - Math.pow(1 - progress, 3);

            // Only animate if childrenData exists
            if (d.childrenData && Array.isArray(d.childrenData)) {
              d.childrenData.forEach((childData) => {
                const finalX = d.x + childData.offsetX;
                const finalY = d.y + childData.offsetY;
                const currentX = childData.node.x;
                const currentY = childData.node.y;

                // Interpolate position
                const newX = currentX + (finalX - currentX) * easeProgress;
                const newY = currentY + (finalY - currentY) * easeProgress;

                // Update the data
                childData.node.x = newX;
                childData.node.y = newY;
              });
            }

            // Update all node positions
            d3.select(svgRef.current)
              .selectAll("g.node")
              .attr("transform", function (n) {
                return `translate(${n.x},${n.y})`;
              });

            updateLinks();

            // Continue animation if not complete
            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              // Clean up when animation is complete
              if (d.childrenData && Array.isArray(d.childrenData)) {
                d.childrenData.forEach((childData) => {
                  childData.node.x = d.x + childData.offsetX;
                  childData.node.y = d.y + childData.offsetY;
                });
                delete d.childrenData;
              }
              delete d.dragStartX;
              delete d.dragStartY;
            }
          };

          requestAnimationFrame(animate);
        }
      };

      const drag = d3
        .drag()
        .on("start", dragstarted)
        .on("drag", dragged)
        .on("end", dragended);

      node.call(drag);
    }

    // Add invisible background circle for better event capture
    node
      .append("circle")
      .attr("r", (d) => getNodeRadius(d) + 10)
      .attr("fill", "transparent")
      .attr("stroke", "none")
      .style("cursor", enableDrag ? "grab" : "pointer");

    // Draw nodes with improved styling
    // Outer ring
    node.each(function (d) {
      const nodeGroup = d3.select(this);
      const radius = getNodeRadius(d);
      const shapePath = createShapePath(nodeShape, radius + 3);

      if (shapePath) {
        // Custom shape outline
        nodeGroup
          .append("path")
          .attr("d", shapePath)
          .attr("fill", "none")
          .attr("stroke", getNodeColor(d))
          .attr("stroke-width", d.type === "aspectGroup" ? 3 : 2)
          .attr("opacity", d.type === "aspectGroup" ? 0.8 : 0.6)
          .attr("class", "node-ring")
          .style("pointer-events", "none");
      } else {
        // Circle outline
        nodeGroup
          .append("circle")
          .attr("r", radius + 3)
          .attr("fill", "none")
          .attr("stroke", getNodeColor(d))
          .attr("stroke-width", d.type === "aspectGroup" ? 3 : 2)
          .attr("opacity", d.type === "aspectGroup" ? 0.8 : 0.6)
          .attr("class", "node-ring")
          .style("pointer-events", "none");
      }
    });

    // Main shape with shadow
    node.each(function (d) {
      const nodeGroup = d3.select(this);
      const radius = getNodeRadius(d);
      const shapePath = createShapePath(nodeShape, radius);

      // Check if this is a newly added node
      const isNewNode = newlyAddedNodes.has(d.id);

      const fillColor = isNewNode ? "#034391" : "white"; // Dark blue background for new nodes

      if (shapePath) {
        // Custom shape
        nodeGroup
          .append("path")
          .attr("d", shapePath)
          .attr("fill", fillColor)
          .attr("fill-opacity", 0.95)
          .attr("stroke", "none")
          .attr("filter", "url(#drop-shadow)")
          .attr("class", "node-shape")
          .style("pointer-events", "none");
      } else {
        // Circle
        nodeGroup
          .append("circle")
          .attr("r", radius)
          .attr("fill", fillColor)
          .attr("fill-opacity", 0.95)
          .attr("stroke", "none")
          .attr("filter", "url(#drop-shadow)")
          .attr("class", "node-shape")
          .style("pointer-events", "none");
      }
    });

    // Add sentiment visualization (bubble, donut, or pie)
    node.each(function (d) {
      const nodeGroup = d3.select(this);
      const nodeRadius = getNodeRadius(d);

      if (visualizationType === "bubble") {
        // Original bubble visualization
        const bubbles = calculateBubblePositions(d, nodeRadius);

        bubbles.forEach((bubble, index) => {
          // Create a group for each bubble
          const bubbleGroup = nodeGroup
            .append("g")
            .attr("class", "bubble-group")
            .attr("transform", `translate(${bubble.x},${bubble.y})`);

          // Add the bubble circle
          bubbleGroup
            .append("circle")
            .attr("class", "sentiment-bubble")
            .attr("cx", 0)
            .attr("cy", 0)
            .attr("r", bubble.radius)
            .attr("fill", bubble.color)
            .attr("stroke", "white")
            .attr("stroke-width", 2)
            .style("opacity", 0.85)
            .style("cursor", "pointer");

          // Add invisible hit area for stable hover
          bubbleGroup
            .append("circle")
            .attr("class", "bubble-hit-area")
            .attr("cx", 0)
            .attr("cy", 0)
            .attr("r", bubble.radius * 1.2)
            .attr("fill", "transparent")
            .style("cursor", "pointer");

          // Add the count text
          bubbleGroup
            .append("text")
            .attr("x", 0)
            .attr("y", bubble.radius * 0.1) // Slightly adjust y position based on radius
            .attr("text-anchor", "middle")
            .attr(
              "font-size",
              getDynamicFontSize(bubble.radius, "bubble") + "px"
            )
            .attr("font-weight", "bold")
            .attr("fill", "white")
            .text(kFormatter(bubble.count, true))
            .style("pointer-events", "none");

          // Add hover effects
          bubbleGroup
            .on("mouseenter", function (event) {
              event.stopPropagation();
              const group = d3.select(this);

              if (!group.classed("hovered")) {
                group.classed("hovered", true);

                // Apply highlight and shadow effects only
                group
                  .select(".sentiment-bubble")
                  .style(
                    "filter",
                    "brightness(1.3) drop-shadow(0px 6px 12px rgba(0,0,0,0.4))"
                  );
              }

              // Show tooltip
              const bubbleElement = this;
              const bubbleBBox = bubbleElement.getBoundingClientRect();
              const container = containerRef.current;
              const containerRect = container.getBoundingClientRect();

              // Calculate bubble position relative to container
              const bubbleX =
                bubbleBBox.left + bubbleBBox.width / 2 - containerRect.left;
              const bubbleY =
                bubbleBBox.top + bubbleBBox.height / 2 - containerRect.top;

              const sentimentData =
                d.breakdown_by_data?.values || d.sentimenttype || {};
              const total = Object.values(sentimentData).reduce((sum, val) => {
                if (typeof val === "object") {
                  return sum + Object.values(val).reduce((s, v) => s + v, 0);
                }
                return sum + val;
              }, 0);
              const percentage =
                total > 0 ? Math.round((bubble.count / total) * 100) : 0;

              // Position tooltip to the right of the bubble
              const tooltipWidth = 250;
              const tooltipHeight = 180;
              const bubbleRadius = bubble.radius || 20;
              let x = bubbleX + bubbleRadius + 20;
              let y = bubbleY - tooltipHeight / 2;

              // Adjust if tooltip goes off edges
              if (x + tooltipWidth > containerRect.width - 10) {
                x = bubbleX - bubbleRadius - tooltipWidth - 20;
              }
              if (x < 10) x = 10;
              if (y < 10) y = 10;
              if (y + tooltipHeight > containerRect.height - 10) {
                y = containerRect.height - tooltipHeight - 10;
              }

              setTooltip({
                visible: true,
                x: x,
                y: y,
                data: {
                  name: `${
                    bubble.type.charAt(0).toUpperCase() + bubble.type.slice(1)
                  }: ${percentage}%`,
                  value: bubble.count,
                  sentimenttype: { [bubble.type]: percentage },
                },
              });
            })
            .on("mouseleave", function (event) {
              event.stopPropagation();
              const group = d3.select(this);

              group.classed("hovered", false);

              // Remove effects with smooth transition
              group.select(".sentiment-bubble").style("filter", "none");

              setTooltip({ visible: false, x: 0, y: 0, data: null });
            })
            .on("click", function (event) {
              event.stopPropagation();
              event.preventDefault();

              // Calculate total and percentage for bubble
              const sentimentData =
                d.breakdown_by_data?.values || d.sentimenttype || {};
              const total = Object.values(sentimentData).reduce((sum, val) => {
                if (typeof val === "object") {
                  return sum + Object.values(val).reduce((s, v) => s + v, 0);
                }
                return sum + val;
              }, 0);
              const percentage =
                total > 0 ? Math.round((bubble.count / total) * 100) : 0;

              // Create bubble-specific data for deep dive
              const bubbleData = {
                ...d, // Include parent node data
                name: `${d.name}`,
                value: bubble.count,
                type: `bubble_${bubble.type}`,
                sentimentType: bubble.type,
                parentNode: d.name,
                bubbleInfo: {
                  type: bubble.type,
                  count: bubble.count,
                  percentage: percentage,
                },
              };

              // Call deep dive function for bubble
              handleDeepDiveNode(bubbleData);
            });
        });
      } else {
        // Pie or donut chart visualization
        let sentimentData = [];
        const nodeData = d;

        // Process data for pie/donut chart
        if (
          nodeData.breakdown_by_data &&
          nodeData.breakdown_by_data.values &&
          nodeData.breakdown_by_data.values.length > 0
        ) {
          const mergedValues = {};
          nodeData.breakdown_by_data.values.forEach((valueObj) => {
            Object.assign(mergedValues, valueObj);
          });
          sentimentData = Object.entries(mergedValues)
            .map(([key, count]) => ({
              type: key,
              value: count || 0,
              count: count || 0,
            }))
            .filter((item) => item.value > 0);
        } else if (nodeData.sentimenttype) {
          sentimentData = Object.entries(nodeData.sentimenttype)
            .map(([key, percentage]) => ({
              type: key,
              value: percentage,
              count: Math.round((percentage / 100) * nodeData.value),
            }))
            .filter((item) => item.value > 0);
        }

        if (sentimentData.length > 0) {
          const innerRadius =
            visualizationType === "donut" ? nodeRadius * 0.35 : 0;
          const outerRadius = nodeRadius * 0.85;

          const {
            pie,
            arc,
            data: pieData,
          } = createPieChart(
            sentimentData,
            nodeRadius,
            visualizationType === "donut"
          );

          const chartGroup = nodeGroup
            .append("g")
            .attr("class", "sentiment-chart")
            .style("opacity", 0.85);

          // Create a group for each slice that contains both path and text
          const sliceGroups = chartGroup
            .selectAll("g.slice")
            .data(pieData)
            .enter()
            .append("g")
            .attr("class", "slice");

          // Draw pie/donut slices
          const slicePaths = sliceGroups
            .append("path")
            .attr("d", arc)
            .attr(
              "fill",
              (d) => sentimentColorMap[d.data.type.toLowerCase()] || "#94a3b8"
            )
            .attr("stroke", "white")
            .attr("stroke-width", 2)
            .style("cursor", "pointer");

          // Add invisible larger hit area to prevent flickering
          const hitArc = d3
            .arc()
            .innerRadius(innerRadius)
            .outerRadius(outerRadius * 1.2);

          sliceGroups
            .append("path")
            .attr("d", hitArc)
            .attr("fill", "transparent")
            .attr("class", "hit-area")
            .style("cursor", "pointer");

          // Add labels for larger slices
          sliceGroups.each(function (d) {
            const angle = d.endAngle - d.startAngle;
            if (angle > 0.5) {
              const [x, y] = arc.centroid(d);
              d3.select(this)
                .append("text")
                .attr("transform", `translate(${x},${y})`)
                .attr("text-anchor", "middle")
                .attr(
                  "font-size",
                  getDynamicFontSize(getNodeRadius(node), "pie") + "px"
                )
                .attr("font-weight", "bold")
                .attr("fill", "white")
                .text(kFormatter(d.data.count, true))
                .style("pointer-events", "none");
            }
          });

          // Add hover effects to the slice groups
          sliceGroups
            .on("mouseenter", function (event, d) {
              event.stopPropagation();
              const sliceGroup = d3.select(this);

              // Only apply effects if not already hovered
              if (!sliceGroup.classed("hovered")) {
                sliceGroup.classed("hovered", true);

                // Apply highlight and shadow effects only
                sliceGroup
                  .select("path:not(.hit-area)")
                  .style(
                    "filter",
                    "brightness(1.3) drop-shadow(0px 6px 12px rgba(0,0,0,0.4))"
                  );
              }

              // Show tooltip for slice
              const sliceElement = this;
              const sliceBBox = sliceElement.getBoundingClientRect();
              const container = containerRef.current;
              const containerRect = container.getBoundingClientRect();

              // Calculate slice position relative to container
              const sliceX =
                sliceBBox.left + sliceBBox.width / 2 - containerRect.left;
              const sliceY =
                sliceBBox.top + sliceBBox.height / 2 - containerRect.top;

              const percentage = Math.round(
                (d.data.value /
                  sentimentData.reduce((sum, item) => sum + item.value, 0)) *
                  100
              );

              // Position tooltip to the right of the slice
              const tooltipWidth = 250;
              const tooltipHeight = 180;
              let x = sliceX + 40;
              let y = sliceY - tooltipHeight / 2;

              // Adjust if tooltip goes off edges
              if (x + tooltipWidth > containerRect.width - 10) {
                x = sliceX - tooltipWidth - 40;
              }
              if (x < 10) x = 10;
              if (y < 10) y = 10;
              if (y + tooltipHeight > containerRect.height - 10) {
                y = containerRect.height - tooltipHeight - 10;
              }

              setTooltip({
                visible: true,
                x: x,
                y: y,
                data: {
                  name: `${
                    d.data.type.charAt(0).toUpperCase() + d.data.type.slice(1)
                  }: ${percentage}%`,
                  value: d.data.count,
                  sentimenttype: { [d.data.type]: percentage },
                },
              });
            })
            .on("mouseleave", function (event, d) {
              event.stopPropagation();
              const sliceGroup = d3.select(this);

              sliceGroup.classed("hovered", false);

              // Remove hover effects
              sliceGroup.select("path:not(.hit-area)").style("filter", "none");

              setTooltip({ visible: false, x: 0, y: 0, data: null });
            })
            .on("click", function (event, d) {
              event.stopPropagation();
              event.preventDefault();

              // Get total for percentage calculation
              const total = sentimentData.reduce(
                (sum, item) => sum + item.value,
                0
              );
              const percentage = Math.round((d.data.value / total) * 100);

              // Create slice-specific data for deep dive
              const sliceData = {
                ...nodeData, // Include parent node data
                name: `${nodeData.name}`,
                value: d.data.count,
                type: `slice_${d.data.type}`,
                sentimentType: d.data.type,
                parentNode: nodeData.name,
                sliceInfo: {
                  type: d.data.type,
                  count: d.data.count,
                  percentage: percentage,
                  visualizationType: visualizationType,
                },
              };

              // Call deep dive function for slice
              handleDeepDiveNode(sliceData);
            });
        }
      }
    });

    // Add expand/collapse indicator for nodes with children
    node.each(function (d) {
      // Check if this node has children (works for both parent and child nodes)
      const hasChildren = data.nodes.some((n) => n.parentId === d.id);
      if (hasChildren) {
        const nodeGroup = d3.select(this);
        const nodeRadius = getNodeRadius(d);

        // Add expand/collapse button
        const button = nodeGroup
          .append("g")
          .attr("class", "expand-button")
          .style("cursor", "pointer")
          .style("opacity", 0)
          .style("visibility", "hidden")
          .attr(
            "title",
            collapsedNodes.has(d.id) ? "Click to expand" : "Click to collapse"
          );

        // Position on the right side of the node
        const buttonX = nodeRadius + 10;
        const buttonY = 0;

        button
          .append("circle")
          .attr("cx", buttonX)
          .attr("cy", buttonY)
          .attr("r", 12)
          .attr("fill", "white")
          .attr("stroke", "#6b7280")
          .attr("stroke-width", 1.5)
          .style("opacity", 1)
          .style("filter", "drop-shadow(0px 2px 4px rgba(0,0,0,0.2))");

        button
          .append("text")
          .attr("x", buttonX)
          .attr("y", buttonY + 5)
          .attr("text-anchor", "middle")
          .attr("font-size", "18px")
          .attr("font-weight", "bold")
          .attr("fill", "#6b7280")
          .text(collapsedNodes.has(d.id) ? "+" : "−")
          .style("user-select", "none")
          .style("pointer-events", "none");

        // Add click handler
        button
          .on("mouseenter", function () {
            d3.select(this)
              .select("circle")
              .style("fill", "#e5e7eb")
              .style("stroke", "#3b82f6")
              .style("cursor", "pointer");
            d3.select(this).select("text").style("fill", "#3b82f6");
          })
          .on("mouseleave", function () {
            d3.select(this)
              .select("circle")
              .style("fill", "white")
              .style("stroke", "#6b7280");
            d3.select(this).select("text").style("fill", "#6b7280");
          })
          .on("click", function (event) {
            event.stopPropagation();
            event.preventDefault();
            toggleNodeCollapse(d.id);
          });
      }
    });

    // Add load more icon for child nodes (initially hidden)
    // Only show in dashboard views, not in widget maker
    const pathName = window.location.pathname?.split("/")?.[1];
    const showLoadIcons =
      pathName === "widgetMaker" ||
      pathName === "edit-widget" ||
      pathName === "dashboard" ||
      pathName === "pin-dashboard" ||
      pathName === "share";

    node.each(function (d) {
      if (
        // !panes?.[index]?.is_widget_maker_open_obj?.open_flag &&
        !isDashboardScheduleModal &&
        !PdfDownloadStatus &&
        !PptGeneratingStatus &&
        !isPreviewState &&
        !isDashboardScheduleModal &&
        showLoadIcons &&
        shouldShowLoadIcon(d)
      ) {
        // Check if load icon should be shown based on depth and configuration
        const nodeGroup = d3.select(this);
        const nodeRadius = getNodeRadius(d);

        // Create a group for the load icon positioned on the right side
        const loadIconGroup = nodeGroup
          .append("g")
          .attr("class", "load-icon-group")
          .attr("transform", `translate(${nodeRadius + 10}, 0)`)
          .style("opacity", 0)
          .style("visibility", "hidden")
          .style("cursor", isFullscreen ? "not-allowed" : "pointer")
          .style("transition", "opacity 0.2s ease, visibility 0.2s ease");

        // Add background circle for the icon
        loadIconGroup
          .append("circle")
          .attr("r", 12)
          .attr("fill", isFullscreen ? "#f3f4f6" : "white")
          .attr("stroke", isFullscreen ? "#d1d5db" : "#6b7280")
          .attr("stroke-width", 1.5)
          .style("filter", "drop-shadow(0px 2px 4px rgba(0,0,0,0.2))");

        // Add the load more icon (plus sign with circle to indicate add/expand)
        // Plus sign for "add more nodes"
        const iconColor = isFullscreen ? "#9ca3af" : "#6b7280";
        loadIconGroup.append("g").attr("class", "plus-icon").html(`
            <line x1="-5" y1="0" x2="5" y2="0" stroke="${iconColor}" stroke-width="2" stroke-linecap="round"/>
            <line x1="0" y1="-5" x2="0" y2="5" stroke="${iconColor}" stroke-width="2" stroke-linecap="round"/>
          `);

        // Prepare tooltip with next level attribute name
        const nodeDepth = getNodeDepth(d, data.nodes);
        let nextAttributeName = "";

        // Get the next level attribute name based on the configuration
        if (
          metadata?.y_axes &&
          Array.isArray(metadata.y_axes) &&
          metadata.y_axes[nodeDepth]
        ) {
          // Dashboard mode: Get from metadata
          nextAttributeName =
            allAttributes?.[metadata.y_axes[nodeDepth]?.attribute] ||
            metadata.y_axes[nodeDepth]?.trace_name ||
            metadata.y_axes[nodeDepth]?.attribute ||
            "";
        } else if (
          axisPanelAtt?.breakdown_by &&
          Array.isArray(axisPanelAtt.breakdown_by) &&
          axisPanelAtt.breakdown_by[nodeDepth]
        ) {
          // Widget Maker mode: Get from axisPanelAtt
          nextAttributeName =
            allAttributes?.[axisPanelAtt.breakdown_by[nodeDepth]?.attribute] ||
            axisPanelAtt.breakdown_by[nodeDepth]?.trace_name ||
            axisPanelAtt.breakdown_by[nodeDepth]?.attribute ||
            "";
        }

        // Format the tooltip text
        const tooltipText = isFullscreen
          ? "Loading more nodes is disabled in full screen"
          : nextAttributeName
          ? `Load next level nodes (${nextAttributeName})`
          : "Load next level nodes";

        // Add native browser tooltip as fallback
        loadIconGroup.append("title").text(tooltipText);

        // Add click handler for the load icon with custom tooltip
        loadIconGroup
          .on("mouseenter", function (event) {
            event.stopPropagation();
            if (!isFullscreen) {
              d3.select(this)
                .select("circle")
                .style("fill", "#e5e7eb")
                .style("stroke", "#3b82f6");
              d3.select(this).selectAll("line").style("stroke", "#3b82f6");
            }
          })
          .on("mouseleave", function (event) {
            event.stopPropagation();
            if (!isFullscreen) {
              d3.select(this)
                .select("circle")
                .style("fill", "white")
                .style("stroke", "#6b7280");
              d3.select(this).selectAll("line").style("stroke", "#6b7280");
            }
          })
          .on("click", function (event) {
            event.stopPropagation();
            event.preventDefault();

            // Trigger API call for loading more data
            handleLoadMoreData(d);
          });
      }
    });

    // Add labels
    node
      .append("text")
      .attr("x", 0)
      .attr("y", (d) => getNodeRadius(d) + 20)
      .attr("text-anchor", "middle")
      .attr("font-size", (d) => (d.type === "aspectGroup" ? "13px" : "11px"))
      .attr("font-weight", (d) => (d.type === "aspectGroup" ? "600" : "400"))
      .attr("fill", "#1f2937")
      .text((d) => {
        // Truncate name if longer than 20 characters
        if (d.name.length > 30) {
          return d.name.substring(0, 30) + "...";
        }
        return d.name;
      })
      .each(function (d) {
        const text = d3.select(this);
        // Get the displayed text (which might be truncated)
        const displayedText =
          d.name.length > 30 ? d.name.substring(0, 30) + "..." : d.name;
        const words = displayedText.split(/\s+/);

        // Limit to maximum 2 lines
        if (words.length > 1) {
          text.text("");

          // Calculate how to split words across 2 lines
          if (words.length <= 2) {
            // If 2 or fewer words, put each on its own line
            words.forEach((word, i) => {
              text
                .append("tspan")
                .attr("x", 0)
                .attr("dy", i === 0 ? "0" : "1.2em")
                .text(word);
            });
          } else {
            // For more than 2 words, split as evenly as possible across 2 lines
            const midPoint = Math.ceil(words.length / 2);
            const line1 = words.slice(0, midPoint).join(" ");
            const line2 = words.slice(midPoint).join(" ");

            text.append("tspan").attr("x", 0).attr("dy", "0").text(line1);

            text.append("tspan").attr("x", 0).attr("dy", "1.2em").text(line2);
          }
        }
      });

    // Add hover and click interactions
    if (showTooltip || onNodeHover || onNodeClick) {
      node
        .on("mouseenter", function (event, d) {
          event.stopPropagation();

          // Show expand/collapse button on hover for nodes with children
          const expandButton = d3.select(this).select(".expand-button");
          if (!expandButton.empty()) {
            expandButton.style("opacity", 1).style("visibility", "visible");
          }

          // Show load icon on hover for child nodes that haven't been loaded
          const loadIcon = d3.select(this).select(".load-icon-group");
          if (!loadIcon.empty()) {
            // Simply show the icon without transition
            loadIcon.style("visibility", "visible").style("opacity", 1);
          }

          // Highlight node and its descendants on hover (works for any node type)
          // Recursively find all descendants (children, grandchildren, etc.)
          const getAllDescendants = (parentId) => {
            const directChildren = data.nodes
              .filter((n) => n.parentId === parentId)
              .map((n) => n.id);

            let allDescendants = [...directChildren];
            directChildren.forEach((childId) => {
              allDescendants = [
                ...allDescendants,
                ...getAllDescendants(childId),
              ];
            });

            return allDescendants;
          };

          // Get all descendant IDs for the hovered node
          const descendantIds = getAllDescendants(d.id);

          // Only proceed if there are descendants to highlight
          if (descendantIds.length > 0) {
            // Highlight links connected to this node and all its descendants
            const svg = d3.select(svgRef.current);
            const allLinks = svg.selectAll("g.links line");

            if (allLinks.size() > 0) {
              allLinks.each(function (l) {
                const linkElement = d3.select(this);
                const sourceId =
                  typeof l.source === "object" ? l.source.id : l.source;
                const targetId =
                  typeof l.target === "object" ? l.target.id : l.target;

                // Highlight links in the descendant tree
                // Include: current node to children, children to grandchildren, etc.
                if (
                  sourceId === d.id ||
                  (descendantIds.includes(sourceId) &&
                    descendantIds.includes(targetId))
                ) {
                  linkElement
                    .style("stroke-width", 5)
                    .style("stroke-opacity", 1)
                    .style("stroke", getNodeColor(d));
                }
              });
            }

            // Highlight descendant nodes and dim others
            nodeGroup.selectAll("g.node").each(function (n) {
              const nodeElement = d3.select(this);

              // Keep current node and its descendants fully visible, dim others
              if (n.id === d.id || descendantIds.includes(n.id)) {
                nodeElement.style("opacity", 1);

                // Check if this is a newly added node
                const isNewlyAdded = newlyAddedNodes.has(n.id);

                // Use much thicker border for the hovered node itself
                const strokeWidth = n.id === d.id ? 6 : isNewlyAdded ? 5 : 4;

                // Apply highlight to both node-ring and path elements
                // Use the hovered node's color for highlighting all related nodes
                nodeElement
                  .select(".node-ring")
                  .style("stroke", getNodeColor(d))
                  .style("stroke-width", strokeWidth)
                  .style("opacity", 1)
                  .style(
                    "filter",
                    n.id === d.id
                      ? "drop-shadow(0 0 8px rgba(0, 130, 246, 0.8))"
                      : "none"
                  );

                // Also highlight any non-node-ring shape paths
                nodeElement
                  .select("path:not(.node-ring)")
                  .style("stroke", getNodeColor(d))
                  .style("stroke-width", strokeWidth);
              } else {
                nodeElement.style("opacity", 0.3);
              }
            });
          }

          if (onNodeHover) onNodeHover(d);
          if (showTooltip) {
            // Clear any existing timeout
            if (tooltipTimeoutRef.current) {
              clearTimeout(tooltipTimeoutRef.current);
              tooltipTimeoutRef.current = null;
            }

            // Get the actual node element and its bounding box
            const nodeElement = this;
            const nodeBBox = nodeElement.getBoundingClientRect();

            // Get container bounding box
            const container = containerRef.current;
            const containerRect = container.getBoundingClientRect();

            // Calculate node position relative to container
            const nodeX =
              nodeBBox.left + nodeBBox.width / 2 - containerRect.left;
            const nodeY =
              nodeBBox.top + nodeBBox.height / 2 - containerRect.top;

            // Get node radius for spacing
            const nodeRadius = getNodeRadius(d);

            // Tooltip dimensions
            const tooltipWidth = 250;
            const tooltipHeight = 180;

            // Position tooltip to the right of the node
            let x = nodeX + nodeRadius + 20;
            let y = nodeY - tooltipHeight / 2;

            // Container dimensions
            const containerWidth = containerRect.width;
            const containerHeight = containerRect.height;

            // Adjust if tooltip goes off right edge
            if (x + tooltipWidth > containerWidth - 10) {
              // Show on left side instead
              x = nodeX - nodeRadius - tooltipWidth - 20;
            }

            // Keep x within bounds
            if (x < 10) {
              x = 10;
            }

            // Keep y within bounds
            if (y < 10) {
              y = 10;
            } else if (y + tooltipHeight > containerHeight - 10) {
              y = containerHeight - tooltipHeight - 10;
            }

            // Set tooltip with a small delay for stability
            tooltipTimeoutRef.current = setTimeout(() => {
              setTooltip({
                visible: true,
                x: x,
                y: y,
                data: d,
              });
            }, 50);
          }
        })
        // Removed mousemove to prevent tooltip following cursor
        .on("mouseleave", function (event, d) {
          event.stopPropagation();

          // Hide expand/collapse button on mouse leave
          const expandButton = d3.select(this).select(".expand-button");
          if (!expandButton.empty()) {
            expandButton.style("opacity", 0).style("visibility", "hidden");
          }

          // Hide load icon on mouse leave
          const loadIcon = d3.select(this).select(".load-icon-group");
          if (!loadIcon.empty()) {
            // Simply hide the icon without transition
            loadIcon.style("opacity", 0).style("visibility", "hidden");
          }

          // Reset highlights
          const svg = d3.select(svgRef.current);
          svg
            .selectAll("g.links line")
            .style("stroke-width", 3)
            .style("stroke-opacity", linkOpacity * 0.8);

          // Reset node opacity
          nodeGroup.selectAll("g.node").style("opacity", 1);

          // Reset node borders to their original colors
          nodeGroup.selectAll("g.node").each(function (n) {
            const nodeElement = d3.select(this);

            // Check if this is a newly added node that should keep highlight
            const isNewlyAdded = newlyAddedNodes.has(n.id);

            // Reset border color and width
            nodeElement
              .select(".node-ring")
              .style("stroke", isNewlyAdded ? "#3b82f6" : getNodeColor(n))
              .style(
                "stroke-width",
                isNewlyAdded ? 4 : n.type === "aspectGroup" ? 3 : 2
              )
              .style("opacity", n.type === "aspectGroup" ? 0.8 : 0.6)
              .style("filter", "none");

            // Reset any path elements
            nodeElement
              .select("path")
              .style("stroke", isNewlyAdded ? "#3b82f6" : getNodeColor(n))
              .style(
                "stroke-width",
                isNewlyAdded ? 4 : n.type === "aspectGroup" ? 3 : 2
              );
          });

          if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
            tooltipTimeoutRef.current = null;
          }
          if (showTooltip) {
            // Hide immediately on mouse leave
            setTooltip({ visible: false, x: 0, y: 0, data: null });
          }
        })
        .on("click", function (event, d) {
          // Prevent event from bubbling up
          event.stopPropagation();
          event.preventDefault();

          // Check if we're not dragging
          const isDragging = d3.select(this).classed("dragging");
          if (!isDragging) {
            // Call deep dive function for logging
            handleDeepDiveNode(d);
          }

          // Return false to prevent any default behavior
          return false;
        })
        .on("dblclick", function (event, d) {
          // Prevent event from bubbling up
          event.stopPropagation();
          event.preventDefault();

          // Allow double-click on any node with children
          const hasChildren = data.nodes.some((n) => n.parentId === d.id);
          if (hasChildren) {
            toggleNodeCollapse(d.id);
          }

          // Return false to prevent zoom behavior
          return false;
        });
    }

    // Create links
    if (isLocalUpdate) {
      // For local updates, update existing links and add new ones
      linkSelection = linkGroup.selectAll("line").data(data.links);

      // Remove any links that are no longer in the data
      linkSelection.exit().remove();

      // Add new links
      const linkEnter = linkSelection
        .enter()
        .append("line")
        .attr("class", "link")
        .style("stroke", (d) => {
          // Get the parent node (source) color
          const sourceId =
            typeof d.source === "object" ? d.source.id : d.source;
          const sourceNode = data.nodes.find((n) => n.id === sourceId);

          // Use the getNodeColor function to handle color logic
          return sourceNode ? getNodeColor(sourceNode) : linkColor;
        })
        .style("stroke-opacity", linkOpacity * 0.8)
        .style("stroke-width", 3);

      // Merge enter and update selections
      linkSelection = linkSelection.merge(linkEnter);
    } else {
      // For complete re-renders, create everything from scratch
      linkSelection = linkGroup
        .selectAll("line")
        .data(data.links)
        .enter()
        .append("line")
        .attr("class", "link")
        .style("stroke", (d) => {
          // Get the parent node (source) color
          const sourceId =
            typeof d.source === "object" ? d.source.id : d.source;
          const sourceNode = data.nodes.find((n) => n.id === sourceId);

          // Use the getNodeColor function to handle color logic
          return sourceNode ? getNodeColor(sourceNode) : linkColor;
        })
        .style("stroke-opacity", linkOpacity * 0.8)
        .style("stroke-width", 3);
    }

    // Apply force simulation for better distribution
    let simulation;

    if (isLocalUpdate && simulationRef.current) {
      // For local updates, reuse existing simulation and preserve positions
      simulation = simulationRef.current;

      // Fix existing nodes in their current positions
      data.nodes.forEach((node) => {
        if (!newlyAddedNodes.has(node.id)) {
          // Fix existing nodes by setting fx/fy (fixed positions)
          node.fx = node.x;
          node.fy = node.y;
        }
      });

      // Update the simulation with new node data
      simulation.nodes(data.nodes);

      // Update the force links
      simulation.force("link").links(data.links);

      // Restart simulation only for new nodes
      simulation.alpha(0.3).restart();

      // After a short time, unfix the existing nodes to allow natural repositioning if needed
      setTimeout(() => {
        data.nodes.forEach((node) => {
          if (!newlyAddedNodes.has(node.id)) {
            node.fx = null;
            node.fy = null;
          }
        });
      }, 500);
    } else {
      // For complete re-renders, create new simulation
      simulation = d3
        .forceSimulation(data.nodes)
        .force(
          "charge",
          d3
            .forceManyBody()
            .strength((d) => (d.type === "aspectGroup" ? -500 : -200))
            .distanceMax(300)
        )
        .force(
          "collide",
          d3
            .forceCollide()
            .radius((d) => getNodeRadius(d) + 25)
            .strength(0.9)
            .iterations(2)
        )
        .force("x", d3.forceX(chartWidth / 2).strength(0.02))
        .force("y", d3.forceY(chartHeight / 2).strength(0.02))
        .force(
          "link",
          d3
            .forceLink(data.links)
            .id((d) => d.id)
            .distance((d) => {
              // Variable distance based on node types
              const source = data.nodes.find(
                (n) => n.id === (d.source.id || d.source)
              );
              const target = data.nodes.find(
                (n) => n.id === (d.target.id || d.target)
              );
              if (source && target) {
                return getNodeRadius(source) + getNodeRadius(target) + 60;
              }
              return 100;
            })
            .strength(0.3)
        )
        .on("tick", () => {
          // Keep nodes within bounds
          data.nodes.forEach((d) => {
            const radius = getNodeRadius(d) + 20;
            d.x = Math.max(radius, Math.min(chartWidth - radius, d.x));
            d.y = Math.max(radius, Math.min(chartHeight - radius, d.y));
          });

          // Update node positions
          node.attr("transform", (d) => `translate(${d.x},${d.y})`);
          updateLinks();
        });

      // Store simulation instance for reuse when adding new nodes
      simulationRef.current = simulation;

      // Run simulation for a bit then stop
      setTimeout(() => {
        simulation.stop();
      }, 1500); // Reduced from 3000ms to 1500ms for faster initial render
    }

    // Reset the local update flag
    isLocalUpdateRef.current = false;

    // Apply initial collapsed state immediately
    if (collapsedNodes.size > 0) {
      const svg = d3.select(svgRef.current);

      // Create a map for faster lookups
      const nodeMap = new Map(data.nodes.map((n) => [n.id, n]));

      // Function to check if a node should be visible (same logic as in toggleNodeCollapse)
      const isNodeVisible = (nodeId) => {
        const node = nodeMap.get(nodeId);
        if (!node) return false;

        // Root nodes (aspectGroup) are always visible
        if (node.type === "aspectGroup") return true;

        // Check if any ancestor is collapsed
        let currentNode = node;
        while (currentNode.parentId) {
          if (collapsedNodes.has(currentNode.parentId)) {
            return false; // Parent is collapsed, so this node is hidden
          }
          currentNode = nodeMap.get(currentNode.parentId);
          if (!currentNode) break;
        }

        return true; // All ancestors are expanded
      };

      // Determine visible nodes
      const visibleNodes = new Set();
      data.nodes.forEach((node) => {
        if (isNodeVisible(node.id)) {
          visibleNodes.add(node.id);
        }
      });

      // Apply visibility in batch
      requestAnimationFrame(() => {
        svg.selectAll("g.node").style("display", function (d) {
          return visibleNodes.has(d.id) ? null : "none";
        });

        svg.selectAll("g.links line").style("display", function (l) {
          const sourceId =
            typeof l.source === "object" ? l.source.id : l.source;
          const targetId =
            typeof l.target === "object" ? l.target.id : l.target;
          return visibleNodes.has(sourceId) && visibleNodes.has(targetId)
            ? null
            : "none";
        });
      });
    }
  }, [
    data,
    loading,
    dimensions,
    enableDrag,
    enableZoom,
    showTooltip,
    onNodeClick,
    onNodeHover,
    linkColor,
    linkOpacity,
    visualizationType,
    isDynamicSize,
    nodeShape,
    collapsedNodes,
    loadedNodes,
  ]);

  // Apply search highlighting
  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);

    // Reset all nodes to default opacity
    svg.selectAll("g.node").style("opacity", 1);
    svg.selectAll("g.links line").style("opacity", linkOpacity * 0.8);

    // Apply highlighting if there are search results
    if (highlightedNodes.size > 0) {
      svg.selectAll("g.node").each(function (d) {
        const nodeElement = d3.select(this);
        if (highlightedNodes.has(d.id)) {
          nodeElement.style("opacity", 1);
          // Add glow effect
          nodeElement
            .select(".node-ring")
            .style("filter", "drop-shadow(0 0 8px rgba(0, 130, 246, 0.8))")
            .attr("stroke-width", 4);
        } else {
          nodeElement.style("opacity", 0.2);
        }
      });

      // Highlight connected links
      svg.selectAll("g.links line").each(function (d) {
        const sourceId = typeof d.source === "object" ? d.source.id : d.source;
        const targetId = typeof d.target === "object" ? d.target.id : d.target;

        if (highlightedNodes.has(sourceId) && highlightedNodes.has(targetId)) {
          d3.select(this).style("opacity", linkOpacity);
        } else {
          d3.select(this).style("opacity", 0.1);
        }
      });
    } else {
      // Remove any glow effects
      svg
        .selectAll(".node-ring")
        .style("filter", "none")
        .attr("stroke-width", (d) => (d.type === "aspectGroup" ? 3 : 2));
    }
  }, [highlightedNodes, linkOpacity]);

  useEffect(() => {
    // Only process if we have new nodes to add
    if (
      Number(sessionStorage.getItem("clusterSectionIndex")) === section_index &&
      Number(sessionStorage.getItem("clusterWidgetIndex")) === widget_index &&
      clusterNextLoadedNodes &&
      clusterNextLoadedNodes.length > 0 &&
      !isProcessingRef.current // Prevent multiple processing
    ) {
      isProcessingRef.current = true; // Set processing flag
      sessionStorage.removeItem("clusterSectionIndex");
      sessionStorage.removeItem("clusterWidgetIndex");

      // Check if we have existing data structure to append to
      if (!data || !data.nodes || !data.links) {
        return;
      }

      // Process the new nodes - data structure is already validated above
      // Process the incoming data format
      const processedNewNodes = [];
      const processedNewLinks = [];
      let parentNodeId = null;

      // Create a map to quickly find nodes by name
      const nodesByName = {};
      data.nodes.forEach((node) => {
        nodesByName[node.name.toLowerCase()] = node;
      });

      // Use sessionStorage first (most reliable) then fall back to ref
      let defaultParentNode = null;
      let parentNodeIdToUse =
        sessionStorage.getItem("clusterClickedParentNodeId") ||
        clickedParentNodeRef.current;

      if (parentNodeIdToUse) {
        // Try multiple approaches to find the node
        const idToFind = String(parentNodeIdToUse);

        // Try exact match first
        defaultParentNode = data.nodes.find((n) => String(n.id) === idToFind);

        // If not found, try without string conversion
        if (!defaultParentNode) {
          defaultParentNode = data.nodes.find(
            (n) => n.id === parentNodeIdToUse
          );
        }

        // If still not found, try numeric comparison
        if (!defaultParentNode && !isNaN(parentNodeIdToUse)) {
          defaultParentNode = data.nodes.find(
            (n) => Number(n.id) === Number(parentNodeIdToUse)
          );
        }

        if (defaultParentNode) {
          parentNodeId = defaultParentNode.id;
        } else {
          console.error(
            "✗ Could not find clicked parent node with ID:",
            parentNodeIdToUse,
            "Type:",
            typeof parentNodeIdToUse,
            "in data.nodes. Available node IDs:",
            data.nodes.map((n) => ({
              id: n.id,
              type: typeof n.id,
              name: n.name,
            }))
          );
        }
      } else {
        console.error(
          "✗ No parent node ID available from ref or sessionStorage"
        );
      }

      // If still no parent, we can't process the nodes
      if (!defaultParentNode) {
        console.error(
          "Unable to determine parent node for new children - exiting"
        );
        return;
      }

      // Process each node from the API response
      clusterNextLoadedNodes.forEach((apiNode) => {
        // ALWAYS use the clicked parent node, ignore the parent field from API
        const parentNode = defaultParentNode;

        // Skip if parent not found (this shouldn't happen if clickedParentNodeId is set)
        if (!parentNode) {
          console.error(
            "No parent node available for:",
            apiNode.name,
            "- defaultParentNode is null/undefined"
          );
          return;
        }

        // Validate that parent node has required properties
        if (!parentNode.x || !parentNode.y) {
          console.warn("Parent node missing x/y coordinates, using defaults");
          parentNode.x = parentNode.x || 400;
          parentNode.y = parentNode.y || 300;
        }

        // Generate a unique ID for the new node using the CLICKED node's ID
        // The clicked node (defaultParentNode) is the one that was clicked to load these children
        // So if you click 3.1 (Twitter brand mention), children will be 3.1_1, 3.1_2, etc.
        // If you click 3.2 (Public Tweets), children will be 3.2_1, 3.2_2, etc.
        const clickedNodeId = defaultParentNode.id; // This is the node that was clicked
        const uniqueNodeId = `${clickedNodeId}_${apiNode.id}`;

        // Get the core parent (root node like Twitter) for the coreGroup
        // Always verify the coreGroup is correct by finding the actual root
        let coreGroupValue;

        // Always find the actual root parent to ensure correctness
        const coreParent = getCoreParentNode(defaultParentNode);
        coreGroupValue = coreParent.name;

        // Log if there's a mismatch
        if (
          defaultParentNode.coreGroup &&
          defaultParentNode.coreGroup !== coreGroupValue
        ) {
          console.warn(
            `MISMATCH: Parent has coreGroup="${defaultParentNode.coreGroup}" but actual root is "${coreGroupValue}"`
          );
        }

        // Convert API node format to cluster chart format
        const newNode = {
          id: uniqueNodeId, // Use unique ID based on clicked node
          name: apiNode.name,
          value: apiNode.value,
          type: "aspect", // Use "aspect" for consistency with initial child nodes
          parentId: clickedNodeId, // The clicked node becomes the parent
          parentNode: defaultParentNode.name, // Store parent node name for deep dive
          parentAttribute: defaultParentNode.attribute, // Store parent attribute
          group: defaultParentNode.name, // Store parent name as group
          coreGroup: coreGroupValue, // Store the root parent (e.g., Twitter)
          attribute: apiNode.attribute,
          trace_name: apiNode.trace_name,
          color: apiNode.color,
          share: apiNode.share,
          // Position near the clicked node with some randomization
          x: defaultParentNode.x + (Math.random() - 0.5) * 150,
          y: defaultParentNode.y + (Math.random() - 0.5) * 150,
          // Add breakdown_by_data if available in API response
          breakdown_by_data: apiNode.breakdown_by_data || null,
          // Add sentiment data if available in your API response
          sentimenttype: apiNode.sentimenttype || {
            positive: Math.random() * 40 + 20,
            negative: Math.random() * 30 + 10,
            neutral: Math.random() * 40 + 20,
          },
        };

        processedNewNodes.push(newNode);

        // Create link from clicked node to this new child node
        // The clicked node is the parent of these new nodes
        processedNewLinks.push({
          source: clickedNodeId, // Use the clicked node as source
          target: uniqueNodeId, // Use the unique node ID
        });
      });

      // Mark the clicked node (which is the parent of new nodes) as loaded to hide its load icon
      // Use the clicked parent node from ref/storage which is the actual clicked node
      const clickedNodeId =
        clickedParentNodeRef.current ||
        sessionStorage.getItem("clusterClickedParentNodeId");
      if (clickedNodeId) {
        // Convert to string to ensure consistency
        const nodeIdToMark = String(clickedNodeId);

        setLoadedNodes((prev) => {
          const newSet = new Set(prev);
          newSet.add(nodeIdToMark);
          // Also add the original format in case of type differences
          newSet.add(clickedNodeId);
          return newSet;
        });

        // Remove from loading state since loading is complete
        setLoadingNodes((prev) => {
          const newSet = new Set(prev);
          newSet.delete(nodeIdToMark);
          newSet.delete(clickedNodeId);
          return newSet;
        });

        // Clear timeout for this node
        const timeoutId = loadingTimeoutRef.current.get(nodeIdToMark);
        if (timeoutId) {
          clearTimeout(timeoutId);
          loadingTimeoutRef.current.delete(nodeIdToMark);
        }
      } else {
        console.warn("No clicked node ID found to mark as loaded");
      }

      // Update the data with new nodes and links
      if (processedNewNodes.length > 0) {
        // Create a new data object to trigger proper re-render
        // Make sure we're creating a proper data structure
        const currentNodes = data.nodes || [];
        const currentLinks = data.links || [];

        const updatedData = {
          nodes: [...currentNodes, ...processedNewNodes],
          links: [...currentLinks, ...processedNewLinks],
        };

        // Track newly added nodes for highlighting
        const newNodeIds = new Set(processedNewNodes.map((node) => node.id));
        setNewlyAddedNodes(newNodeIds);

        // Auto-remove highlight after 10 seconds by directly updating DOM
        setTimeout(() => {
          // Instead of updating state, directly change the fill color in the DOM
          const svg = d3.select(svgRef.current);
          newNodeIds.forEach((nodeId) => {
            svg
              .select(`g.node[data-id="${nodeId}"]`)
              .select(".node-shape")
              .attr("fill", "white");
          });
          // Clear the state without triggering re-render of entire chart
          setNewlyAddedNodes(new Set());
        }, 10000);

        // Update local data state immediately for UI update
        isLocalUpdateRef.current = true; // Mark that we're doing a local update
        setData(updatedData);

        let finalData = [];

        // Convert all current nodes (including previously added ones) to cluster-chart format
        currentNodes.forEach((node) => {
          // For parent nodes (type: aspectGroup), parent should be "0"
          // For child nodes, use their parentId
          const parentValue =
            node.type === "aspectGroup" ? "0" : node.parentId || "0";

          finalData.push({
            id: node.id,
            parent: parentValue,
            name: node.name,
            value: node.value || node.count || 0,
            attribute: node.attribute || "",
            trace_name: node.trace_name || "",
            color: node.color || "",
            share: node.share || 0,
            breakdown_by_data: node.breakdown_by_data || null,
          });
        });

        // Add the new nodes to finalData
        processedNewNodes.forEach((node) => {
          const newNode = {
            id: node.id,
            parent: node.parentId, // This is already the clicked node ID
            name: node.name,
            value: node.value,
            attribute: node.attribute,
            trace_name: node.trace_name || "",
            color: node.color,
            share: node.share,
            breakdown_by_data: node.breakdown_by_data || null,
          };
          finalData.push(newNode);
        });

        // console.log("Final data =>", finalData);
        let pathName = window.location.pathname?.split("/")?.[1];
        if (
          panes?.[index]?.is_widget_maker_open_obj?.open_flag ||
          pathName === "widgetMaker" ||
          pathName === "edit-widget"
        ) {
          let b_data = {
            data: {
              kpi: [],
              "cluster-chart": finalData,
              dropdown: null,
              roi_grid: null,
            },
            refresh_ri: false,
          };

          // Use a timeout to avoid conflict with local state update
          dispatch({
            type: "SET_WIDGET_MAKER_CHART",
            payload: b_data,
          });
        } else if (shareTemplateData) {
          // share dashboard - preserve existing dropdown and roi_grid values
          let existingDropdown = null;
          let existingRoiGrid = null;
          let existingNodePositions = null;

          // Get existing values from persist_graphdata
          if (
            shareTemplateData?.sections?.[section_index]?.widgets?.[
              widget_index
            ]?.persist_graphdata
          ) {
            existingDropdown =
              shareTemplateData.sections[section_index]?.widgets[widget_index]
                ?.persist_graphdata?.data?.dropdown;
            existingRoiGrid =
              shareTemplateData.sections[section_index]?.widgets[widget_index]
                ?.persist_graphdata?.data?.roi_grid;
            existingNodePositions =
              shareTemplateData.sections[section_index]?.widgets[widget_index]
                ?.persist_graphdata?.data?.nodePositions;
          } else if (
            shareTemplateData?.section?.[0]?.widgets?.[widget_index]
              ?.persist_graphdata
          ) {
            existingDropdown =
              shareTemplateData.section[0].widgets[widget_index]
                ?.persist_graphdata?.data?.dropdown;
            existingRoiGrid =
              shareTemplateData.section[0].widgets[widget_index]
                ?.persist_graphdata?.data?.roi_grid;
            existingNodePositions =
              shareTemplateData.section[0].widgets[widget_index]
                ?.persist_graphdata?.data?.nodePositions;
          }

          let updated_cluster_data = {
            data: {
              kpi: [],
              "cluster-chart": finalData,
              dropdown: existingDropdown,
              roi_grid: existingRoiGrid,
              nodePositions: existingNodePositions,
            },
            refresh_ri: false,
          };

          if (metadata) {
            let updateShareTemplateData = { ...shareTemplateData };
            if (updateShareTemplateData?.sections) {
              updateShareTemplateData.sections[section_index].widgets[
                widget_index
              ].deep_dive_on = null;
              updateShareTemplateData.sections[section_index].widgets[
                widget_index
              ].graphData = updated_cluster_data;

              if (
                updateShareTemplateData?.sections?.[section_index]?.widgets?.[
                  widget_index
                ]?.persist_graphdata
              ) {
                updateShareTemplateData.sections[section_index].widgets[
                  widget_index
                ].persist_graphdata = updated_cluster_data;
              }
            } else {
              updateShareTemplateData.section[0].widgets[
                widget_index
              ].deep_dive_on = null;
              updateShareTemplateData.section[0].widgets[
                widget_index
              ].graphData = updated_cluster_data;
              if (
                updateShareTemplateData?.section?.[0]?.widgets?.[widget_index]
                  ?.persist_graphdata
              ) {
                updateShareTemplateData.section[0].widgets[
                  widget_index
                ].persist_graphdata = updated_cluster_data;
              }
            }
            dispatch({
              type: "SET_SHARE_TEMPLATE_DATA",
              payload: updateShareTemplateData,
            });
          }
        } else {
          // view dashboard - preserve existing dropdown and roi_grid values
          let existingDropdown = null;
          let existingRoiGrid = null;
          let existingNodePositions = null;

          // Get existing values from persist_graphdata
          if (
            panes?.[index]?.sections?.[section_index]?.widgets?.[widget_index]
              ?.persist_graphdata
          ) {
            existingDropdown =
              panes[index].sections[section_index]?.widgets[widget_index]
                ?.persist_graphdata?.data?.dropdown;
            existingRoiGrid =
              panes[index].sections[section_index]?.widgets[widget_index]
                ?.persist_graphdata?.data?.roi_grid;
            existingNodePositions =
              panes[index].sections[section_index]?.widgets[widget_index]
                ?.persist_graphdata?.data?.nodePositions;
          } else if (
            panes?.[index]?.section?.[0]?.widgets?.[widget_index]
              ?.persist_graphdata
          ) {
            existingDropdown =
              panes[index].section[0].widgets[widget_index]?.persist_graphdata
                ?.data?.dropdown;
            existingRoiGrid =
              panes[index].section[0].widgets[widget_index]?.persist_graphdata
                ?.data?.roi_grid;
            existingNodePositions =
              panes[index].section[0].widgets[widget_index]?.persist_graphdata
                ?.data?.nodePositions;
          }

          let updated_cluster_data = {
            data: {
              kpi: [],
              "cluster-chart": finalData,
              dropdown: existingDropdown,
              roi_grid: existingRoiGrid,
              nodePositions: existingNodePositions,
            },
            refresh_ri: false,
          };

          if (metadata) {
            let updatePanes = [...panes];
            if (updatePanes?.[index]?.sections) {
              updatePanes[index].sections[section_index].widgets[
                widget_index
              ].deep_dive_on = null;
              updatePanes[index].sections[section_index].widgets[
                widget_index
              ].graphData = updated_cluster_data;

              if (
                updatePanes[index]?.sections?.[section_index]?.widgets?.[
                  widget_index
                ]?.persist_graphdata
              ) {
                updatePanes[index].sections[section_index].widgets[
                  widget_index
                ].persist_graphdata = updated_cluster_data;
              }
            } else {
              updatePanes[index].section[0].widgets[widget_index].deep_dive_on =
                null;
              updatePanes[index].section[0].widgets[widget_index].graphData =
                updated_cluster_data;
              if (
                updatePanes?.[index]?.section?.[0]?.widgets?.[widget_index]
                  ?.persist_graphdata
              ) {
                updatePanes[index].section[0].widgets[
                  widget_index
                ].persist_graphdata = updated_cluster_data;
              }
            }
            dispatch({ type: "SET_WIDGET_GRAPH_DATA", panes: updatePanes });
          }
        }

        dispatch(setClusterNextLoadedNodes([]));
        clickedParentNodeRef.current = null;
        sessionStorage.removeItem("clusterClickedParentNodeId");
        isProcessingRef.current = false; // Reset processing flag
      } else {
        // If no new nodes were processed, clear loading state for the clicked node
        const clickedNodeId =
          clickedParentNodeRef.current ||
          sessionStorage.getItem("clusterClickedParentNodeId");
        if (clickedNodeId) {
          const nodeIdToMark = String(clickedNodeId);
          setLoadingNodes((prev) => {
            const newSet = new Set(prev);
            newSet.delete(nodeIdToMark);
            newSet.delete(clickedNodeId);
            return newSet;
          });

          // Clear timeout for this node
          const timeoutId = loadingTimeoutRef.current.get(nodeIdToMark);
          if (timeoutId) {
            clearTimeout(timeoutId);
            loadingTimeoutRef.current.delete(nodeIdToMark);
          }
        }
      }
    } else {
      // Clear any stale loading states if clusterNextLoadedNodes is empty or doesn't match current widget
      if (clusterNextLoadedNodes && clusterNextLoadedNodes.length === 0) {
        setLoadingNodes(new Set()); // Clear all loading states
      }
    }
  }, [clusterNextLoadedNodes, data]);

  // Reset collapsed nodes when total_count_flag changes
  useEffect(() => {
    // Clear all collapsed nodes to reset child node flow
    setCollapsedNodes(new Set());
    // Clear loaded nodes to allow reloading with new flag
    setLoadedNodes(new Set());
    // Clear any loading states
    setLoadingNodes(new Set());

    isProcessingRef.current = false; // Reset processing flag

    // Clear any stored parent node references
    // clickedParentNodeRef.current = null;
    // sessionStorage.removeItem("clusterClickedParentNodeId");

    // console.log(
    //   "Resetting cluster chart state due to total_count_flag change:",
    //   total_count_flag
    // );
  }, [total_count_flag]);

  // Cleanup tooltip timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
      // Clear all loading timeouts
      loadingTimeoutRef.current.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      loadingTimeoutRef.current.clear();
    };
  }, []);

  // Handle no data case
  if (!loading && data && (!data.nodes || data.nodes.length === 0)) {
    return (
      <div
        ref={containerRef}
        style={{
          textAlign: "center",
          padding: "50px",
          width: typeof width === "string" ? width : `${width}px`,
          height: typeof height === "string" ? height : `${height}px`,
          minHeight: `${minHeight}px`,
          maxHeight: `${maxHeight}px`,
          backgroundColor,
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
        }}
      >
        <div style={{ color: "#666" }}>No data available</div>
      </div>
    );
  }

  if (loading || dimensions.width === 0) {
    return (
      <div
        ref={containerRef}
        style={{
          textAlign: "center",
          padding: "50px",
          width: typeof width === "string" ? width : `${width}px`,
          height: typeof height === "string" ? height : `${height}px`,
          minHeight: `${minHeight}px`,
          maxHeight: `${maxHeight}px`,
          backgroundColor,
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
        }}
      >
        <div>
          Loading cluster chart...
          <br />
          <small style={{ fontSize: "10px", opacity: 0.5 }}>
            {dimensions.width === 0
              ? "Waiting for container dimensions..."
              : "Processing data..."}
          </small>
        </div>
      </div>
    );
  }

  // Helper function to get attribute display names
  const getAttributeDisplayName = (attribute) => {
    if (!attribute) return "";
    const allAttributes = allAttributes1 || All_WM_attributes || {};
    return (
      allAttributes[attribute] ||
      attribute.charAt(0).toUpperCase() + attribute.slice(1).replace(/_/g, " ")
    );
  };

  // Generate helpful guide text based on current attributes
  const generateHelpfulText = () => {
    const guidePoints = [];

    // Check if we're in widget maker mode or dashboard mode
    let isWidgetMakerMode = false;

    // Only try to get pane index if panes is actually an array
    if (Array.isArray(panes) && activeKey) {
      try {
        const paneIndex = getPaneIndex(activeKey, panes);
        if (
          paneIndex >= 0 &&
          panes[paneIndex]?.is_widget_maker_open_obj?.open_flag
        ) {
          isWidgetMakerMode = true;
        }
      } catch (e) {
        // Fallback if getPaneIndex fails
        // console.log("Error getting pane index:", e);
      }
    }

    // Also check URL path as fallback
    if (!isWidgetMakerMode && window.location.pathname === "/widgetMaker") {
      isWidgetMakerMode = true;
    }

    // Check if data has any expanded nodes to determine what's already visible
    const hasExpandedNodes =
      data?.nodes?.some((node) => node.parentId) || false;

    if (isWidgetMakerMode && axisPanelAtt) {
      // Widget Maker mode: use axisPanelAtt
      const parentAttr = axisPanelAtt.x_axis?.attribute;
      const childAttrs = axisPanelAtt.breakdown_by || [];

      // Check which levels are loaded
      const hasFirstLevelLoaded = hasExpandedNodes;
      const hasSecondLevelLoaded =
        data?.nodes?.some((node) => {
          // Check if this is a second level node (has parent that has parent)
          if (node.parentId) {
            const parent = data.nodes.find((n) => n.id === node.parentId);
            return parent && parent.parentId;
          }
          return false;
        }) || false;
      const hasThirdLevelLoaded =
        data?.nodes?.some((node) => {
          // Check if this is a third level node (has parent that has parent that has parent)
          if (node.parentId) {
            const parent = data.nodes.find((n) => n.id === node.parentId);
            if (parent && parent.parentId) {
              const grandParent = data.nodes.find(
                (n) => n.id === parent.parentId
              );
              return grandParent && grandParent.parentId;
            }
          }
          return false;
        }) || false;

      // Point 1: Parent node (Channel) - always loaded as it's the center node
      const parentName = getAttributeDisplayName(parentAttr);
      if (parentName) {
        guidePoints.push(`${parentName} nodes are visible ✓`);
      }

      // Point 2: First child node (Inner Channel)
      if (childAttrs.length > 0) {
        const firstChildName = getAttributeDisplayName(
          childAttrs[0]?.attribute
        );
        if (firstChildName) {
          if (hasFirstLevelLoaded) {
            guidePoints.push(`${firstChildName} nodes are visible ✓`);
          } else {
            guidePoints.push(
              `Click any ${parentName} node to see its ${firstChildName} details`
            );
          }
        }
      }

      // Point 3: Second child node (Post Type)
      if (childAttrs.length > 1) {
        const firstChildName = getAttributeDisplayName(
          childAttrs[0]?.attribute
        );
        const secondChildName = getAttributeDisplayName(
          childAttrs[1]?.attribute
        );
        if (secondChildName) {
          if (hasSecondLevelLoaded) {
            guidePoints.push(`${secondChildName} details are shown ✓`);
          } else if (hasFirstLevelLoaded) {
            guidePoints.push(
              `Click any ${firstChildName} node to view ${secondChildName} details`
            );
          } else {
            guidePoints.push(
              `Expand ${firstChildName} to explore ${secondChildName}`
            );
          }
        }
      }

      // Point 4: Third child node (Gender)
      if (childAttrs.length > 2) {
        const secondChildName = getAttributeDisplayName(
          childAttrs[1]?.attribute
        );
        const thirdChildName = getAttributeDisplayName(
          childAttrs[2]?.attribute
        );
        if (thirdChildName) {
          if (hasThirdLevelLoaded) {
            guidePoints.push(`${thirdChildName} details are shown ✓`);
          } else if (hasSecondLevelLoaded) {
            guidePoints.push(
              `Click ${secondChildName} bubbles to view ${thirdChildName} details`
            );
          } else {
            guidePoints.push(
              `Expand ${secondChildName} to enable ${thirdChildName} details`
            );
          }
        }
      }
    } else if (metadata) {
      // Dashboard mode: use metadata
      const parentAttr = metadata.x_axis?.attribute;
      const childAttrs = metadata.y_axes || metadata.breakdown_by || [];

      // Check which levels are loaded
      const hasFirstLevelLoaded = hasExpandedNodes;
      const hasSecondLevelLoaded =
        data?.nodes?.some((node) => {
          // Check if this is a second level node (has parent that has parent)
          if (node.parentId) {
            const parent = data.nodes.find((n) => n.id === node.parentId);
            return parent && parent.parentId;
          }
          return false;
        }) || false;
      const hasThirdLevelLoaded =
        data?.nodes?.some((node) => {
          // Check if this is a third level node (has parent that has parent that has parent)
          if (node.parentId) {
            const parent = data.nodes.find((n) => n.id === node.parentId);
            if (parent && parent.parentId) {
              const grandParent = data.nodes.find(
                (n) => n.id === parent.parentId
              );
              return grandParent && grandParent.parentId;
            }
          }
          return false;
        }) || false;

      // Point 1: Parent node - always loaded as it's the center node
      const parentName = getAttributeDisplayName(parentAttr);
      if (parentName) {
        guidePoints.push(`${parentName} nodes are visible ✓`);
      }

      // Point 2: First child node
      if (childAttrs.length > 0) {
        const firstChildName = getAttributeDisplayName(
          childAttrs[0]?.attribute
        );
        if (firstChildName) {
          if (hasFirstLevelLoaded) {
            guidePoints.push(`${firstChildName} nodes are visible ✓`);
          } else {
            guidePoints.push(
              `Click any ${parentName} nodes to see its ${firstChildName} details`
            );
          }
        }
      }

      // Point 3: Second child node
      if (childAttrs.length > 1) {
        const firstChildName = getAttributeDisplayName(
          childAttrs[0]?.attribute
        );
        const secondChildName = getAttributeDisplayName(
          childAttrs[1]?.attribute
        );
        if (secondChildName) {
          if (hasSecondLevelLoaded) {
            guidePoints.push(`${secondChildName} details are shown ✓`);
          } else if (hasFirstLevelLoaded) {
            guidePoints.push(
              `Click any ${firstChildName} node to view ${secondChildName} details`
            );
          } else {
            guidePoints.push(
              `Expand ${firstChildName} to explore ${secondChildName}`
            );
          }
        }
      }

      // Point 4: Third child node
      if (childAttrs.length > 2) {
        const secondChildName = getAttributeDisplayName(
          childAttrs[1]?.attribute
        );
        const thirdChildName = getAttributeDisplayName(
          childAttrs[2]?.attribute
        );
        if (thirdChildName) {
          if (hasThirdLevelLoaded) {
            guidePoints.push(`${thirdChildName} details are shown ✓`);
          } else if (hasSecondLevelLoaded) {
            guidePoints.push(
              `Click ${secondChildName} nodes to view ${thirdChildName} details`
            );
          } else {
            guidePoints.push(
              `Expand ${secondChildName} to enable ${thirdChildName} details`
            );
          }
        }
      }
    }

    return guidePoints;
  };

  const helpfulTextPoints = generateHelpfulText();

  return (
    <>
      <div
        ref={containerRef}
        className="cluster-chart-container"
        style={{
          position: isFullscreen ? "fixed" : "relative",
          top: isFullscreen ? 0 : "auto",
          left: isFullscreen ? 0 : "auto",
          right: isFullscreen ? 0 : "auto",
          bottom: isFullscreen ? 0 : "auto",
          backgroundColor,
          borderRadius: isFullscreen ? 0 : "8px",
          padding: "2px",
          width: isFullscreen
            ? "100vw"
            : typeof width === "string"
            ? width
            : `${width}px`,
          height: isFullscreen
            ? "100vh"
            : typeof height === "string"
            ? height
            : `${height}px`,
          minHeight: isFullscreen ? "100vh" : `${minHeight}px`,
          maxHeight: isFullscreen ? "100vh" : `${maxHeight}px`,
          overflow: "hidden",
          boxSizing: "border-box",
          isolation: "isolate",
          contain: "layout style",
          zIndex: isFullscreen ? 9999 : "auto",
        }}
      >
        <style>{`
        .cluster-chart-container .node {
          cursor: ${enableDrag ? "grab" : "pointer"};
        }
        .cluster-chart-container .node.dragging {
          cursor: grabbing;
        }
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
          filter: drop-shadow(0 8px 16px rgba(0,0,0,0.4));
        }
        .cluster-chart-container .node:hover:not(.dragging) > circle:nth-child(2) {
          stroke-width: 5px !important;
        }
        .cluster-chart-container .link {
          pointer-events: none;
          transition: all 0.3s ease;
        }
        .cluster-chart-container svg {
          cursor: move;
        }
        .cluster-chart-container svg.dragging {
          cursor: grabbing;
        }
        .cluster-chart-container .bubble-group {
          pointer-events: all;
        }
        .cluster-chart-container .sentiment-chart {
          pointer-events: auto;
        }
        .cluster-chart-container .sentiment-chart g.slice path:not(.hit-area) {
          transition: filter 0.3s ease;
          cursor: pointer;
        }
        .cluster-chart-container .sentiment-chart g.slice .hit-area {
          pointer-events: all;
        }
        .cluster-chart-container .bubble-group .sentiment-bubble {
          transition: filter 0.3s ease;
        }
        .cluster-chart-container .expand-button {
          pointer-events: all;
          z-index: 100;
          transition: opacity 0.2s ease, visibility 0.2s ease;
        }
        .cluster-chart-container .expand-button:hover circle {
          fill: #f3f4f6;
        }
        .cluster-chart-container .expand-button:hover text {
          fill: #1f2937;
        }
        .cluster-chart-container text {
          pointer-events: none;
          user-select: none;
        }
      `}</style>

        {/* Helpful Guide Text */}
        {helpfulTextPoints.length > 0 &&
          !isDashboardScheduleModal &&
          !PdfDownloadStatus &&
          !PptGeneratingStatus &&
          !isPreviewState && (
            <div
              style={{
                position: "absolute",
                bottom: "20px",
                left: "20px",
                zIndex: 15,
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                padding: isHelpTextCollapsed ? "8px 12px" : "12px 14px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                maxWidth: "240px",
                pointerEvents: "auto",
                transition: "all 0.2s ease",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#374151",
                  marginBottom: isHelpTextCollapsed ? "0" : "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                }}
                onClick={() => setIsHelpTextCollapsed(!isHelpTextCollapsed)}
              >
                <span>Node Explorer Guide</span>
                <span
                  style={{
                    fontSize: "10px",
                    marginLeft: "8px",
                    transform: isHelpTextCollapsed
                      ? "rotate(0deg)"
                      : "rotate(180deg)",
                    transition: "transform 0.2s ease",
                    display: "inline-block",
                  }}
                >
                  ▲
                </span>
              </div>
              {!isHelpTextCollapsed && (
                <>
                  {/* Preloaded nodes section */}
                  {helpfulTextPoints.filter((point) => point.includes("✓"))
                    .length > 0 && (
                    <>
                      <div
                        style={{
                          fontSize: "11px",
                          fontWeight: "600",
                          color: "#374151",
                          marginBottom: "6px",
                          marginTop: "4px",
                        }}
                      >
                        Preloaded nodes
                      </div>
                      {helpfulTextPoints
                        .filter((point) => point.includes("✓"))
                        .map((point, index) => (
                          <div
                            key={`preloaded-${index}`}
                            style={{
                              fontSize: "10px",
                              color: "#6b7280",
                              marginBottom: "5px",
                              paddingLeft: "14px",
                              position: "relative",
                              lineHeight: "1.4",
                            }}
                          >
                            <span
                              style={{
                                position: "absolute",
                                left: "0",
                                top: "0",
                                fontWeight: "600",
                                color: "#9ca3af",
                                fontSize: "9px",
                              }}
                            >
                              {index + 1}.
                            </span>
                            <span
                              style={{
                                color: "#6b7280",
                                fontWeight: "normal",
                              }}
                            >
                              {point}
                            </span>
                          </div>
                        ))}
                    </>
                  )}

                  {/* How to explore next nodes section */}
                  {helpfulTextPoints.filter((point) => !point.includes("✓"))
                    .length > 0 && (
                    <>
                      <div
                        style={{
                          fontSize: "11px",
                          fontWeight: "600",
                          color: "#374151",
                          marginBottom: "6px",
                          marginTop:
                            helpfulTextPoints.filter((point) =>
                              point.includes("✓")
                            ).length > 0
                              ? "10px"
                              : "4px",
                        }}
                      >
                        How to explore next nodes
                      </div>
                      {helpfulTextPoints
                        .filter((point) => !point.includes("✓"))
                        .map((point, index) => (
                          <div
                            key={`explore-${index}`}
                            style={{
                              fontSize: "10px",
                              color: "#6b7280",
                              marginBottom:
                                index <
                                helpfulTextPoints.filter(
                                  (p) => !p.includes("✓")
                                ).length -
                                  1
                                  ? "5px"
                                  : "0",
                              paddingLeft: "14px",
                              position: "relative",
                              lineHeight: "1.4",
                            }}
                          >
                            <span
                              style={{
                                position: "absolute",
                                left: "0",
                                top: "0",
                                fontWeight: "600",
                                color: "#9ca3af",
                                fontSize: "9px",
                              }}
                            >
                              {index + 1}.
                            </span>
                            <span
                              style={{
                                color: "#6b7280",
                                fontWeight: "normal",
                              }}
                            >
                              {point}
                            </span>
                          </div>
                        ))}
                    </>
                  )}
                </>
              )}
            </div>
          )}

        <svg
          ref={svgRef}
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            margin: "0 auto",
          }}
        ></svg>

        {/* Search Bar */}
        {!isDashboardScheduleModal &&
        !PdfDownloadStatus &&
        !PptGeneratingStatus &&
        !isPreviewState ? (
          <div
            style={{
              position: "absolute",
              top: "20px",
              left: "20px",
              zIndex: 10,
              pointerEvents: "auto",
            }}
          >
            {showSearch ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "white",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  padding: "4px 8px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  style={{ marginRight: "6px" }}
                >
                  <circle
                    cx="6.5"
                    cy="6.5"
                    r="5.5"
                    stroke="#6b7280"
                    strokeWidth="1.5"
                  />
                  <line
                    x1="10.5"
                    y1="10.5"
                    x2="14.5"
                    y2="14.5"
                    stroke="#6b7280"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search nodes..."
                  style={{
                    border: "none",
                    outline: "none",
                    fontSize: "14px",
                    width: "200px",
                    padding: "4px",
                  }}
                  autoFocus
                />
                <button
                  onClick={() => {
                    setShowSearch(false);
                    setSearchTerm("");
                    setHighlightedNodes(new Set());
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "18px",
                    color: "#6b7280",
                    padding: "0 4px",
                  }}
                  title="Close search"
                >
                  ×
                </button>
              </div>
            ) : (
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "10px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  width: "60px",
                  textAlign: "center",
                }}
              >
                <h4
                  style={{
                    margin: "0 0 6px 0",
                    fontSize: "11px",
                    fontWeight: "600",
                    color: "#374151",
                    lineHeight: "1.2",
                  }}
                >
                  Search
                  <br />
                  Nodes
                </h4>
                <button
                  onClick={() => setShowSearch(true)}
                  style={{
                    width: "40px",
                    height: "40px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: "white",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto",
                  }}
                  title="Search nodes"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle
                      cx="8.5"
                      cy="8.5"
                      r="6.5"
                      stroke="#6b7280"
                      strokeWidth="1.5"
                    />
                    <line
                      x1="13.5"
                      y1="13.5"
                      x2="18.5"
                      y2="18.5"
                      stroke="#6b7280"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
            )}
            {highlightedNodes.size > 0 && (
              <div
                style={{
                  marginTop: "8px",
                  fontSize: "11px",
                  color: "#6b7280",
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  border: "1px solid #e5e7eb",
                  borderRadius: "4px",
                  padding: "4px 8px",
                  textAlign: "center",
                }}
              >
                {highlightedNodes.size} match
                {highlightedNodes.size > 1 ? "es" : ""} found
              </div>
            )}
          </div>
        ) : (
          ""
        )}

        {/* Zoom Controls */}
        {!isDashboardScheduleModal &&
        !PdfDownloadStatus &&
        !PptGeneratingStatus &&
        !isPreviewState ? (
          <>
            {enableZoom && (
              <div
                style={{
                  position: "absolute",
                  top: "20px",
                  right: "20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: "5px",
                  zIndex: 10,
                  pointerEvents: "auto",
                }}
              >
                {/* Count Mode Indicator - Above Fullscreen Button */}
                <div
                  style={{
                    width: "auto",
                    padding: "10px 18px",
                    border: total_count_flag
                      ? "2px solid #3b82f6"
                      : "2px solid #10b981",
                    borderRadius: "4px",
                    backgroundColor: total_count_flag ? "#eff6ff" : "#f0fdf4",
                    color: total_count_flag ? "#1e40af" : "#166534",
                    fontSize: "14px",
                    fontWeight: "600",
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    pointerEvents: "none",
                    whiteSpace: "nowrap",
                    textAlign: "center",
                    marginBottom: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Background gradient effect */}
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: total_count_flag
                        ? "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)"
                        : "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)",
                      borderRadius: "4px",
                      pointerEvents: "none",
                    }}
                  />
                  <span
                    style={{ position: "relative", letterSpacing: "0.025em" }}
                  >
                    {total_count_flag
                      ? "Total Counts Mode"
                      : "Relation Counts Mode"}
                  </span>
                </div>

                {/* Fullscreen Toggle Button */}
                <button
                  style={{
                    width: "40px",
                    height: "40px",
                    border: isFullscreen
                      ? "2px solid #3b82f6"
                      : "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: isFullscreen ? "#3b82f6" : "white",
                    color: isFullscreen ? "white" : "black",
                    cursor: "pointer",
                    fontSize: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s",
                  }}
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  title={isFullscreen ? "Exit fullscreen" : "Full screen view"}
                >
                  {isFullscreen ? "⊗" : "⛶"}
                </button>
                <button
                  style={{
                    width: "40px",
                    height: "40px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: isFullscreen ? "white" : "#f5f5f5",
                    cursor: isFullscreen ? "pointer" : "not-allowed",
                    fontSize: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: isFullscreen ? 1 : 0.5,
                    transition: "all 0.2s",
                  }}
                  onClick={() => {
                    if (!isFullscreen) return; // Only allow zoom in fullscreen mode

                    const svg = d3.select(svgRef.current);
                    const container = svg.select(".container");
                    if (zoomRef.current && svg.node()) {
                      try {
                        const currentTransform =
                          svg.node().__zoom || d3.zoomIdentity;
                        const k = Math.min(currentTransform.k * 1.3, 4); // Respect max scale
                        const newTransform = d3.zoomIdentity
                          .translate(currentTransform.x, currentTransform.y)
                          .scale(k);

                        container.attr("transform", newTransform);
                        if (svg.node()) {
                          svg.node().__zoom = newTransform;
                        }
                      } catch (e) {
                        console.error("Zoom in error:", e);
                      }
                    }
                  }}
                  title={
                    isFullscreen
                      ? "Zoom in"
                      : "Zoom only available in fullscreen mode"
                  }
                  disabled={!isFullscreen}
                >
                  +
                </button>
                <button
                  style={{
                    width: "40px",
                    height: "40px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: isFullscreen ? "white" : "#f5f5f5",
                    cursor: isFullscreen ? "pointer" : "not-allowed",
                    fontSize: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: isFullscreen ? 1 : 0.5,
                    transition: "all 0.2s",
                  }}
                  onClick={() => {
                    if (!isFullscreen) return; // Only allow zoom in fullscreen mode

                    const svg = d3.select(svgRef.current);
                    const container = svg.select(".container");
                    if (zoomRef.current && svg.node()) {
                      try {
                        const currentTransform =
                          svg.node().__zoom || d3.zoomIdentity;
                        const k = Math.max(currentTransform.k * 0.7, 0.3); // Respect min scale
                        const newTransform = d3.zoomIdentity
                          .translate(currentTransform.x, currentTransform.y)
                          .scale(k);

                        container.attr("transform", newTransform);
                        if (svg.node()) {
                          svg.node().__zoom = newTransform;
                        }
                      } catch (e) {
                        console.error("Zoom out error:", e);
                      }
                    }
                  }}
                  title={
                    isFullscreen
                      ? "Zoom out"
                      : "Zoom only available in fullscreen mode"
                  }
                  disabled={!isFullscreen}
                >
                  −
                </button>
                <button
                  style={{
                    width: "40px",
                    height: "40px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: isFullscreen ? "white" : "#f5f5f5",
                    cursor: isFullscreen ? "pointer" : "not-allowed",
                    fontSize: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: isFullscreen ? 1 : 0.5,
                    transition: "all 0.2s",
                  }}
                  onClick={() => {
                    if (!isFullscreen) return; // Only allow fit to view in fullscreen mode

                    const svg = d3.select(svgRef.current);
                    if (zoomRef.current) {
                      // Find bounds and auto-fit
                      if (data.nodes && data.nodes.length > 0) {
                        let minX = Infinity,
                          maxX = -Infinity;
                        let minY = Infinity,
                          maxY = -Infinity;

                        data.nodes.forEach((d) => {
                          const radius = 50; // Approximate radius
                          minX = Math.min(minX, d.x - radius);
                          maxX = Math.max(maxX, d.x + radius);
                          minY = Math.min(minY, d.y - radius);
                          maxY = Math.max(maxY, d.y + radius);
                        });

                        const boundsWidth = maxX - minX;
                        const boundsHeight = maxY - minY;
                        const centerX = (minX + maxX) / 2;
                        const centerY = (minY + maxY) / 2;

                        const scale =
                          Math.min(
                            (dimensions.width - 10) / boundsWidth,
                            (dimensions.height - 10) / boundsHeight
                          ) * 0.95;

                        const translateX =
                          dimensions.width / 2 - centerX * scale;
                        const translateY =
                          dimensions.height / 2 - centerY * scale;

                        const transform = d3.zoomIdentity
                          .translate(translateX, translateY)
                          .scale(scale);

                        const container = svg.select(".container");
                        container.attr("transform", transform);
                        if (svg.node()) {
                          svg.node().__zoom = transform;
                        }
                      }
                    }
                  }}
                  title={
                    isFullscreen
                      ? "Fit to view"
                      : "Fit to view only available in fullscreen mode"
                  }
                  disabled={!isFullscreen}
                >
                  ⊡
                </button>
              </div>
            )}
          </>
        ) : (
          ""
        )}

        {/* Visualization Type Controls */}
        {!isDashboardScheduleModal &&
          !PdfDownloadStatus &&
          !PptGeneratingStatus &&
          !isPreviewState &&
          metadata?.multilevel_breakdown && (
            <div
              style={{
                position: "absolute",
                top: enableZoom ? "230px" : "20px",
                right: "20px",
                display: "flex",
                flexDirection: "column",
                gap: "5px",
                zIndex: 10,
                pointerEvents: "auto",
              }}
            >
              <button
                style={{
                  width: "40px",
                  height: "40px",
                  border:
                    visualizationType === "pie"
                      ? "2px solid #3b82f6"
                      : "1px solid #ddd",
                  borderRadius: "4px",
                  backgroundColor:
                    visualizationType === "pie" ? "#3b82f6" : "white",
                  color: visualizationType === "pie" ? "white" : "black",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0",
                }}
                onClick={() => handleVisualizationChange("pie")}
                title="Pie Chart"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 2 A8 8 0 1 1 9.99 2.01 Z" fill="#10b981" />
                  <path d="M10 10 L10 2 A8 8 0 0 1 18 10 Z" fill="#ef4444" />
                  <path d="M10 10 L18 10 A8 8 0 0 1 10 18 Z" fill="#f59e0b" />
                </svg>
              </button>
              <button
                style={{
                  width: "40px",
                  height: "40px",
                  border:
                    visualizationType === "donut"
                      ? "2px solid #3b82f6"
                      : "1px solid #ddd",
                  borderRadius: "4px",
                  backgroundColor:
                    visualizationType === "donut" ? "#3b82f6" : "white",
                  color: visualizationType === "donut" ? "white" : "black",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0",
                }}
                onClick={() => handleVisualizationChange("donut")}
                title="Donut Chart"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M10 2 A8 8 0 1 1 9.99 2.01 L10 6 A4 4 0 1 0 10.01 6.01 Z"
                    fill="#10b981"
                  />
                  <path
                    d="M10 2 A8 8 0 0 1 18 10 L14 10 A4 4 0 0 0 10 6 Z"
                    fill="#ef4444"
                  />
                  <path
                    d="M18 10 A8 8 0 0 1 10 18 L10 14 A4 4 0 0 0 14 10 Z"
                    fill="#f59e0b"
                  />
                </svg>
              </button>
              <button
                style={{
                  width: "40px",
                  height: "40px",
                  border:
                    visualizationType === "bubble"
                      ? "2px solid #3b82f6"
                      : "1px solid #ddd",
                  borderRadius: "4px",
                  backgroundColor:
                    visualizationType === "bubble" ? "#3b82f6" : "white",
                  color: visualizationType === "bubble" ? "white" : "black",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0",
                }}
                onClick={() => handleVisualizationChange("bubble")}
                title="Bubble Chart"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="6" cy="6" r="4" fill="#10b981" />
                  <circle cx="14" cy="8" r="3" fill="#ef4444" />
                  <circle cx="8" cy="14" r="3.5" fill="#f59e0b" />
                </svg>
              </button>
            </div>
          )}

        {/* Shape Selector - Below Search */}
        {!isDashboardScheduleModal &&
        !PdfDownloadStatus &&
        !PptGeneratingStatus &&
        !isPreviewState ? (
          <div
            style={{
              position: "absolute",
              top: showSearch && highlightedNodes.size > 0 ? "180px" : "120px",
              left: "20px",
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              padding: isShapePanelCollapsed ? "8px" : "10px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              zIndex: 10,
              pointerEvents: "auto",
              width: isShapePanelCollapsed ? "auto" : "60px",
              transition: "all 0.3s ease",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: isShapePanelCollapsed ? "0" : "6px",
                cursor: "pointer",
                gap: isShapePanelCollapsed ? "8px" : "4px",
              }}
              onClick={() => setIsShapePanelCollapsed(!isShapePanelCollapsed)}
            >
              <h4
                style={{
                  margin: "0",
                  fontSize: "11px",
                  fontWeight: "600",
                  color: "#374151",
                  textAlign: "center",
                  lineHeight: "1.2",
                }}
              >
                Node
                <br />
                Shape
              </h4>
              <button
                style={{
                  background: "none",
                  border: "none",
                  color: "#6b7280",
                  cursor: "pointer",
                  padding: "0",
                  fontSize: "10px",
                  lineHeight: "1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "16px",
                  height: "16px",
                  flexShrink: 0,
                  marginLeft: "-4px",
                }}
                title={
                  isShapePanelCollapsed
                    ? "Expand shape selector"
                    : "Collapse shape selector"
                }
              >
                {isShapePanelCollapsed ? "▼" : "▲"}
              </button>
            </div>
            <div
              style={{
                display: isShapePanelCollapsed ? "none" : "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              {[
                "circle",
                "square",
                "diamond",
                "hexagon",
                "triangle",
                "star",
              ].map((shape) => (
                <button
                  key={shape}
                  onClick={() => handleNodeShapeChange(shape)}
                  style={{
                    width: "40px",
                    height: "40px",
                    border:
                      nodeShape === shape
                        ? "2px solid #3b82f6"
                        : "1px solid #e5e7eb",
                    borderRadius: "6px",
                    backgroundColor: nodeShape === shape ? "#eff6ff" : "white",
                    color: nodeShape === shape ? "#3b82f6" : "#6b7280",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (nodeShape !== shape) {
                      e.currentTarget.style.backgroundColor = "#f9fafb";
                      e.currentTarget.style.borderColor = "#d1d5db";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (nodeShape !== shape) {
                      e.currentTarget.style.backgroundColor = "white";
                      e.currentTarget.style.borderColor = "#e5e7eb";
                    }
                  }}
                  title={`${
                    shape.charAt(0).toUpperCase() + shape.slice(1)
                  } shape`}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    {shape === "circle" && (
                      <circle cx="12" cy="12" r="10" fill="currentColor" />
                    )}
                    {shape === "square" && (
                      <rect
                        x="4"
                        y="4"
                        width="16"
                        height="16"
                        fill="currentColor"
                      />
                    )}
                    {shape === "diamond" && (
                      <path
                        d="M12 2 L22 12 L12 22 L2 12 Z"
                        fill="currentColor"
                      />
                    )}
                    {shape === "hexagon" && (
                      <path
                        d="M12 2 L20 7 L20 17 L12 22 L4 17 L4 7 Z"
                        fill="currentColor"
                      />
                    )}
                    {shape === "triangle" && (
                      <path d="M12 4 L20 18 L4 18 Z" fill="currentColor" />
                    )}
                    {shape === "star" && (
                      <path
                        d="M12 2 L14.5 9 L22 9 L16.25 13.5 L18.75 20.5 L12 16 L5.25 20.5 L7.75 13.5 L2 9 L9.5 9 Z"
                        fill="currentColor"
                      />
                    )}
                  </svg>
                </button>
              ))}
            </div>
          </div>
        ) : (
          ""
        )}

        {/* Size Toggle */}
        {!isDashboardScheduleModal &&
        !PdfDownloadStatus &&
        !PptGeneratingStatus &&
        !isPreviewState ? (
          <div
            style={{
              position: "absolute",
              bottom: "20px",
              right: "20px",
              zIndex: 10,
              pointerEvents: "auto",
            }}
          >
            <button
              onClick={() => setIsDynamicSize(!isDynamicSize)}
              style={{
                width: "40px",
                height: "40px",
                border: isDynamicSize ? "2px solid #3b82f6" : "1px solid #ddd",
                borderRadius: "4px",
                backgroundColor: isDynamicSize ? "#3b82f6" : "white",
                color: isDynamicSize ? "white" : "#666",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0",
              }}
              title={
                isDynamicSize
                  ? "Switch to fixed size"
                  : "Switch to dynamic size"
              }
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                {isDynamicSize ? (
                  <>
                    <circle cx="5" cy="5" r="2" fill="currentColor" />
                    <circle cx="10" cy="10" r="4" fill="currentColor" />
                    <circle cx="15" cy="15" r="3" fill="currentColor" />
                  </>
                ) : (
                  <>
                    <circle cx="5" cy="5" r="3" fill="currentColor" />
                    <circle cx="10" cy="10" r="3" fill="currentColor" />
                    <circle cx="15" cy="15" r="3" fill="currentColor" />
                  </>
                )}
              </svg>
            </button>
          </div>
        ) : (
          ""
        )}

        {/* Export Controls */}
        {!isDashboardScheduleModal &&
        !PdfDownloadStatus &&
        !PptGeneratingStatus &&
        !isPreviewState ? (
          <div
            style={{
              position: "absolute",
              top: enableZoom ? "380px" : "170px",
              right: "20px",
              zIndex: 10,
              pointerEvents: "auto",
            }}
          >
            {showExportMenu ? (
              <div
                style={{
                  backgroundColor: "white",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  padding: "8px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                <button
                  onClick={() => {
                    exportAsPNG();
                    setShowExportMenu(false);
                  }}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: "white",
                    cursor: "pointer",
                    fontSize: "12px",
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#f3f4f6")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = "white")
                  }
                >
                  🖼️ Export as PNG
                </button>
                <button
                  onClick={() => {
                    exportAsSVG();
                    setShowExportMenu(false);
                  }}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: "white",
                    cursor: "pointer",
                    fontSize: "12px",
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#f3f4f6")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = "white")
                  }
                >
                  📐 Export as SVG
                </button>
                {/* <button
                onClick={() => {
                  exportAsJSON();
                  setShowExportMenu(false);
                }}
                style={{
                  padding: "6px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  backgroundColor: "white",
                  cursor: "pointer",
                  fontSize: "12px",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "#f3f4f6")
                }
                onMouseLeave={(e) => (e.target.style.backgroundColor = "white")}
              >
                📄 Export as JSON
              </button> */}
                <Tooltip title="Downloaded Excel contains only parent and first-level node data.">
                  <button
                    onClick={() => {
                      exportAsExcel();
                      setShowExportMenu(false);
                    }}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      backgroundColor: "white",
                      cursor: "pointer",
                      fontSize: "12px",
                      textAlign: "left",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#f3f4f6")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "white")
                    }
                  >
                    📊 Export as Excel
                  </button>
                </Tooltip>
                <button
                  onClick={() => setShowExportMenu(false)}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: "white",
                    cursor: "pointer",
                    fontSize: "12px",
                    textAlign: "center",
                    marginTop: "4px",
                  }}
                  title="Cancel export"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowExportMenu(true)}
                style={{
                  width: "40px",
                  height: "40px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  backgroundColor: "white",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0",
                }}
                title="Export chart"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M10 2 L10 12 M10 12 L6 8 M10 12 L14 8"
                    stroke="#6b7280"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4 14 L4 17 C4 17.5 4.5 18 5 18 L15 18 C15.5 18 16 17.5 16 17 L16 14"
                    stroke="#6b7280"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}
          </div>
        ) : (
          ""
        )}

        {/* Tooltip */}
        {showTooltip && tooltip.visible && tooltip.data && (
          <div
            style={{
              position: "absolute",
              left: tooltip.x,
              top: tooltip.y,
              background: "rgba(0, 0, 0, 0.9)",
              color: "white",
              padding: "12px",
              borderRadius: "6px",
              fontSize: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
              zIndex: 1000,
              pointerEvents: "none",
              maxWidth: "250px",
              transition: "opacity 0.15s ease",
              opacity: tooltip.visible ? 1 : 0,
              userSelect: "none",
            }}
          >
            <div
              style={{
                fontWeight: "bold",
                marginBottom: "8px",
                fontSize: "14px",
              }}
            >
              {tooltip.data.name}
            </div>
            {/* <div style={{ marginBottom: "6px" }}>
            {tooltip.data.attribute ||
              (tooltip.data.type === "aspectGroup"
                ? "Aspect Group"
                : "Opinion")}
          </div> */}
            <div style={{ marginBottom: "4px" }}>
              {metadata?.y_series?.[0]?.trace_name
                ? metadata?.y_series?.[0]?.trace_name
                : metadata?.y_axis?.y_series_1?.trace_name}
              :
              {(() => {
                let total;
                // Calculate total from breakdown_by_data if available
                if (
                  tooltip.data.breakdown_by_data &&
                  tooltip.data.breakdown_by_data.values &&
                  tooltip.data.breakdown_by_data.values.length > 0
                ) {
                  total = 0;
                  tooltip.data.breakdown_by_data.values.forEach((valueObj) => {
                    total += Object.values(valueObj).reduce(
                      (sum, val) => sum + val,
                      0
                    );
                  });
                } else {
                  // Otherwise use the value field
                  total = tooltip.data.value;
                }
                // Format with comma separators
                return total.toLocaleString();
              })()}
            </div>

            {metadata?.multilevel_breakdown && (
              <div
                style={{
                  borderTop: "1px solid rgba(255,255,255,0.3)",
                  paddingTop: "6px",
                  marginTop: "6px",
                }}
              >
                {/* Dynamic sentiment/breakdown display */}
                {tooltip.data.breakdown_by_data &&
                  tooltip.data.breakdown_by_data.trace_name && (
                    <div
                      style={{
                        marginBottom: "4px",
                        fontSize: "11px",
                        color: "#9ca3af",
                      }}
                    >
                      {tooltip.data.breakdown_by_data.trace_name}
                    </div>
                  )}
                {(() => {
                  // Get values to display - prioritize breakdown_by_data over sentimenttype
                  let valuesToDisplay = {};
                  let isPercentage = true; // Flag to track if values are already percentages

                  if (
                    tooltip.data.breakdown_by_data &&
                    tooltip.data.breakdown_by_data.values &&
                    tooltip.data.breakdown_by_data.values.length > 0
                  ) {
                    // Merge all objects in the values array
                    tooltip.data.breakdown_by_data.values.forEach(
                      (valueObj) => {
                        Object.assign(valuesToDisplay, valueObj);
                      }
                    );
                    isPercentage = false; // breakdown_by_data contains counts, not percentages
                  } else if (tooltip.data.sentimenttype) {
                    // Fall back to sentimenttype (already in percentage)
                    valuesToDisplay = tooltip.data.sentimenttype;
                  }

                  // Calculate total for percentage calculation
                  const total = Object.values(valuesToDisplay).reduce(
                    (sum, val) => sum + val,
                    0
                  );

                  // Color mapping for common types
                  const colorMap = {
                    positive: "#1c9f00",
                    negative: "#dc1e1e",
                    neutral: "#deaa0c",
                    mixed: "#6366f1",
                    happy: "#10b981",
                    sad: "#3b82f6",
                    angry: "#ef4444",
                    surprised: "#fbbf24",
                    satisfied: "#10b981",
                    unsatisfied: "#ef4444",
                    supportive: "#22c55e",
                    critical: "#dc2626",
                    questioning: "#8b5cf6",
                    constructive: "#06b6d4",
                    complaint: "#f97316",
                    praise: "#22d3ee",
                    "5-star": "#10b981",
                    "4-star": "#22c55e",
                    "3-star": "#fbbf24",
                    "2-star": "#fb923c",
                    "1-star": "#ef4444",
                    // Default color for unknown types
                    default: "#94a3b8",
                  };

                  return Object.entries(valuesToDisplay).map(([key, value]) => {
                    const color =
                      colorMap[key.toLowerCase()] || colorMap.default;

                    // Calculate percentage if needed
                    let displayValue;
                    if (isPercentage) {
                      displayValue = value || 0;
                    } else {
                      // Calculate percentage from counts
                      displayValue =
                        total > 0 ? Math.round((value / total) * 100) : 0;
                    }

                    return (
                      <div key={key} style={{ color }}>
                        {key.charAt(0).toUpperCase() + key.slice(1)}:{" "}
                        {displayValue}%
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ClusterChart;
